import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { safeCopyToClipboard } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { 
  LogOut, 
  Users, 
  TrendingUp, 
  UserCheck, 
  UserX,
  ChevronRight,
  BookOpen,
  Settings,
  UserPlus,
  Send,
  RefreshCw,
  Mail,
  Globe,
  Server,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  AlertTriangle,
  Download,
  Building2,
  CalendarDays,
  Calendar,
  Repeat,
  MessageSquare,
  Lightbulb,
  X,
  BarChart3,
  Target,
  Ticket,
  Heart,
  Gift,
  Phone,
  FileText,
  Save,
  Pencil,
  Plus,
  Trash2,
  UserCog,
  Check,
  Palette,
  Image,
  Key,
  Copy,
  Zap,
  Printer,
  Eye,
  EyeOff,
  ChevronDown,
  MessageCircle,
  Lock,
  MapPin,
  UserMinus,
  DollarSign,
  Shield,
  FileUp,
  RotateCcw,
  Activity
} from "lucide-react";
import { toast } from "sonner";
import TapTrackLogo from "../components/TapTrackLogo";
import { useFeatureMode } from "../hooks/useFeatureMode";
import SafetyFreezeNotice from "../components/SafetyFreezeNotice";
import { GiftAidPanel } from "../components/GiftAidPanel";
import { GiftAidMetricsStrip } from "../components/GiftAidMetricsStrip";
import { WorkforceReadinessPanel } from "../components/WorkforceReadinessPanel";
import ManagerOperationsHome from "../components/ManagerOperationsHome";
import { WorkforceOperationsCentre } from "../components/WorkforceOperationsCentre";
import { DonorHubPanel } from "../components/DonorHubPanel";
import { TrusteeDashboardSection } from "../components/TrusteeDashboardSection";
import { DeliveryCentrePanel } from "../components/V24Panels";
import { CharityManagementCentre } from "../components/CharityManagementCentre";
import ActivityTrailMap from "../components/ActivityTrailMap";
import LocationProfitability from "../components/LocationProfitability";
import OwnerAlerts from "../components/OwnerAlerts";
import TerritoryPlanning from "../components/TerritoryPlanning";
import Leaderboards from "../components/Leaderboards";
import FirstRunSetup from "../components/FirstRunSetup";
import ForecastPanel from "../components/ForecastPanel";
import FundraiserPerformance from "../components/FundraiserPerformance";
import CampaignIntelligence from "../components/CampaignIntelligence";
import CampaignPlannerView from "../components/CampaignPlannerView";
import OrgTreeNode from "../components/OrgTreeNode";
import CommandCard from "../components/CommandCard";
import TapTrackIntelligence from "../components/TapTrackIntelligence";
import WeeklyDigestCard from "../components/WeeklyDigestCard";
import ManagerScorecards from "../components/ManagerScorecards";
import RevenueLeaks from "../components/RevenueLeaks";
import LeadershipSnapshot from "../components/LeadershipSnapshot";
import RecoveryList from "../components/RecoveryList";
import RecoveryProgress from "../components/RecoveryProgress";
import BoardSnapshot from "../components/BoardSnapshot";
import PromotionRadar from "../components/PromotionRadar";
import RiskRadar from "../components/RiskRadar";
import BoardPackExport from "../components/BoardPackExport";
import ClientHealth from "../components/ClientHealth";
import ROIProof from "../components/ROIProof";
import OwnerOverviewV2 from "../components/OwnerOverviewV2";
import OwnerInsightsTab from "../components/OwnerInsightsTab";
import OnboardingTour, { ReplayTourButton } from "../components/OnboardingTour";
import WorkforceTab from "../components/WorkforceTab";
import { ReferralQueue } from "../components/ReferralQueue";
import ScopeIndicator from "../components/ScopeIndicator";
import { ContextSwitcher } from "../components/ContextSwitcher";
import FailedPaymentsPanel from "../components/FailedPaymentsPanel";
import AgencyBillingPanel from "../components/AgencyBillingPanel";
import OwnerValueTile from "../components/OwnerValueTile";
import AdminDiagnosticsCard from "../components/AdminDiagnosticsCard";
import OnboardingChecklistCard from "../components/OnboardingChecklistCard";
import CampaignControlCentre from "../components/CampaignControlCentre";
import DualViewSwitcher from "../components/DualViewSwitcher";
import CampaignBeneficiarySelector from "../components/CampaignBeneficiarySelector";
import BondManagement from "../components/BondManagement";
import { isOwnerTier as isOwnerTierFn } from "../lib/roleAccess";

export default function OwnerDashboard() {
  const { user, logout } = useAuth();
  const featureMode = useFeatureMode();
  const isOwnerTier = isOwnerTierFn(user);
  const isStandardManager = user?.role === "manager" && user?.managerLevel !== "elevated";
  const isManager = user?.role === "manager";
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Campaign filter state
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState("");
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Delivery Settings State
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [deliveryConfigs, setDeliveryConfigs] = useState([]);
  const [deliveryConfig, setDeliveryConfig] = useState({
    method: "email",
    emailAddress: "",
    webhookUrl: "",
    apiUrl: "",
    apiKey: "",
    active: true
  });
  const [savingConfig, setSavingConfig] = useState(false);

  // Signups State
  const [signups, setSignups] = useState([]);
  const [signupSummary, setSignupSummary] = useState({});
  const [signupFilters, setSignupFilters] = useState({
    campaignId: "",
    fundraiserId: "",
    teamLeaderId: "",
    deliveryStatus: "",
    dateFrom: "",
    dateTo: ""
  });
  const [loadingSignups, setLoadingSignups] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [fundraisers, setFundraisers] = useState([]);
  const [selectedSignups, setSelectedSignups] = useState([]);
  const [showSignupDetailModal, setShowSignupDetailModal] = useState(false);
  const [selectedSignupDetail, setSelectedSignupDetail] = useState(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineData, setTimelineData] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Retention Upload State
  const [retentionFile, setRetentionFile] = useState(null);
  const [uploadingRetention, setUploadingRetention] = useState(false);
  const [retentionResult, setRetentionResult] = useState(null);

  // Organisation Status State
  const [orgStatus, setOrgStatus] = useState(null);

  // Guidance State
  const [selectedFundraiserForGuidance, setSelectedFundraiserForGuidance] = useState("");
  const [guidanceMessage, setGuidanceMessage] = useState("");
  const [sendingGuidance, setSendingGuidance] = useState(false);
  const [guidanceHistory, setGuidanceHistory] = useState([]);
  const [guidanceTargetType, setGuidanceTargetType] = useState("individual"); // individual, team, campaign, all
  const [guidanceTargetTeam, setGuidanceTargetTeam] = useState("");
  const [guidanceTargetCampaign, setGuidanceTargetCampaign] = useState("");

  // Invite Fundraiser Modal State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    campaignIds: [],
    profileImageUrl: ""
  });
  const [inviting, setInviting] = useState(false);
  const [inviteCredentials, setInviteCredentials] = useState(null); // Store credentials to show after invite

  // Fundraiser Detail Panel State
  const [selectedFundraiserDetail, setSelectedFundraiserDetail] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  // Campaign Settings State
  const [settingsCampaignId, setSettingsCampaignId] = useState("");
  const [campaignSettings, setCampaignSettings] = useState({
    // Donation Modes
    enableLottery: true,
    enableRegularGiving: true,
    enableOneOff: true,
    // Lottery Settings
    lotterySinglePrice: 5.00,
    lotteryDoublePrice: 10.00,
    // Regular Giving Settings
    regularPresets: [5, 10, 15, 20, 25],
    allowCustomAmount: true,
    // One-Off Settings
    enableContactless: false,
    oneOffRequireDetails: false,
    // Contact Permissions
    allowEmailConsent: true,
    allowSmsConsent: true,
    allowPhoneConsent: true,
    allowPostConsent: true,
    // Form Requirements
    requirePhone: false,
    requireEmail: false,
    requireAddress: true,
    // SMS Settings
    enableSMS: true,
    enableAutoThankYouSMS: true,
    // Disclosure
    disclosureText: "",
    directDebitGuaranteeText: "",
    oneOffDisclosureText: "",
    // Age Flagging (for compliance review)
    minAge: 18,
    maxAge: 100,
    // Over-Age Rule Engine
    overAgeThreshold: 80,
    overAgeAction: "allow",  // allow, block, callback, self_complete, callback_or_self_complete
    // Payment Frequency
    enableAnnualPayments: false,
    // Gift Aid (additive)
    enableGiftAid: false,
    giftAidStatement: ""
  });
  const [savingCampaignSettings, setSavingCampaignSettings] = useState(false);
  const [presetsInput, setPresetsInput] = useState("5, 10, 15, 20, 25");

  // Team Management State
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [assigningCampaigns, setAssigningCampaigns] = useState(false);
  const [assigningLocations, setAssigningLocations] = useState(false);
  const [selectedLocationsForAssignment, setSelectedLocationsForAssignment] = useState([]);

  // Create Campaign State
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: "", description: "", active: true, charityId: "" });
  const [charityOptions, setCharityOptions] = useState([]);
  const loadCharityOptions = async () => {
    try { const r = await apiClient.get("/charities?filter=active"); setCharityOptions(r.data || []); }
    catch (e) { /* non-fatal */ }
  };
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  // Create Location State
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: "", address: "", campaignId: "" });
  const [creatingLocation, setCreatingLocation] = useState(false);
  const [locations, setLocations] = useState([]);

  // Team filter state
  const [teamFilterCampaign, setTeamFilterCampaign] = useState("all");

  // Upgrade Modal State
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Branding State
  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: "",
    headerImageUrl: "",
    primaryColor: "#8b5cf6",
    buttonColor: "#10b981",
    brandName: "",
    tagline: "",
    hideTapTrackBranding: false
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingDirty, setBrandingDirty] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [headerBroken, setHeaderBroken] = useState(false);

  // API Key State
  const [apiKey, setApiKey] = useState(null);
  const [loadingApiKey, setLoadingApiKey] = useState(false);
  const [generatingApiKey, setGeneratingApiKey] = useState(false);

  // Organisation Branding State (for white-labelling fundraiser UI)
  const [orgBranding, setOrgBranding] = useState({ orgLogoUrl: "", orgDisplayName: "" });
  const [uploadingOrgLogo, setUploadingOrgLogo] = useState(false);
  const [savingOrgBranding, setSavingOrgBranding] = useState(false);

  // Compliance Tracker State
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [complianceData, setComplianceData] = useState({
    dbsChecked: false,
    dbsCheckDate: "",
    dbsInitial: "",
    idVerified: false,
    idCheckDate: "",
    idInitial: "",
    referencesChecked: false,
    referencesCheckDate: "",
    referencesInitial: "",
    notes: ""
  });
  const [complianceFundraiserId, setComplianceFundraiserId] = useState(null);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [savingCompliance, setSavingCompliance] = useState(false);

  // Performance Export State
  const [showPerformanceExportModal, setShowPerformanceExportModal] = useState(false);
  const [performanceExportFilters, setPerformanceExportFilters] = useState({
    startDate: "",
    endDate: "",
    campaignId: "",
    fundraiserIds: []
  });
  const [exportingPerformance, setExportingPerformance] = useState(false);

  // Event Log State (Activity + System Log tabs)
  const [activityEvents, setActivityEvents] = useState([]);
  const [systemEvents, setSystemEvents] = useState([]);
  const [eventFilters, setEventFilters] = useState({ source: "", userId: "", dateFrom: "", dateTo: "", status: "" });
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Team Hierarchy State
  const [teamHierarchy, setTeamHierarchy] = useState(null);
  const [expandedLeaders, setExpandedLeaders] = useState({});
  const [teamLeaderFilter, setTeamLeaderFilter] = useState("all");
  const [fundraiserMetrics, setFundraiserMetrics] = useState({});
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [assigningLeader, setAssigningLeader] = useState(null);

  // Pricing Plans State (for dynamic upgrade modal)
  const [availablePlans, setAvailablePlans] = useState([]);
  const [pricingDefaults, setPricingDefaults] = useState({
    basicMonthlyFee: 0,
    growthMonthlyFee: 299,
    proMonthlyFee: 999
  });

  // Flagged Submissions State (Age Review)
  const [flaggedSubmissions, setFlaggedSubmissions] = useState([]);
  const [loadingFlagged, setLoadingFlagged] = useState(false);
  const [flaggedDateFilter, setFlaggedDateFilter] = useState({
    startDate: "",
    endDate: ""
  });
  const [exportingFlagged, setExportingFlagged] = useState(false);

  // Schedule State
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [showAddShiftModal, setShowAddShiftModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    userIds: [],  // Multi-select: empty = unassigned
    locationId: "",
    campaignId: "",
    date: "",
    endDate: "",  // Optional: creates a shift per day inclusive; skips duplicates
    startTime: "09:00",
    endTime: "17:00",
    isRecurring: false,
    repeatWeeks: 4  // Default 4 weeks
  });
  const [savingShift, setSavingShift] = useState(false);
  const [scheduleWeekStart, setScheduleWeekStart] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });
  const [showEditModeDialog, setShowEditModeDialog] = useState(false);
  const [pendingEditMode, setPendingEditMode] = useState(null);
  const [showDeleteModeDialog, setShowDeleteModeDialog] = useState(false);
  const [pendingDeleteShift, setPendingDeleteShift] = useState(null);
  const [teamPreferences, setTeamPreferences] = useState({});
  const [showPreferenceWarning, setShowPreferenceWarning] = useState(false);
  const [pendingShiftWithWarning, setPendingShiftWithWarning] = useState(null);
  const [plannerCampaignFilter, setPlannerCampaignFilter] = useState("");
  const [showDuplicateWeek, setShowDuplicateWeek] = useState(false);
  const [duplicateWeeksForward, setDuplicateWeeksForward] = useState(4);
  const [duplicating, setDuplicating] = useState(false);
  const [shiftSuggestions, setShiftSuggestions] = useState([]);
  // Auto-Plan Next Week state
  const [showAutoPlanModal, setShowAutoPlanModal] = useState(false);
  const [autoPlanLoading, setAutoPlanLoading] = useState(false);
  const [autoPlanSaving, setAutoPlanSaving] = useState(false);
  const [autoPlanData, setAutoPlanData] = useState(null);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [setPasswordTarget, setSetPasswordTarget] = useState(null);
  const [newPasswordForm, setNewPasswordForm] = useState({ password: "", confirm: "" });
  const [settingPassword, setSettingPassword] = useState(false);

  // Billing State
  const [billingData, setBillingData] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [manualCharges, setManualCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [chargeForm, setChargeForm] = useState({ amount: "", description: "" });
  const [creatingCharge, setCreatingCharge] = useState(false);
  const [lockStatus, setLockStatus] = useState(null);
  const [lockingPricing, setLockingPricing] = useState(false);
  const [selectedLockPlan, setSelectedLockPlan] = useState(null); // "monthly" | "6_month" | "12_month" | null
  // Campaign-First Planner view toggle + intelligence modals
  const [plannerView, setPlannerView] = useState("user"); // "user" | "campaign"
  const [showFundraiserPerf, setShowFundraiserPerf] = useState(false);
  const [perfFundraiserId, setPerfFundraiserId] = useState(null);
  const [showCampaignIntel, setShowCampaignIntel] = useState(false);
  const [intelCampaignId, setIntelCampaignId] = useState(null);

  // Earnings state
  const [earningsData, setEarningsData] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [earningsFilter, setEarningsFilter] = useState({ campaignId: "", fundraiserId: "" });
  const [earningsDrilldown, setEarningsDrilldown] = useState(null);

  // Cancellations state
  const [cancellations, setCancellations] = useState([]);
  const [loadingCancellations, setLoadingCancellations] = useState(false);
  const [cancelForm, setCancelForm] = useState({ taptrack_id: "", reason: "" });
  const [submittingCancel, setSubmittingCancel] = useState(false);

  // Bond settings state
  const [bondSettings, setBondSettings] = useState({ bond_type: "percentage", bond_value: 0, apply_to: "all" });
  const [savingBond, setSavingBond] = useState(false);
  // Package 6G — legacy bond controls hidden when a 6D bond policy governs the agency
  const [bond6DActive, setBond6DActive] = useState(false);
  const authedFileUrl = (u) => {
    if (!u || !u.startsWith("/api/static/uploads/")) return u;
    const t = sessionStorage.getItem("taptrack_token");
    return `${process.env.REACT_APP_BACKEND_URL || ""}${u}?auth=${encodeURIComponent(t || "")}`;
  };

  // API Access state
  const [apiSettings, setApiSettings] = useState(null);
  const [newApiKey, setNewApiKey] = useState(null);

  // Scheduling state
  const [teamAvailability, setTeamAvailability] = useState(null);
  const [loadingTeamAvail, setLoadingTeamAvail] = useState(false);
  const [quarterOverview, setQuarterOverview] = useState(null);
  const [assignForm, setAssignForm] = useState({ fundraiser_id: "", campaign_id: "", location_id: "", date: "", assign_mode: "individual", team_id: "" });
  const [assigningShift, setAssigningShift] = useState(false);

  // Payroll state
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [generatingPayroll, setGeneratingPayroll] = useState(false);
  const [payrollDateRange, setPayrollDateRange] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const mon = new Date(now);
    mon.setDate(now.getDate() - diff - 7);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { start: mon.toISOString().split("T")[0], end: sun.toISOString().split("T")[0] };
  });
  const [selectedPayrollPeriod, setSelectedPayrollPeriod] = useState(null);
  const [editingPaySettings, setEditingPaySettings] = useState(null);
  const [savingPaySettings, setSavingPaySettings] = useState(false);

  // Activity Locations state
  const [activityLocationsData, setActivityLocationsData] = useState(null);
  const [loadingActivityLocations, setLoadingActivityLocations] = useState(false);
  const [actLocFilters, setActLocFilters] = useState({ eventType: "", fundraiserId: "", campaignId: "", dateFrom: "", dateTo: "" });

  const loadActivityLocations = async (filters) => {
    setLoadingActivityLocations(true);
    try {
      const f = filters || actLocFilters;
      const params = new URLSearchParams();
      if (f.eventType) params.set("event_type", f.eventType);
      if (f.fundraiserId) params.set("fundraiser_id", f.fundraiserId);
      if (f.campaignId) params.set("campaign_id", f.campaignId);
      if (f.dateFrom) params.set("date_from", f.dateFrom);
      if (f.dateTo) params.set("date_to", f.dateTo);
      const res = await apiClient.get(`/activity-locations?${params.toString()}`);
      setActivityLocationsData(res.data);
    } catch {
      toast.error("Failed to load activity locations");
    } finally {
      setLoadingActivityLocations(false);
    }
  };

  // Location Insights state
  const [insightsLocationId, setInsightsLocationId] = useState("");
  const [locationInsights, setLocationInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const loadLocationInsights = async (locId) => {
    if (!locId) { setLocationInsights(null); return; }
    setLoadingInsights(true);
    try {
      const res = await apiClient.get(`/locations/${locId}/insights`);
      setLocationInsights(res.data);
    } catch {
      toast.error("Failed to load location insights");
      setLocationInsights(null);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Organisation User Management State
  const [orgUsers, setOrgUsers] = useState([]);
  const [loadingOrgUsers, setLoadingOrgUsers] = useState(false);
  const [orgUserActionLoading, setOrgUserActionLoading] = useState(null);
  const [activityWindowMinutes, setActivityWindowMinutes] = useState(30);
  const [syncIntervalMinutes, setSyncIntervalMinutes] = useState(null);
  const [showCreateFundraiserModal, setShowCreateFundraiserModal] = useState(false);
  const [createFundraiserForm, setCreateFundraiserForm] = useState({ name: "", email: "", password: "", profileImageUrl: "", createRole: "fundraiser", managerLevel: "standard" });
  const [creatingFundraiser, setCreatingFundraiser] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);

  // Helper: Calculate date range
  const getDateRange = () => {
    const now = new Date();
    let startDate = null;
    let endDate = now.toISOString();
    
    switch (dateFilter) {
      case "today":
        startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        startDate = yesterday.toISOString();
        endDate = new Date(yesterday.setHours(23, 59, 59, 999)).toISOString();
        break;
      case "last5":
        const fiveDaysAgo = new Date(now);
        fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
        startDate = fiveDaysAgo.toISOString();
        break;
      case "last7":
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        startDate = sevenDaysAgo.toISOString();
        break;
      case "last14":
        const fourteenDaysAgo = new Date(now);
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
        startDate = fourteenDaysAgo.toISOString();
        break;
      case "custom":
        startDate = customStartDate ? new Date(customStartDate).toISOString() : null;
        endDate = customEndDate ? new Date(customEndDate).toISOString() : null;
        break;
      default:
        return { startDate: null, endDate: null };
    }
    
    return { startDate, endDate };
  };

  useEffect(() => {
    document.title = "Engage — Operations";
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _autoDispatchChecked = useRef(false);
  useEffect(() => {
    // Silent monthly board-pack auto-dispatch check (owner-only endpoint; no-op outside days 1-3)
    if (!_autoDispatchChecked.current && (user?.role === "owner" || user?.role === "platform_owner")) {
      _autoDispatchChecked.current = true;
      apiClient.post("/board-pack/auto-dispatch/check").catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "signups") {
      loadSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, signupFilters, dateFilter, customStartDate, customEndDate]);

  useEffect(() => {
    if (selectedCampaign) {
      loadDeliveryConfig(selectedCampaign);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign]);

  // Reload data when campaign filter or date filter changes
  useEffect(() => {
    if (!loading && activeTab === "signups") {
      loadSignups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignFilter]);

  // Reload metrics when date filter changes
  useEffect(() => {
    if (!loading) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, customStartDate, customEndDate]);

  // Load guidance history when guidance tab is shown
  useEffect(() => {
    if (activeTab === "guidance" && selectedFundraiserForGuidance) {
      loadGuidanceHistory(selectedFundraiserForGuidance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedFundraiserForGuidance]);

  // Load campaign settings when settings tab is selected or campaign changes
  useEffect(() => {
    if (activeTab === "settings" && settingsCampaignId) {
      loadCampaignSettings(settingsCampaignId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, settingsCampaignId]);

  // Set default settings campaign when campaigns load
  useEffect(() => {
    if (campaigns.length > 0 && !settingsCampaignId) {
      setSettingsCampaignId(campaigns[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns]);

  // Load team when team tab is selected
  useEffect(() => {
    if (activeTab === "team") {
      loadTeamMembers();
      loadTeamHierarchy();
      loadOrgTree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-refresh team hierarchy every 30s while viewing team tab — surfaces fresh lastSyncAt
  useEffect(() => {
    if (activeTab !== "team") return;
    const intervalId = setInterval(() => {
      loadTeamHierarchy();
      loadTeamMembers();
      loadOrgTree();
    }, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load all team metrics when team members change
  useEffect(() => {
    if (activeTab === "team" && teamMembers.length > 0) {
      loadAllTeamMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, teamMembers.length]);

  // Load pricing plans when upgrade modal opens
  useEffect(() => {
    if (showUpgradeModal) {
      loadPricingPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpgradeModal]);

  // Load org users and settings when users tab is selected
  useEffect(() => {
    if (activeTab === "users") {
      loadOrgUsers();
      loadActivitySettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const params = new URLSearchParams();
      if (eventFilters.source) params.append("source", eventFilters.source);
      if (eventFilters.userId) params.append("user_id", eventFilters.userId);
      if (eventFilters.status) params.append("status", eventFilters.status);
      if (eventFilters.dateFrom) params.append("date_from", new Date(eventFilters.dateFrom).toISOString());
      if (eventFilters.dateTo) params.append("date_to", new Date(eventFilters.dateTo).toISOString());
      params.append("limit", "100");

      // Activity: financial + communication + billing
      const actParams = new URLSearchParams(params);
      const actFinancial = await apiClient.get(`/events?${actParams}&category=financial`);
      const actComms = await apiClient.get(`/events?${actParams}&category=communication`);
      const merged = [...(actFinancial.data || []), ...(actComms.data || [])].sort((a, b) => b.timestamp?.localeCompare(a.timestamp));
      setActivityEvents(merged.slice(0, 100));

      // System: system + operations
      const sysParams = new URLSearchParams(params);
      const sysSystem = await apiClient.get(`/events?${sysParams}&category=system`);
      const sysOps = await apiClient.get(`/events?${sysParams}&category=operations`);
      const sysMerged = [...(sysSystem.data || []), ...(sysOps.data || [])].sort((a, b) => b.timestamp?.localeCompare(a.timestamp));
      setSystemEvents(sysMerged.slice(0, 50));
    } catch (err) {
      console.error("Failed to load events", err);
    } finally {
      setLoadingEvents(false);
    }
  }, [eventFilters]);

  const loadData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const metricsParams = new URLSearchParams();
      if (startDate) metricsParams.append('date_from', startDate);
      if (endDate) metricsParams.append('date_to', endDate);
      const metricsUrl = metricsParams.toString() 
        ? `/metrics/organisation?${metricsParams.toString()}`
        : '/metrics/organisation';
      
      const [metricsRes, campaignsRes, orgStatusRes, configsRes] = await Promise.allSettled([
        apiClient.get(metricsUrl),
        apiClient.get("/campaigns"),
        apiClient.get("/organisation/status"),
        apiClient.get("/delivery-configs")
      ]);
      if (metricsRes.status === "fulfilled") {
        setMetrics(metricsRes.value.data);
        setFundraisers(metricsRes.value.data?.fundraisers || []);
      }
      if (campaignsRes.status === "fulfilled") {
        setCampaigns(campaignsRes.value.data || []);
        if (campaignsRes.value.data?.length > 0) setSelectedCampaign(campaignsRes.value.data[0].id);
      }
      if (orgStatusRes.status === "fulfilled") setOrgStatus(orgStatusRes.value.data);
      if (configsRes.status === "fulfilled") setDeliveryConfigs(configsRes.value.data || []);
    } catch (error) {
      console.error("Dashboard load error", error);
    } finally {
      setLoading(false);
    }
  };

  const loadGuidanceHistory = async (fundraiserId) => {
    try {
      const res = await apiClient.get(`/guidance/history/${fundraiserId}`);
      setGuidanceHistory(res.data || []);
    } catch (error) {
      setGuidanceHistory([]);
    }
  };

  // Load flagged submissions for age review
  const fetchFlaggedSubmissions = async () => {
    setLoadingFlagged(true);
    try {
      const params = new URLSearchParams();
      if (flaggedDateFilter.startDate) params.append('start_date', flaggedDateFilter.startDate);
      if (flaggedDateFilter.endDate) params.append('end_date', flaggedDateFilter.endDate);
      
      const url = params.toString() 
        ? `/conversions/flagged?${params.toString()}`
        : '/conversions/flagged';
      
      const res = await apiClient.get(url);
      setFlaggedSubmissions(res.data || []);
    } catch (error) {
      setFlaggedSubmissions([]);
    } finally {
      setLoadingFlagged(false);
    }
  };

  // Export flagged submissions as CSV
  const exportFlaggedSubmissions = async () => {
    setExportingFlagged(true);
    try {
      const params = new URLSearchParams();
      if (flaggedDateFilter.startDate) params.append('start_date', flaggedDateFilter.startDate);
      if (flaggedDateFilter.endDate) params.append('end_date', flaggedDateFilter.endDate);
      
      const url = params.toString() 
        ? `/conversions/flagged/export?${params.toString()}`
        : '/conversions/flagged/export';
      
      const response = await apiClient.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `flagged_submissions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export flagged submissions");
    } finally {
      setExportingFlagged(false);
    }
  };

  // Load campaign settings from API
  const loadCampaignSettings = async (campaignId) => {
    try {
      const camp = campaigns.find(c => c.id === campaignId);
      if (camp) {
        setCampaignSettings({
          enableLottery: camp.enableLottery ?? true,
          enableRegularGiving: camp.enableRegularGiving ?? true,
          enableOneOff: camp.enableOneOff ?? true,
          lotterySinglePrice: camp.lotterySinglePrice ?? 5.00,
          lotteryDoublePrice: camp.lotteryDoublePrice ?? 10.00,
          regularPresets: camp.regularPresets ?? [5, 10, 15, 20, 25],
          allowCustomAmount: camp.allowCustomAmount ?? true,
          enableContactless: camp.enableContactless ?? false,
          oneOffRequireDetails: camp.oneOffRequireDetails ?? false,
          allowEmailConsent: camp.allowEmailConsent ?? true,
          allowSmsConsent: camp.allowSmsConsent ?? true,
          allowPhoneConsent: camp.allowPhoneConsent ?? true,
          allowPostConsent: camp.allowPostConsent ?? true,
          requirePhone: camp.requirePhone ?? false,
          requireEmail: camp.requireEmail ?? false,
          requireAddress: camp.requireAddress ?? true,
          enableSMS: camp.enableSMS ?? true,
          enableAutoThankYouSMS: camp.enableAutoThankYouSMS ?? true,
          disclosureText: camp.disclosureText ?? "",
          directDebitGuaranteeText: camp.directDebitGuaranteeText ?? "",
          oneOffDisclosureText: camp.oneOffDisclosureText ?? "",
          minAge: camp.minAge ?? 18,
          maxAge: camp.maxAge ?? 100,
          // Over-Age Rule Engine
          overAgeThreshold: camp.overAgeThreshold ?? 80,
          overAgeAction: camp.overAgeAction ?? "allow",
          // Payment Frequency
          enableAnnualPayments: camp.enableAnnualPayments ?? false,
          // Gift Aid
          enableGiftAid: camp.enableGiftAid ?? false,
          giftAidStatement: camp.giftAidStatement ?? ""
        });
        setPresetsInput((camp.regularPresets ?? [5, 10, 15, 20, 25]).join(", "));
      }
    } catch (error) {
      toast.error("Failed to load campaign settings");
    }
  };

  // Save campaign settings
  const saveCampaignSettings = async () => {
    if (!settingsCampaignId) {
      toast.error("Please select a campaign");
      return;
    }

    // Parse presets from comma-separated string
    const presets = presetsInput
      .split(",")
      .map(v => parseFloat(v.trim()))
      .filter(v => !isNaN(v) && v > 0);

    setSavingCampaignSettings(true);
    try {
      await apiClient.put(`/campaigns/${settingsCampaignId}`, {
        enableLottery: campaignSettings.enableLottery,
        enableRegularGiving: campaignSettings.enableRegularGiving,
        enableOneOff: campaignSettings.enableOneOff,
        lotterySinglePrice: campaignSettings.lotterySinglePrice,
        lotteryDoublePrice: campaignSettings.lotteryDoublePrice,
        regularPresets: presets.length > 0 ? presets : [5, 10, 15, 20, 25],
        allowCustomAmount: campaignSettings.allowCustomAmount,
        enableContactless: campaignSettings.enableContactless,
        oneOffRequireDetails: campaignSettings.oneOffRequireDetails,
        allowEmailConsent: campaignSettings.allowEmailConsent,
        allowSmsConsent: campaignSettings.allowSmsConsent,
        allowPhoneConsent: campaignSettings.allowPhoneConsent,
        allowPostConsent: campaignSettings.allowPostConsent,
        requirePhone: campaignSettings.requirePhone,
        requireEmail: campaignSettings.requireEmail,
        requireAddress: campaignSettings.requireAddress,
        enableSMS: campaignSettings.enableSMS,
        enableAutoThankYouSMS: campaignSettings.enableAutoThankYouSMS,
        disclosureText: campaignSettings.disclosureText || null,
        directDebitGuaranteeText: campaignSettings.directDebitGuaranteeText || null,
        oneOffDisclosureText: campaignSettings.oneOffDisclosureText || null,
        minAge: campaignSettings.minAge,
        maxAge: campaignSettings.maxAge,
        // Over-Age Rule Engine
        overAgeThreshold: campaignSettings.overAgeThreshold,
        overAgeAction: campaignSettings.overAgeAction,
        // Payment Frequency
        enableAnnualPayments: campaignSettings.enableAnnualPayments,
        // Gift Aid
        enableGiftAid: !!campaignSettings.enableGiftAid,
        giftAidStatement: campaignSettings.giftAidStatement || null
      });
      toast.success("Campaign settings saved");
      // Refresh campaigns to update local state
      const campaignsRes = await apiClient.get("/campaigns");
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error("Failed to save campaign settings");
    } finally {
      setSavingCampaignSettings(false);
    }
  };

  // Load team members
  const loadTeamMembers = async () => {
    setLoadingTeam(true);
    try {
      const res = await apiClient.get("/team");
      setTeamMembers(res.data || []);
    } catch (error) {
      setTeamMembers([]);
    } finally {
      setLoadingTeam(false);
    }
  };

  // Toggle team member selection
  const toggleTeamMemberSelection = (userId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  // Assign selected team members to a campaign
  const assignSelectedToCampaign = async (campaignId) => {
    if (selectedTeamMembers.length === 0) {
      toast.error("Select at least one team member");
      return;
    }
    setAssigningCampaigns(true);
    try {
      await apiClient.post(`/campaigns/${campaignId}/assign-fundraisers`, selectedTeamMembers);
      toast.success(`Assigned ${selectedTeamMembers.length} fundraisers to campaign`);
      setSelectedTeamMembers([]);
      loadTeamMembers();
    } catch (error) {
      toast.error("Failed to assign fundraisers");
    } finally {
      setAssigningCampaigns(false);
    }
  };

  // Remove fundraiser from campaign
  const removeFundraiserFromCampaign = async (userId, campaignId) => {
    try {
      await apiClient.post(`/campaigns/${campaignId}/remove-fundraisers`, [userId]);
      toast.success("Removed from campaign");
      loadTeamMembers();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  // Create new campaign
  const createCampaign = async () => {
    if (!newCampaign.name.trim()) {
      toast.error("Campaign name is required");
      return;
    }
    setCreatingCampaign(true);
    try {
      // Include organisationId from the current user's context
      await apiClient.post("/campaigns", {
        name: newCampaign.name,
        tagline: newCampaign.description || "",
        active: newCampaign.active,
        charityId: newCampaign.charityId || null,  // OCI V2.7B: link charity client
        organisationId: orgStatus?.id || ""  // Required field for campaign creation
      });
      toast.success("Campaign created");
      setShowCreateCampaignModal(false);
      // Reset form state completely
      setNewCampaign({ name: "", description: "", active: true, charityId: "" });
      // Reload campaigns
      const campaignsRes = await apiClient.get("/campaigns");
      setCampaigns(campaignsRes.data);
    } catch (error) {
      // Show specific error message from backend
      const errorMsg = error.response?.data?.detail || "Failed to create campaign";
      toast.error(errorMsg);
    } finally {
      setCreatingCampaign(false);
    }
  };

  // OCI V2.38 — Archive / restore the selected campaign (owner-only). Data is retained.
  const toggleArchiveCampaign = async (archive) => {
    if (!settingsCampaignId) return;
    if (archive && !window.confirm("Archive this campaign? It will stop accepting new donations (fundraiser + referral forms), but all donors, subscriptions, payments and reports are retained. You can restore it anytime.")) return;
    try {
      await apiClient.put(`/campaigns/${settingsCampaignId}/${archive ? "archive" : "restore"}`);
      toast.success(archive ? "Campaign archived" : "Campaign restored");
      const campaignsRes = await apiClient.get("/campaigns");
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${archive ? "archive" : "restore"} campaign`);
    }
  };


  // Create new location
  const createLocation = async () => {
    if (!newLocation.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    setCreatingLocation(true);
    try {
      // Backend derives organisationId from JWT - do NOT pass it
      const payload = {
        name: newLocation.name,
        address: newLocation.address || null,
        campaignId: newLocation.campaignId || null
      };
      await apiClient.post("/locations", payload);
      toast.success("Location created");
      setShowCreateLocationModal(false);
      setNewLocation({ name: "", address: "", campaignId: "" });
      // Reload locations
      const locRes = await apiClient.get("/locations");
      setLocations(locRes.data || []);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to create location";
      toast.error(errorMsg);
    } finally {
      setCreatingLocation(false);
    }
  };

  // Load locations
  const loadLocations = async () => {
    try {
      const res = await apiClient.get("/locations");
      setLocations(res.data || []);
    } catch (error) {
      setLocations([]);
    }
  };

  // Load organisation branding
  const loadOrgBranding = async () => {
    try {
      const res = await apiClient.get("/organisation/branding");
      setOrgBranding({
        orgLogoUrl: res.data.orgLogoUrl || "",
        orgDisplayName: res.data.orgDisplayName || ""
      });
    } catch (error) {
      // Ignore - branding is optional
    }
  };

  // Upload organisation logo
  const uploadOrgLogo = async (file) => {
    setUploadingOrgLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/uploads/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setOrgBranding(prev => ({ ...prev, orgLogoUrl: res.data.url }));
      toast.success("Organisation logo uploaded");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingOrgLogo(false);
    }
  };

  // Save organisation branding
  const saveOrgBranding = async () => {
    setSavingOrgBranding(true);
    try {
      await apiClient.put("/organisation/branding", {
        orgLogoUrl: orgBranding.orgLogoUrl || null,
        orgDisplayName: orgBranding.orgDisplayName || null
      });
      toast.success("Organisation branding saved");
    } catch (error) {
      toast.error("Failed to save branding");
    } finally {
      setSavingOrgBranding(false);
    }
  };

  // ==================== SCHEDULE FUNCTIONS ====================
  
  // Get week dates from start date
  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  };

  // Load schedules for current week
  const loadSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const weekDates = getWeekDates(scheduleWeekStart);
      const startDate = weekDates[0];
      const endDate = weekDates[6];
      let url = `/schedules?startDate=${startDate}&endDate=${endDate}`;
      if (plannerCampaignFilter) url += `&campaignId=${plannerCampaignFilter}`;
      const res = await apiClient.get(url);
      setSchedules(res.data || []);
    } catch (error) {
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Load team preferences
  const loadTeamPreferences = async () => {
    try {
      const res = await apiClient.get("/preferences/team");
      setTeamPreferences(res.data || {});
    } catch (error) {
      // Preferences not critical - silently fail
      setTeamPreferences({});
    }
  };

  // Load billing data
  const loadBilling = async () => {
    setLoadingBilling(true);
    try {
      const res = await apiClient.get("/billing/projected");
      setBillingData(res.data);
    } catch (e) {
      setBillingData(null);
    } finally {
      setLoadingBilling(false);
    }
  };

  const loadUsage = async () => {
    setLoadingUsage(true);
    try {
      const res = await apiClient.get("/billing/usage");
      setUsageData(res.data);
    } catch (e) {
      setUsageData(null);
    } finally {
      setLoadingUsage(false);
    }
  };

  // Load manual charges
  const loadManualCharges = async () => {
    if (!user?.organisationId) return;
    setLoadingCharges(true);
    try {
      const res = await apiClient.get(`/billing/charges/${user.organisationId}`);
      setManualCharges(res.data || []);
    } catch (e) {
      setManualCharges([]);
    } finally {
      setLoadingCharges(false);
    }
  };

  // Load pricing lock status
  const loadLockStatus = async () => {
    if (!user?.organisationId) return;
    try {
      const res = await apiClient.get(`/billing/lock-status/${user.organisationId}`);
      setLockStatus(res.data);
    } catch (e) {
    }
  };

  // Create manual charge
  const createManualCharge = async () => {
    if (!chargeForm.amount || parseFloat(chargeForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!chargeForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    setCreatingCharge(true);
    try {
      const res = await apiClient.post(`/billing/manual-charge/${user.organisationId}`, {
        amount: parseFloat(chargeForm.amount),
        description: chargeForm.description,
        originUrl: window.location.origin
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.success("Charge created");
        setChargeForm({ amount: "", description: "" });
        loadManualCharges();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create charge");
    } finally {
      setCreatingCharge(false);
    }
  };

  // Pay pending charge
  const payCharge = async (chargeId) => {
    try {
      const res = await apiClient.post(`/billing/charges/${chargeId}/pay`);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initiate payment");
    }
  };

  // Lock pricing
  const lockPricing = async (lockType) => {
    setLockingPricing(true);
    try {
      const res = await apiClient.post(`/billing/lock-pricing/${user.organisationId}`, {
        lock_type: lockType,
        originUrl: window.location.origin
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else if (res.data?.success) {
        toast.success("Pricing locked successfully!");
        setSelectedLockPlan(null);
        loadLockStatus();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to lock pricing");
    } finally {
      setLockingPricing(false);
    }
  };

  // Load earnings (derived)
  const loadEarnings = useCallback(async () => {
    if (!user?.organisationId) return;
    setLoadingEarnings(true);
    try {
      const params = new URLSearchParams();
      if (earningsFilter.campaignId) params.append("campaign_id", earningsFilter.campaignId);
      if (earningsFilter.fundraiserId) params.append("fundraiser_id", earningsFilter.fundraiserId);
      const res = await apiClient.get(`/earnings?${params}`);
      setEarningsData(res.data);
    } catch (e) {
    } finally {
      setLoadingEarnings(false);
    }
  }, [earningsFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load cancellations
  const loadCancellations = async () => {
    setLoadingCancellations(true);
    try {
      const res = await apiClient.get("/cancellations");
      setCancellations(res.data || []);
    } catch (e) {
    } finally {
      setLoadingCancellations(false);
    }
  };

  // Record single cancellation
  const recordCancellation = async () => {
    if (!cancelForm.taptrack_id.trim()) { toast.error("Engage Tracking ID required"); return; }
    setSubmittingCancel(true);
    try {
      await apiClient.post("/cancellations", { taptrack_id: cancelForm.taptrack_id, reason: cancelForm.reason });
      toast.success("Cancellation recorded");
      setCancelForm({ taptrack_id: "", reason: "" });
      loadCancellations();
      if (earningsData) loadEarnings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record cancellation");
    } finally {
      setSubmittingCancel(false);
    }
  };

  // Reverse cancellation
  const reverseCancellation = async (taptractId) => {
    try {
      await apiClient.post(`/cancellations/${taptractId}/reverse`);
      toast.success("Cancellation reversed");
      loadCancellations();
      if (earningsData) loadEarnings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reverse");
    }
  };

  // Upload cancellation CSV
  const uploadCancellationCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const res = await apiClient.post("/cancellations/upload", text, { headers: { "Content-Type": "text/plain" } });
      toast.success(`Processed: ${res.data.cancelled} cancelled, ${res.data.skipped} skipped`);
      loadCancellations();
      if (earningsData) loadEarnings();
    } catch (err) {
      toast.error("Couldn't upload the CSV. Please check the file and try again.");
    }
    e.target.value = "";
  };

  // Load bond settings
  const loadBondSettings = async () => {
    try {
      const res = await apiClient.get("/organisation/bond-settings");
      setBondSettings(res.data);
    } catch (e) {
    }
  };

  // Save bond settings
  const saveBondSettings = async () => {
    setSavingBond(true);
    try {
      await apiClient.put("/organisation/bond-settings", bondSettings);
      toast.success("Bond settings saved");
    } catch (e) {
      toast.error("Failed to save bond settings");
    } finally {
      setSavingBond(false);
    }
  };

  // Load API settings
  const loadApiSettings = async () => {
    try {
      const res = await apiClient.get("/organisation/api-settings");
      setApiSettings(res.data);
    } catch (e) {
    }
  };

  // Generate API key
  const generateApiKey = async () => {
    try {
      const res = await apiClient.post("/organisation/api-key");
      setNewApiKey(res.data.api_key);
      loadApiSettings();
      toast.success("API key generated");
    } catch (e) {
      toast.error("Failed to generate API key");
    }
  };

  // Revoke API key
  const revokeApiKey = async () => {
    try {
      await apiClient.delete("/organisation/api-key");
      setNewApiKey(null);
      loadApiSettings();
      toast.success("API key revoked");
    } catch (e) {
      toast.error("Failed to revoke API key");
    }
  };

  // Load team availability + quarter overview
  const loadSchedulingData = async () => {
    setLoadingTeamAvail(true);
    try {
      const [teamRes, quarterRes] = await Promise.all([
        apiClient.get("/availability/team"),
        apiClient.get("/assignments/quarter-overview")
      ]);
      setTeamAvailability(teamRes.data);
      setQuarterOverview(quarterRes.data);
    } catch (e) {
    } finally {
      setLoadingTeamAvail(false);
    }
  };

  // Create assignment
  const createAssignment = async () => {
    if (!assignForm.campaign_id) { toast.error("Please select a campaign"); return; }
    if (!assignForm.location_id) { toast.error("Please select a location"); return; }
    if (!assignForm.date) { toast.error("Please select a date"); return; }
    if (assignForm.assign_mode === "individual" && !assignForm.fundraiser_id) { toast.error("Please select a fundraiser"); return; }
    if (assignForm.assign_mode === "team" && !assignForm.team_id) { toast.error("Please select a team"); return; }
    setAssigningShift(true);
    try {
      const payload = {
        campaign_id: assignForm.campaign_id,
        location_id: assignForm.location_id,
        date: assignForm.date,
      };
      if (assignForm.assign_mode === "team") {
        payload.team_id = assignForm.team_id;
      } else {
        payload.fundraiser_id = assignForm.fundraiser_id;
      }
      const res = await apiClient.post("/assignments", payload);
      if (res.data?.warning) toast.warning(res.data.warning);
      const created = res.data?.created || 1;
      const skipped = res.data?.skipped || 0;
      if (skipped > 0) toast.info(`${skipped} assignment(s) skipped (already exist)`);
      toast.success(`${created} assignment(s) created`);
      setAssignForm({ fundraiser_id: "", campaign_id: assignForm.campaign_id, location_id: assignForm.location_id, date: assignForm.date, assign_mode: assignForm.assign_mode, team_id: "" });
      loadSchedulingData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create assignment");
    } finally {
      setAssigningShift(false);
    }
  };

  // === PAYROLL FUNCTIONS ===
  const loadPayrollPeriods = async () => {
    try {
      const res = await apiClient.get("/payroll/periods");
      setPayrollPeriods(res.data || []);
    } catch (e) {
      setPayrollPeriods([]);
    }
  };

  const loadPayrollRecords = async (weekStart, weekEnd) => {
    setLoadingPayroll(true);
    try {
      const res = await apiClient.get(`/payroll?week_start=${weekStart}&week_end=${weekEnd}`);
      setPayrollRecords(res.data || []);
    } catch (e) {
      setPayrollRecords([]);
    } finally {
      setLoadingPayroll(false);
    }
  };

  const generatePayroll = async () => {
    if (!payrollDateRange.start || !payrollDateRange.end) { toast.error("Select date range"); return; }
    setGeneratingPayroll(true);
    try {
      const res = await apiClient.post("/payroll/generate", { weekStart: payrollDateRange.start, weekEnd: payrollDateRange.end });
      toast.success(`Payroll generated: ${res.data.recordCount} records`);
      setSelectedPayrollPeriod({ weekStart: payrollDateRange.start, weekEnd: payrollDateRange.end });
      loadPayrollRecords(payrollDateRange.start, payrollDateRange.end);
      loadPayrollPeriods();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate payroll");
    } finally {
      setGeneratingPayroll(false);
    }
  };

  const deletePayroll = async (payrollId, weekStart, weekEnd) => {
    try {
      await apiClient.delete(`/payroll/${payrollId}`);
      toast.success("Payroll deleted");
      setPayrollRecords([]);
      setSelectedPayrollPeriod(null);
      loadPayrollPeriods();
    } catch (e) {
      toast.error("Failed to delete payroll");
    }
  };

  const exportPayrollCsv = async () => {
    if (!selectedPayrollPeriod) { toast.error("Select a payroll period first"); return; }
    try {
      const res = await apiClient.get(`/payroll/export?week_start=${selectedPayrollPeriod.weekStart}&week_end=${selectedPayrollPeriod.weekEnd}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll_${selectedPayrollPeriod.weekStart}_to_${selectedPayrollPeriod.weekEnd}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payroll CSV exported");
    } catch (e) {
      toast.error("Failed to export payroll");
    }
  };

  const savePaySettings = async (userId, payPerSignup, bondAmount) => {
    setSavingPaySettings(true);
    try {
      await apiClient.put(`/team/${userId}/pay-settings`, { payPerSignup: parseFloat(payPerSignup) || 0, bondAmount: parseFloat(bondAmount) || 0 });
      toast.success("Pay settings saved");
      setEditingPaySettings(null);
      loadTeamMembers();
    } catch (e) {
      toast.error("Failed to save pay settings");
    } finally {
      setSavingPaySettings(false);
    }
  };

  // === ORGANISATION USER MANAGEMENT FUNCTIONS ===
  const loadOrgUsers = async () => {
    setLoadingOrgUsers(true);
    try {
      const res = await apiClient.get("/organisation/users");
      setOrgUsers(res.data || []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoadingOrgUsers(false);
    }
  };

  const loadActivitySettings = async () => {
    try {
      const res = await apiClient.get("/organisation/settings");
      setActivityWindowMinutes(res.data?.activityWindowMinutes || 30);
      setSyncIntervalMinutes(res.data?.syncIntervalMinutes || null);
    } catch (e) { /* keep default */ }
  };

  const saveActivityWindow = async (val) => {
    setActivityWindowMinutes(val);
    try {
      await apiClient.put("/organisation/settings", { activityWindowMinutes: val });
      toast.success(`Activity window set to ${val} minutes`);
    } catch (e) {
      toast.error("Failed to save setting");
    }
  };

  const saveSyncInterval = async (val) => {
    const parsed = val === "off" ? null : Number(val);
    setSyncIntervalMinutes(parsed);
    try {
      await apiClient.put("/organisation/settings", { syncIntervalMinutes: parsed });
      toast.success(parsed ? `Auto sync set to every ${parsed} minutes` : "Auto sync disabled");
    } catch (e) {
      toast.error("Failed to save sync interval");
    }
  };

  const isUserActive = (lastActiveAt) => {
    if (!lastActiveAt) return false;
    const diff = (Date.now() - new Date(lastActiveAt).getTime()) / 60000;
    return diff < activityWindowMinutes;
  };

  const getActivityTooltip = (lastActiveAt) => {
    if (!lastActiveAt) return "Never active";
    const diff = Math.round((Date.now() - new Date(lastActiveAt).getTime()) / 60000);
    if (diff < activityWindowMinutes) return `Active within last ${activityWindowMinutes} min`;
    if (diff < 60) return `Last active ${diff} min ago`;
    if (diff < 1440) return `Last active ${Math.round(diff / 60)}h ago`;
    return `Last active ${Math.round(diff / 1440)}d ago`;
  };

  const handleOrgDeactivateUser = async (userId, email) => {
    if (!window.confirm(`Deactivate "${email}"? They will be unable to log in.`)) return;
    setOrgUserActionLoading(userId);
    try {
      await apiClient.put(`/organisation/users/${userId}/deactivate`);
      toast.success(`${email} deactivated`);
      loadOrgUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to deactivate user");
    } finally {
      setOrgUserActionLoading(null);
    }
  };

  const handleOrgActivateUser = async (userId, email) => {
    setOrgUserActionLoading(userId);
    try {
      await apiClient.put(`/organisation/users/${userId}/activate`);
      toast.success(`${email} activated`);
      loadOrgUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate user");
    } finally {
      setOrgUserActionLoading(null);
    }
  };

  const handleOrgDeleteUser = async (userId, email) => {
    if (!window.confirm(`PERMANENTLY DELETE "${email}"? This cannot be undone.`)) return;
    setOrgUserActionLoading(userId);
    try {
      await apiClient.delete(`/organisation/users/${userId}`);
      toast.success(`${email} deleted`);
      loadOrgUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    } finally {
      setOrgUserActionLoading(null);
    }
  };

  const handleOrgResetPassword = async (userId, email) => {
    setOrgUserActionLoading(userId);
    try {
      const res = await apiClient.post(`/organisation/users/${userId}/reset-password`);
      toast.success(`Reset link generated for ${email}`);
      const copied = await safeCopyToClipboard(res.data.resetLink);
      if (copied) toast.info("Reset link copied to clipboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate reset link");
    } finally {
      setOrgUserActionLoading(null);
    }
  };

  const handleSetPasswordManually = async () => {
    if (!setPasswordTarget) return;
    if (newPasswordForm.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPasswordForm.password !== newPasswordForm.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSettingPassword(true);
    try {
      await apiClient.post(`/organisation/users/${setPasswordTarget.id}/set-password`, { password: newPasswordForm.password });
      toast.success(`Password updated for ${setPasswordTarget.email}`);
      setShowSetPasswordModal(false);
      setNewPasswordForm({ password: "", confirm: "" });
      setSetPasswordTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };

  const handleCreateFundraiser = async () => {
    if (!createFundraiserForm.name.trim() || !createFundraiserForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    const password = createFundraiserForm.password.trim() || `TT${Math.random().toString(36).slice(2, 8)}${Math.floor(Math.random() * 100)}`;
    setCreatingFundraiser(true);
    try {
      const payload = {
        name: createFundraiserForm.name.trim(),
        email: createFundraiserForm.email.trim(),
        password: password,
        role: createFundraiserForm.createRole,
        profileImageUrl: createFundraiserForm.profileImageUrl || null
      };
      if (createFundraiserForm.createRole === "manager") {
        payload.managerLevel = createFundraiserForm.managerLevel;
      }
      await apiClient.post("/users/invite", payload);
      setCreatedCredentials({ email: createFundraiserForm.email.trim(), password, name: createFundraiserForm.name.trim(), role: createFundraiserForm.createRole });
      setCreateFundraiserForm({ name: "", email: "", password: "", profileImageUrl: "", createRole: "fundraiser", managerLevel: "standard" });
      loadOrgUsers();
      loadTeamMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingFundraiser(false);
    }
  };

  const handleProfilePhotoUpload = async (file, onSuccess) => {
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Invalid file type. Only PNG, JPEG, WEBP allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }
    setUploadingProfilePhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/uploads/profile-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onSuccess(res.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  // Check if any assigned user has a non-preferred day conflict
  const checkPreferenceConflict = (userIds, date) => {
    if (!userIds || userIds.length === 0) return null;
    
    const dayOfWeek = new Date(date).getDay();
    // Convert JS day (0=Sun) to our format (0=Mon)
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    const conflicts = [];
    for (const userId of userIds) {
      const userPrefs = teamPreferences[userId];
      if (userPrefs?.preferences) {
        const dayPref = userPrefs.preferences.find(p => p.dayOfWeek === adjustedDay);
        if (dayPref?.preferenceType === "not_preferred") {
          conflicts.push(userPrefs.name);
        }
      }
    }
    return conflicts.length > 0 ? conflicts : null;
  };

  // Get preference indicator for a user on a day
  const getUserDayPreference = (userId, date) => {
    const dayOfWeek = new Date(date).getDay();
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    const userPrefs = teamPreferences[userId];
    if (!userPrefs?.preferences) return null;
    
    const dayPref = userPrefs.preferences.find(p => p.dayOfWeek === adjustedDay);
    return dayPref?.preferenceType || null;
  };

  // Group schedules by date
  const getSchedulesByDay = () => {
    const weekDates = getWeekDates(scheduleWeekStart);
    const grouped = {};
    weekDates.forEach(date => {
      grouped[date] = schedules.filter(s => s.date === date);
    });
    return grouped;
  };

  // Save shift (create or update)
  const handleSaveShift = async (editMode = "single") => {
    // Location and date are always required; userIds can be empty (unassigned)
    if (!shiftForm.locationId || !shiftForm.date || !shiftForm.startTime || !shiftForm.endTime) {
      toast.error("Please fill in location, date, and times");
      return;
    }
    
    // Check for preference conflicts (but allow save)
    const conflicts = checkPreferenceConflict(shiftForm.userIds, shiftForm.date);
    if (conflicts && !pendingShiftWithWarning) {
      setPendingShiftWithWarning({ editMode });
      setShowPreferenceWarning(true);
      return;
    }
    
    // Clear warning state
    setPendingShiftWithWarning(null);
    
    setSavingShift(true);
    try {
      if (editingShift) {
        // Update existing shift
        await apiClient.put(`/schedules/${editingShift.id}`, {
          ...shiftForm,
          editMode: editMode
        });
        toast.success(editMode === "series" ? "Series updated" : "Shift updated");
      } else {
        // Create new shift(s)
        const result = await apiClient.post("/schedules", shiftForm);
        const count = Array.isArray(result.data) ? result.data.length : 1;
        toast.success(`${count} shift${count > 1 ? 's' : ''} created`);
      }
      setShowAddShiftModal(false);
      setEditingShift(null);
      resetShiftForm();
      loadSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save shift");
    } finally {
      setSavingShift(false);
    }
  };

  // Continue save after preference warning acknowledged
  const continueAfterWarning = () => {
    setShowPreferenceWarning(false);
    const editMode = pendingShiftWithWarning?.editMode || "single";
    setPendingShiftWithWarning({ confirmed: true, editMode });
    // Re-call save with confirmed flag - handled by pendingShiftWithWarning being set
    handleSaveShift(editMode);
  };

  // Reset shift form to defaults
  const resetShiftForm = () => {
    setShiftForm({
      userIds: [],
      locationId: "",
      campaignId: "",
      date: "",
      endDate: "",
      startTime: "09:00",
      endTime: "17:00",
      isRecurring: false,
      repeatWeeks: 4
    });
    setShiftSuggestions([]);
  };

  // Duplicate current week forward
  const handleDuplicateWeek = async () => {
    setDuplicating(true);
    try {
      const payload = { sourceWeekStart: scheduleWeekStart, weeksForward: duplicateWeeksForward };
      if (plannerCampaignFilter) payload.campaignId = plannerCampaignFilter;
      const res = await apiClient.post("/schedules/duplicate-week", payload);
      const { created, skipped } = res.data;
      toast.success(`${created} shift${created !== 1 ? 's' : ''} created${skipped ? `, ${skipped} skipped (conflicts)` : ''}`);
      setShowDuplicateWeek(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to duplicate week");
    } finally {
      setDuplicating(false);
    }
  };

  // Auto-Plan Next Week — open modal and fetch draft
  const openAutoPlanNextWeek = async () => {
    setAutoPlanData(null);
    setShowAutoPlanModal(true);
    setAutoPlanLoading(true);
    try {
      const res = await apiClient.post("/schedules/auto-plan-next-week", {});
      setAutoPlanData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate draft plan");
      setAutoPlanData({ drafts: [], skipped: [], summary: { created: 0 } });
    } finally {
      setAutoPlanLoading(false);
    }
  };

  // Save drafts as real shifts
  const saveAutoPlan = async () => {
    if (!autoPlanData?.drafts?.length) return;
    setAutoPlanSaving(true);
    try {
      const res = await apiClient.post("/schedules/save-draft-plan", { drafts: autoPlanData.drafts });
      const { created, skipped } = res.data;
      const skippedCount = Array.isArray(skipped) ? skipped.length : 0;
      toast.success(`${created} shift${created !== 1 ? "s" : ""} created${skippedCount ? `, ${skippedCount} skipped (clashes)` : ""}`);
      setShowAutoPlanModal(false);
      setAutoPlanData(null);
      // Jump calendar to planned week and refresh
      if (res.data && autoPlanData?.weekStart) {
        setScheduleWeekStart(autoPlanData.weekStart);
      }
      loadSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save draft plan");
    } finally {
      setAutoPlanSaving(false);
    }
  };



  // Load suggestions for shift modal
  const loadShiftSuggestions = async (date, locationId, campaignId) => {
    if (!date) return;
    try {
      let url = `/scheduling/suggestions?date=${date}`;
      if (locationId) url += `&locationId=${locationId}`;
      if (campaignId) url += `&campaignId=${campaignId}`;
      const res = await apiClient.get(url);
      setShiftSuggestions(res.data || []);
    } catch {
      setShiftSuggestions([]);
    }
  };

  // Delete shift - ask for mode if recurring
  const handleDeleteShift = async (shift) => {
    if (shift.seriesId && shift.isRecurring) {
      setPendingDeleteShift(shift);
      setShowDeleteModeDialog(true);
    } else {
      await confirmDeleteShift(shift.id, "single");
    }
  };

  // Confirm delete with mode
  const confirmDeleteShift = async (shiftId, deleteMode) => {
    try {
      await apiClient.delete(`/schedules/${shiftId}?deleteMode=${deleteMode}`);
      toast.success(deleteMode === "series" ? "Series deleted" : "Shift deleted");
      loadSchedules();
    } catch (error) {
      toast.error("Failed to delete shift");
    } finally {
      setShowDeleteModeDialog(false);
      setPendingDeleteShift(null);
    }
  };

  // Open edit modal - ask for mode if recurring
  const handleEditShift = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      userIds: shift.userIds || (shift.userId ? [shift.userId] : []),
      locationId: shift.locationId,
      campaignId: shift.campaignId || "",
      date: shift.date,
      startTime: shift.startTime,
      endTime: shift.endTime,
      isRecurring: shift.isRecurring || false,
      repeatWeeks: 4
    });
    
    if (shift.seriesId && shift.isRecurring) {
      // Ask user if they want to edit single or series
      setShowEditModeDialog(true);
    } else {
      setShowAddShiftModal(true);
    }
  };

  // Handle edit mode selection
  const handleEditModeSelect = (mode) => {
    setPendingEditMode(mode);
    setShowEditModeDialog(false);
    setShowAddShiftModal(true);
  };

  // Toggle fundraiser selection for multi-select
  const toggleShiftUserSelection = (userId) => {
    setShiftForm(prev => {
      const current = prev.userIds || [];
      if (current.includes(userId)) {
        return { ...prev, userIds: current.filter(id => id !== userId) };
      } else {
        return { ...prev, userIds: [...current, userId] };
      }
    });
  };

  // Navigate week
  const navigateWeek = (direction) => {
    const current = new Date(scheduleWeekStart);
    current.setDate(current.getDate() + (direction * 7));
    setScheduleWeekStart(current.toISOString().split('T')[0]);
  };

  // Load schedules and preferences when tab changes or week changes or campaign filter changes
  useEffect(() => {
    if (activeTab === "schedule") {
      loadSchedules();
      loadTeamPreferences();
      loadTeamMembers();  // Need team members for the shift modal
    }
  }, [activeTab, scheduleWeekStart, plannerCampaignFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter team members by campaign (memoized)
  const filteredTeamMembers = useMemo(() => 
    teamFilterCampaign === "all" 
      ? teamMembers 
      : teamMembers.filter(m => m.campaignIds?.includes(teamFilterCampaign)),
    [teamMembers, teamFilterCampaign]
  );

  // === TAB-TRIGGERED DATA LOADING (after all function defs to avoid TDZ) ===
  useEffect(() => {
    if (activeTab === "activity" || activeTab === "systemlog") loadEvents();
  }, [activeTab, loadEvents]);

  useEffect(() => {
    if (activeTab === "billing") { loadManualCharges(); loadLockStatus(); loadUsage(); }
    if (activeTab !== "billing") { setSelectedLockPlan(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "earnings") loadEarnings();
  }, [activeTab, loadEarnings]);

  useEffect(() => {
    if (activeTab === "cancellations") loadCancellations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "api-access") loadApiSettings();
    if (activeTab === "settings") loadBondSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "scheduling") loadSchedulingData();
    if (activeTab === "payroll") { loadPayrollPeriods(); loadTeamMembers(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load branding settings when settings tab is selected and campaign changes
  useEffect(() => {
    if (activeTab === "settings" && settingsCampaignId) {
      const camp = campaigns.find(c => c.id === settingsCampaignId);
      if (camp) {
        setBrandingSettings({
          logoUrl: camp.logoUrl || "",
          headerImageUrl: camp.headerImageUrl || "",
          primaryColor: camp.primaryColor || "#8b5cf6",
          buttonColor: camp.buttonColor || "#10b981",
          brandName: camp.brandName || "",
          tagline: camp.tagline || "",
          hideTapTrackBranding: camp.hideTapTrackBranding || false
        });
        setBrandingDirty(false);
        setLogoBroken(false);
        setHeaderBroken(false);
      }
    }
  }, [activeTab, settingsCampaignId, campaigns]);

  // Load API key on mount
  useEffect(() => {
    loadApiKey();
    loadLocations();
    loadOrgBranding();
  }, []);

  const loadApiKey = async () => {
    setLoadingApiKey(true);
    try {
      const res = await apiClient.get("/org/api-key");
      setApiKey(res.data);
    } catch (error) {
      // No key yet, that's fine
    } finally {
      setLoadingApiKey(false);
    }
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/uploads/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBrandingSettings(prev => ({ ...prev, logoUrl: res.data.url }));
      setBrandingDirty(true);
      setLogoBroken(false);
      toast.success("Logo uploaded — click Save Branding to keep it");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const uploadHeader = async (file) => {
    setUploadingHeader(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post("/uploads/header", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBrandingSettings(prev => ({ ...prev, headerImageUrl: res.data.url }));
      setBrandingDirty(true);
      setHeaderBroken(false);
      toast.success("Header image uploaded — click Save Branding to keep it");
    } catch (error) {
      toast.error("Failed to upload header");
    } finally {
      setUploadingHeader(false);
    }
  };

  const saveBranding = async () => {
    if (!settingsCampaignId) {
      toast.error("Please select a campaign");
      return;
    }
    setSavingBranding(true);
    try {
      await apiClient.put(`/campaigns/${settingsCampaignId}`, {
        logoUrl: brandingSettings.logoUrl || null,
        headerImageUrl: brandingSettings.headerImageUrl || null,
        primaryColor: brandingSettings.primaryColor || null,
        buttonColor: brandingSettings.buttonColor || null,
        brandName: brandingSettings.brandName || null,
        tagline: brandingSettings.tagline || null,
        hideTapTrackBranding: brandingSettings.hideTapTrackBranding
      });
      toast.success("Branding saved");
      const campaignsRes = await apiClient.get("/campaigns");
      setCampaigns(campaignsRes.data);
      // OCI V2.34 — reload persisted branding from backend so UI shows the saved truth.
      const saved = (campaignsRes.data || []).find(c => c.id === settingsCampaignId);
      if (saved) {
        setBrandingSettings({
          logoUrl: saved.logoUrl || "",
          headerImageUrl: saved.headerImageUrl || "",
          primaryColor: saved.primaryColor || "#8b5cf6",
          buttonColor: saved.buttonColor || "#10b981",
          brandName: saved.brandName || "",
          tagline: saved.tagline || "",
          hideTapTrackBranding: saved.hideTapTrackBranding || false
        });
      }
      setBrandingDirty(false);
    } catch (error) {
      toast.error("Failed to save branding");
    } finally {
      setSavingBranding(false);
    }
  };

  const sendGuidance = async () => {
    if (!guidanceMessage.trim()) {
      toast.error("Please enter a guidance message");
      return;
    }
    
    // Validate based on target type
    if (guidanceTargetType === "individual" && !selectedFundraiserForGuidance) {
      toast.error("Please select a fundraiser");
      return;
    }
    if (guidanceTargetType === "team" && !guidanceTargetTeam) {
      toast.error("Please select a team leader");
      return;
    }
    if (guidanceTargetType === "campaign" && !guidanceTargetCampaign) {
      toast.error("Please select a campaign");
      return;
    }

    setSendingGuidance(true);
    try {
      if (guidanceTargetType === "individual") {
        // Send to single fundraiser
        await apiClient.post("/guidance/send", {
          fundraiserId: selectedFundraiserForGuidance,
          message: guidanceMessage.trim()
        });
        toast.success("Guidance sent successfully");
        loadGuidanceHistory(selectedFundraiserForGuidance);
      } else {
        // Batch send
        const batchData = { message: guidanceMessage.trim() };
        if (guidanceTargetType === "team") {
          batchData.teamLeaderId = guidanceTargetTeam;
        } else if (guidanceTargetType === "campaign") {
          batchData.campaignId = guidanceTargetCampaign;
        } else if (guidanceTargetType === "all") {
          batchData.broadcast = true;
        }
        const res = await apiClient.post("/guidance/send-batch", batchData);
        toast.success(`Guidance sent to ${res.data.sentCount} fundraisers`);
      }
      setGuidanceMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send guidance");
    } finally {
      setSendingGuidance(false);
    }
  };

  const getSuggestedInsight = () => {
    if (!selectedFundraiserForGuidance) return null;
    const fundraiser = fundraisers.find(f => f.id === selectedFundraiserForGuidance);
    if (!fundraiser) return null;
    
    const insights = [];
    if (fundraiser.conversionRate < 0.3) {
      insights.push("Try building more rapport before asking for commitment. Spend extra time on initial conversation.");
    }
    if (fundraiser.cancellation30d > 0.2) {
      insights.push("Focus on commitment reinforcement during consolidation. Make sure all checklist items are completed.");
    }
    if (fundraiser.avgAge > 60) {
      insights.push("Consider targeting younger demographics for better long-term retention.");
    }
    
    return insights.length > 0 ? insights[0] : "Great work! Keep up the good performance.";
  };

  // Invite Fundraiser Function
  const inviteFundraiser = async () => {
    if (!inviteForm.firstName.trim() || !inviteForm.lastName.trim() || !inviteForm.email.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteForm.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Generate random temporary password
    const tempPassword = `TT${Math.random().toString(36).slice(2, 8)}${Math.floor(Math.random() * 100)}`;

    setInviting(true);
    try {
      await apiClient.post("/users/invite", {
        name: `${inviteForm.firstName.trim()} ${inviteForm.lastName.trim()}`,
        email: inviteForm.email.trim(),
        password: tempPassword,
        role: "fundraiser",
        campaignIds: inviteForm.campaignIds.length > 0 ? inviteForm.campaignIds : null,
        profileImageUrl: inviteForm.profileImageUrl || null
      });
      
      // Store credentials to display
      setInviteCredentials({
        email: inviteForm.email.trim(),
        password: tempPassword,
        name: `${inviteForm.firstName.trim()} ${inviteForm.lastName.trim()}`
      });
      
      // Clear form but keep modal open to show credentials
      setInviteForm({ firstName: "", lastName: "", email: "", campaignIds: [], profileImageUrl: "" });
      
      // Refresh data to show new fundraiser
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to invite fundraiser";
      toast.error(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  // Toggle campaign selection in invite form
  const toggleCampaignSelection = (campaignId) => {
    setInviteForm(prev => {
      const currentIds = prev.campaignIds || [];
      if (currentIds.includes(campaignId)) {
        return { ...prev, campaignIds: currentIds.filter(id => id !== campaignId) };
      } else {
        return { ...prev, campaignIds: [...currentIds, campaignId] };
      }
    });
  };

  // Open Fundraiser Detail Panel
  const openFundraiserDetail = (fundraiser) => {
    setSelectedFundraiserDetail(fundraiser);
    setShowDetailPanel(true);
  };

  // Compliance Functions
  const loadComplianceData = async (userId) => {
    setLoadingCompliance(true);
    try {
      const res = await apiClient.get(`/team/${userId}/compliance`);
      // Also fetch the user details for address/UTR/DBS
      const memberData = teamMembers.find(m => m.id === userId) || {};
      setComplianceData({
        dbsChecked: res.data.dbsChecked || false,
        dbsCheckDate: res.data.dbsCheckDate || "",
        dbsInitial: res.data.dbsInitial || "",
        idVerified: res.data.idVerified || false,
        idCheckDate: res.data.idCheckDate || "",
        idInitial: res.data.idInitial || "",
        referencesChecked: res.data.referencesChecked || false,
        referencesCheckDate: res.data.referencesCheckDate || "",
        referencesInitial: res.data.referencesInitial || "",
        notes: res.data.notes || "",
        address: memberData.address || "",
        utrNumber: memberData.utrNumber || "",
        dbsNumber: memberData.dbsNumber || "",
        utrDocumentUrl: memberData.utrDocumentUrl || "",
        dbsDocumentUrl: memberData.dbsDocumentUrl || "",
      });
    } catch (error) {
      const memberData = teamMembers.find(m => m.id === userId) || {};
      setComplianceData({
        dbsChecked: false, dbsCheckDate: "", dbsInitial: "",
        idVerified: false, idCheckDate: "", idInitial: "",
        referencesChecked: false, referencesCheckDate: "", referencesInitial: "",
        notes: "",
        address: memberData.address || "",
        utrNumber: memberData.utrNumber || "",
        dbsNumber: memberData.dbsNumber || "",
        utrDocumentUrl: memberData.utrDocumentUrl || "",
        dbsDocumentUrl: memberData.dbsDocumentUrl || "",
      });
    } finally {
      setLoadingCompliance(false);
    }
  };

  const openComplianceModal = (member) => {
    setComplianceFundraiserId(member.id);
    loadComplianceData(member.id);
    setShowComplianceModal(true);
  };

  // Send Password Reset Link to Fundraiser
  const [sendingPasswordReset, setSendingPasswordReset] = useState(null);
  
  const sendPasswordResetLink = async (email, name) => {
    if (!email) {
      toast.error("No email address for this user");
      return;
    }
    
    setSendingPasswordReset(email);
    try {
      await apiClient.post("/auth/request-reset", { email });
      toast.success(`Password reset link sent to ${name || email}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send reset link";
      toast.error(message);
    } finally {
      setSendingPasswordReset(null);
    }
  };

  const saveComplianceData = async () => {
    if (!complianceFundraiserId) return;
    setSavingCompliance(true);
    try {
      // Save compliance checks
      await apiClient.put(`/team/${complianceFundraiserId}/compliance`, complianceData);
      // Also save fundraiser details (address, UTR, DBS, docs)
      await apiClient.put(`/team/${complianceFundraiserId}/details`, {
        address: complianceData.address || null,
        utrNumber: complianceData.utrNumber || null,
        dbsNumber: complianceData.dbsNumber || null,
        utrDocumentUrl: complianceData.utrDocumentUrl || null,
        dbsDocumentUrl: complianceData.dbsDocumentUrl || null,
      });
      toast.success("Compliance data saved");
      setShowComplianceModal(false);
      loadTeamMembers(); // Refresh to update status indicators
    } catch (error) {
      toast.error("Failed to save compliance data");
    } finally {
      setSavingCompliance(false);
    }
  };

  const getComplianceStatus = (member) => {
    const c = member.compliance || {};
    const checks = [c.dbsChecked, c.idVerified, c.referencesChecked];
    const completed = checks.filter(Boolean).length;
    if (completed === 3) return { status: "complete", color: "bg-emerald-500", label: "Complete" };
    if (completed > 0) return { status: "partial", color: "bg-amber-500", label: "Partial" };
    return { status: "none", color: "bg-red-500", label: "None" };
  };

  // Performance Export Function
  const handlePerformanceExport = async () => {
    setExportingPerformance(true);
    try {
      const params = new URLSearchParams();
      if (performanceExportFilters.startDate) params.append("dateFrom", performanceExportFilters.startDate);
      if (performanceExportFilters.endDate) params.append("dateTo", performanceExportFilters.endDate);
      if (performanceExportFilters.campaignId) params.append("campaignId", performanceExportFilters.campaignId);
      if (performanceExportFilters.fundraiserIds.length > 0) {
        performanceExportFilters.fundraiserIds.forEach(id => params.append("fundraiserIds", id));
      }
      
      const res = await apiClient.get(`/exports/performance?${params.toString()}`);
      
      // Create CSV from data
      const data = res.data.data || [];
      if (data.length === 0) {
        toast.error("No data to export");
        return;
      }
      
      const headers = ["Fundraiser", "Lottery Single", "Lottery Double", "Regular Giving", "One-Off Total", "Total Conversions"];
      const rows = data.map(r => [
        r.fundraiserName,
        r.lotterySingle || 0,
        r.lotteryDouble || 0,
        r.regularGiving || 0,
        r.oneOffTotal || 0,
        r.totalConversions || 0
      ]);
      
      const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `performance_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Performance data exported");
      setShowPerformanceExportModal(false);
    } catch (error) {
      toast.error("Failed to export performance data");
    } finally {
      setExportingPerformance(false);
    }
  };

  // Load Team Hierarchy
  const loadTeamHierarchy = async () => {
    try {
      const res = await apiClient.get("/team/hierarchy");
      setTeamHierarchy(res.data);
      // Default all leaders to expanded
      const expanded = {};
      res.data.hierarchy?.forEach(leader => {
        expanded[leader.id] = true;
      });
      setExpandedLeaders(expanded);
    } catch (error) {
    }
  };

  // Approval toggle — updates ALL local sources of truth so the Switch + badge flip instantly,
  // regardless of whether the row was rendered from teamMembers, teamHierarchy.hierarchy[*].fundraisers,
  // or teamHierarchy.unassigned.
  const applyApprovalLocally = (userId, approvedVal) => {
    setTeamMembers(prev => prev.map(m => m.id === userId ? { ...m, approved: approvedVal } : m));
    setTeamHierarchy(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        hierarchy: (prev.hierarchy || []).map(leader => ({
          ...leader,
          fundraisers: (leader.fundraisers || []).map(fr => fr.id === userId ? { ...fr, approved: approvedVal } : fr)
        })),
        unassigned: (prev.unassigned || []).map(fr => fr.id === userId ? { ...fr, approved: approvedVal } : fr)
      };
    });
  };

  const toggleFundraiserApproval = async (userId, newVal) => {
    const prevVal = !newVal;
    applyApprovalLocally(userId, newVal);
    try {
      const res = await apiClient.put(`/team/${userId}/approved`, { approved: newVal });
      // Trust server response; if server disagrees, reconcile.
      if (typeof res?.data?.approved === "boolean" && res.data.approved !== newVal) {
        applyApprovalLocally(userId, res.data.approved);
      }
      toast.success(`Fundraiser ${newVal ? "approved" : "unapproved"}`);
    } catch (err) {
      applyApprovalLocally(userId, prevVal);
      toast.error("Failed to update approval");
    }
  };

  // Load Fundraiser Metrics
  const loadFundraiserMetrics = async (fundraiserId) => {
    if (fundraiserMetrics[fundraiserId]) return; // Already loaded
    setLoadingMetrics(true);
    try {
      const res = await apiClient.get(`/metrics/fundraiser/${fundraiserId}`);
      setFundraiserMetrics(prev => ({
        ...prev,
        [fundraiserId]: res.data
      }));
    } catch (error) {
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Load all team metrics at once
  const loadAllTeamMetrics = async () => {
    if (teamMembers.length === 0) return;
    setLoadingMetrics(true);
    try {
      const promises = teamMembers.map(async (m) => {
        try {
          const res = await apiClient.get(`/metrics/fundraiser/${m.id}`);
          return { id: m.id, metrics: res.data };
        } catch (e) {
          return { id: m.id, metrics: null };
        }
      });
      const results = await Promise.all(promises);
      const metricsMap = {};
      results.forEach(r => {
        if (r.metrics) metricsMap[r.id] = r.metrics;
      });
      setFundraiserMetrics(metricsMap);
    } catch (error) {
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Load Pricing Plans for Upgrade Modal
  const loadPricingPlans = async () => {
    try {
      const [plansRes, defaultsRes] = await Promise.all([
        apiClient.get("/platform/pricing-plans").catch(() => ({ data: [] })),
        apiClient.get("/platform/pricing-defaults").catch(() => ({ data: {} }))
      ]);
      setAvailablePlans(plansRes.data || []);
      if (defaultsRes.data) {
        setPricingDefaults(defaultsRes.data);
      }
    } catch (error) {
    }
  };

  // Update Team Role (fundraiser/team_leader)
  const updateTeamRole = async (userId, newRole) => {
    setUpdatingRole(userId);
    // Optimistic: apply role locally in both teamMembers and teamHierarchy so badges flip instantly.
    setTeamMembers(prev => prev.map(m => m.id === userId ? { ...m, teamRole: newRole } : m));
    setTeamHierarchy(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        hierarchy: (prev.hierarchy || []).map(leader => ({
          ...leader,
          fundraisers: (leader.fundraisers || []).map(fr => fr.id === userId ? { ...fr, teamRole: newRole } : fr)
        })),
        unassigned: (prev.unassigned || []).map(fr => fr.id === userId ? { ...fr, teamRole: newRole } : fr)
      };
    });
    try {
      await apiClient.put(`/team/${userId}/role?team_role=${newRole}`);
      toast.success(`Role updated to ${newRole.replace("_", " ")}`);
      // Refresh hierarchy to reflect changes (team_leader promotion may move the user into a leader node)
      loadTeamHierarchy();
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
      // Reload truth from server on failure
      loadTeamHierarchy();
      loadTeamMembers();
    } finally {
      setUpdatingRole(null);
    }
  };

  // Assign Fundraiser to Team Leader
  const assignToTeamLeader = async (userId, leaderId) => {
    setAssigningLeader(userId);
    try {
      if (leaderId === "none") {
        // Remove assignment
        await apiClient.put(`/team/${userId}/assign-leader?team_leader_id=`);
      } else {
        await apiClient.put(`/team/${userId}/assign-leader?team_leader_id=${leaderId}`);
      }
      toast.success(leaderId === "none" ? "Removed from team leader" : "Assigned to team leader");
      // Refresh hierarchy
      loadTeamHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign team leader");
    } finally {
      setAssigningLeader(null);
    }
  };

  // ===== Reporting-line assignment (Reports To) =====
  // Upward role rules (must mirror backend VALID_REPORTS_TO_ROLES)
  const REPORTS_TO_ROLES = {
    fundraiser: ["team_leader", "area_manager", "regional_manager", "national_manager", "campaign_manager"],
    team_leader: ["area_manager", "regional_manager", "national_manager", "campaign_manager"],
    area_manager: ["regional_manager", "national_manager", "campaign_manager"],
    regional_manager: ["national_manager", "campaign_manager"],
    national_manager: ["campaign_manager"],
    campaign_manager: [],
    client_viewer: []
  };
  const getEligibleManagers = (subRole) => {
    const allowed = REPORTS_TO_ROLES[subRole || "fundraiser"] || [];
    return teamMembers.filter(m => allowed.includes(m.teamRole));
  };
  const updateReportsTo = async (userId, managerId) => {
    // Optimistic
    const resolved = managerId === "none" ? null : managerId;
    setTeamMembers(prev => prev.map(m => m.id === userId ? { ...m, reportsToUserId: resolved } : m));
    setTeamHierarchy(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        hierarchy: (prev.hierarchy || []).map(l => ({
          ...l,
          fundraisers: (l.fundraisers || []).map(f => f.id === userId ? { ...f, reportsToUserId: resolved } : f)
        })),
        unassigned: (prev.unassigned || []).map(f => f.id === userId ? { ...f, reportsToUserId: resolved } : f)
      };
    });
    try {
      await apiClient.put(`/team/${userId}/reports-to`, { reportsToUserId: resolved });
      toast.success(resolved ? "Reporting line updated" : "Reporting line cleared");
      loadOrgTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update reporting line");
      loadTeamMembers();
      loadTeamHierarchy();
    }
  };

  // Org Tree state + loader
  const [orgTree, setOrgTree] = useState([]);
  const [loadingOrgTree, setLoadingOrgTree] = useState(false);
  const loadOrgTree = async () => {
    setLoadingOrgTree(true);
    try {
      const res = await apiClient.get("/team/org-tree");
      setOrgTree(res.data?.tree || []);
    } catch {
      setOrgTree([]);
    } finally {
      setLoadingOrgTree(false);
    }
  };

  // Bulk assign locations to multiple users
  const handleBulkLocationAssignment = async () => {
    if (selectedTeamMembers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }
    if (selectedLocationsForAssignment.length === 0) {
      toast.error("Please select at least one location");
      return;
    }
    
    setAssigningLocations(true);
    try {
      await Promise.all(selectedTeamMembers.map(userId => 
        apiClient.put(`/team/${userId}/locations`, { locationIds: selectedLocationsForAssignment })
      ));
      toast.success(`${selectedLocationsForAssignment.length} location(s) assigned to ${selectedTeamMembers.length} user(s)`);
      setSelectedTeamMembers([]);
      setSelectedLocationsForAssignment([]);
      loadTeamMembers();
    } catch (error) {
      toast.error("Failed to assign locations");
    } finally {
      setAssigningLocations(false);
    }
  };

  // Clear all location assignments for selected users
  const handleClearLocationAssignments = async () => {
    if (selectedTeamMembers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }
    
    setAssigningLocations(true);
    try {
      await Promise.all(selectedTeamMembers.map(userId => 
        apiClient.put(`/team/${userId}/locations`, { locationIds: [] })
      ));
      toast.success(`Location restrictions cleared for ${selectedTeamMembers.length} user(s) - they can now access all locations`);
      setSelectedTeamMembers([]);
      loadTeamMembers();
    } catch (error) {
      toast.error("Failed to clear location assignments");
    } finally {
      setAssigningLocations(false);
    }
  };

  // Toggle location selection for bulk assignment
  const toggleLocationSelection = (locationId) => {
    setSelectedLocationsForAssignment(prev => 
      prev.includes(locationId) 
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  // Get all team leaders from hierarchy
  const getTeamLeaders = () => {
    const leaders = [];
    
    // First, check teamHierarchy for team_leaders
    if (teamHierarchy) {
      teamHierarchy.hierarchy?.forEach(item => {
        if (item.teamRole === "team_leader") {
          leaders.push({ id: item.id, name: item.name });
        }
        // Also check teamLeaders array if it's a manager
        item.teamLeaders?.forEach(leader => {
          if (!leaders.find(l => l.id === leader.id)) {
            leaders.push({ id: leader.id, name: leader.name });
          }
        });
      });
    }
    
    // Also check all members for team_leader role
    teamMembers.forEach(m => {
      if (m.teamRole === "team_leader" && !leaders.find(l => l.id === m.id)) {
        leaders.push({ id: m.id, name: m.name });
      }
    });
    
    // If no team leaders found, show all fundraisers as selectable targets
    if (leaders.length === 0 && teamMembers.length > 0) {
      teamMembers.forEach(m => {
        if (!leaders.find(l => l.id === m.id)) {
          leaders.push({ id: m.id, name: m.name });
        }
      });
    }
    
    return leaders;
  };

  // Toggle leader expand/collapse
  const toggleLeaderExpand = (leaderId) => {
    setExpandedLeaders(prev => ({
      ...prev,
      [leaderId]: !prev[leaderId]
    }));
  };

  // Request Custom Quote
  const handleRequestCustomQuote = async () => {
    try {
      await apiClient.post("/org/request-custom-quote");
      toast.success("Custom quote request submitted. We'll be in touch soon!");
    } catch (error) {
      toast.error("Failed to submit quote request");
    }
  };

  const loadDeliveryConfig = async (campaignId) => {
    try {
      const res = await apiClient.get(`/delivery-configs/${campaignId}`);
      if (!res.data?.method) {
        setDeliveryConfig({ method: "email", emailAddress: "", webhookUrl: "", apiUrl: "", apiKey: "", active: true });
        return;
      }
      setDeliveryConfig({
        method: res.data.method,
        emailAddress: res.data.emailAddress || "",
        webhookUrl: res.data.webhookUrl || "",
        apiUrl: res.data.apiUrl || "",
        apiKey: "",
        active: res.data.active
      });
    } catch (error) {
      // No config exists, reset to defaults
      setDeliveryConfig({
        method: "email",
        emailAddress: "",
        webhookUrl: "",
        apiUrl: "",
        apiKey: "",
        active: true
      });
    }
  };

  const loadSignups = async () => {
    setLoadingSignups(true);
    try {
      const params = new URLSearchParams();
      // Use global campaign filter or per-tab filter
      const campaignToFilter = selectedCampaignFilter || signupFilters.campaignId;
      if (campaignToFilter && campaignToFilter !== "all") params.append("campaign_id", campaignToFilter);
      if (signupFilters.fundraiserId && signupFilters.fundraiserId !== "all") params.append("fundraiser_id", signupFilters.fundraiserId);
      if (signupFilters.teamLeaderId && signupFilters.teamLeaderId !== "all") params.append("team_leader_id", signupFilters.teamLeaderId);
      if (signupFilters.deliveryStatus && signupFilters.deliveryStatus !== "all") params.append("delivery_status", signupFilters.deliveryStatus);
      
      // Add date filtering - use filter state dates first, then global date range
      if (signupFilters.dateFrom) {
        params.append("date_from", signupFilters.dateFrom);
      } else {
        const { startDate } = getDateRange();
        if (startDate) params.append("date_from", startDate);
      }
      
      if (signupFilters.dateTo) {
        params.append("date_to", signupFilters.dateTo);
      } else {
        const { endDate } = getDateRange();
        if (endDate && dateFilter !== "all") params.append("date_to", endDate);
      }
      
      const res = await apiClient.get(`/signups?${params.toString()}`);
      setSignups(res.data.signups || []);
      setSignupSummary(res.data.summary || {});
    } catch (error) {
      console.error("Failed to load signups", error);
      setSignups([]);
      setSignupSummary({});
    } finally {
      setLoadingSignups(false);
    }
  };

  const saveDeliveryConfig = async () => {
    if (!selectedCampaign) {
      toast.error("Please select a campaign");
      return;
    }

    setSavingConfig(true);
    try {
      await apiClient.post("/delivery-configs", {
        campaignId: selectedCampaign,
        method: deliveryConfig.method,
        emailAddress: deliveryConfig.emailAddress || null,
        webhookUrl: deliveryConfig.webhookUrl || null,
        apiUrl: deliveryConfig.apiUrl || null,
        apiKey: deliveryConfig.apiKey || null,
        active: deliveryConfig.active
      });
      toast.success("Delivery settings saved");
      
      // Reload configs
      const configsRes = await apiClient.get("/delivery-configs");
      setDeliveryConfigs(configsRes.data);
    } catch (error) {
      toast.error("Failed to save delivery settings");
    } finally {
      setSavingConfig(false);
    }
  };

  const retryDelivery = async (conversionId) => {
    setRetryingId(conversionId);
    try {
      const res = await apiClient.post(`/delivery/retry/${conversionId}`);
      toast.success(`Delivery ${res.data.deliveryStatus}: ${res.data.deliveryResponse || ''}`);
      loadSignups();
    } catch (error) {
      toast.error("Failed to retry delivery");
    } finally {
      setRetryingId(null);
    }
  };

  const viewTimeline = async (conversionId) => {
    setLoadingTimeline(true);
    setShowTimelineModal(true);
    setTimelineData(null);
    try {
      const res = await apiClient.get(`/events/timeline/${conversionId}`);
      setTimelineData(res.data);
    } catch (error) {
      toast.error("Failed to load timeline");
      setShowTimelineModal(false);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const handleRetentionUpload = async () => {
    if (!retentionFile) {
      toast.error("Please select a CSV file");
      return;
    }

    setUploadingRetention(true);
    setRetentionResult(null);

    try {
      const formData = new FormData();
      formData.append("file", retentionFile);

      const res = await apiClient.post("/retention/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      setRetentionResult(res.data);
      toast.success(`Processed ${res.data.processed} rows, ${res.data.updated} updated`);
      
      // Refresh metrics to reflect changes
      const metricsRes = await apiClient.get("/metrics/organisation");
      setMetrics(metricsRes.data);
      
      // Clear file input
      setRetentionFile(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to upload CSV";
      toast.error(errorMsg);
      setRetentionResult({ error: errorMsg });
    } finally {
      setUploadingRetention(false);
    }
  };

  const handleUpgrade = async (planId) => {
    // Find the selected plan details
    const selectedPlan = availablePlans.find(p => p.id === planId || p.name.toLowerCase() === planId?.toLowerCase());
    
    // If plan has £0 price, skip Stripe and assign directly
    if (selectedPlan && selectedPlan.monthlyPrice === 0) {
      try {
        await apiClient.post("/org/assign-free-plan", { planId: selectedPlan.id || planId });
        toast.success(`Successfully switched to ${selectedPlan.name} plan!`);
        setShowUpgradeModal(false);
        // Refresh org status
        const orgStatusRes = await apiClient.get("/organisation/status");
        setOrgStatus(orgStatusRes.data);
        return;
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to assign plan");
        return;
      }
    }
    
    // For paid plans, use Stripe checkout
    try {
      const res = await apiClient.post("/payments/checkout", {
        packageId: planId || (orgStatus?.plan === "basic" ? "pro" : "enterprise"),
        originUrl: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error("Failed to initiate upgrade");
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCampaignFilter) {
        params.append("campaign_id", selectedCampaignFilter);
      }
      if (signupFilters.fundraiserId) {
        params.append("fundraiser_id", signupFilters.fundraiserId);
      }
      
      // Add date filtering
      const { startDate, endDate } = getDateRange();
      if (startDate) params.append("start_date", startDate);
      if (endDate && dateFilter !== "all") params.append("end_date", endDate);
      
      const response = await apiClient.get(`/export/conversions?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `engage_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  // Export selected signups
  const handleExportSelectedSignups = async () => {
    if (selectedSignups.length === 0) {
      toast.error("Please select at least one signup to export");
      return;
    }
    
    try {
      // Build CSV content from selected signups
      const headers = ["Date", "Fundraiser", "Campaign", "Type", "Amount", "Tracking ID", "Delivery Status", "First Name", "Last Name", "Email", "Phone"];
      const rows = signups
        .filter(s => selectedSignups.includes(s.id))
        .map(s => [
          new Date(s.date).toLocaleDateString(),
          s.fundraiserName,
          s.campaignName,
          s.conversionType?.replace("_", " "),
          s.amount || "",
          s.trackingId,
          s.deliveryStatus,
          s.firstName || "",
          s.lastName || "",
          s.email || "",
          s.phone || ""
        ]);
      
      const csvContent = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `engage_selected_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedSignups.length} signups`);
    } catch (error) {
      toast.error("Failed to export signups");
    }
  };

  // Period-based signup export (Today / Week / Month / Custom) — CSV
  const exportSignupsByPeriod = (period = "week") => {
    try {
      const today = new Date();
      let from = null, to = null, label = "";
      if (period === "today") {
        const d = today.toISOString().slice(0,10); from = d; to = d; label = "today";
      } else if (period === "week") {
        const day = today.getDay(); const mondayOffset = (day === 0 ? 6 : day - 1);
        const mon = new Date(today); mon.setDate(today.getDate() - mondayOffset);
        from = mon.toISOString().slice(0,10); to = today.toISOString().slice(0,10); label = "week";
      } else if (period === "month") {
        from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0,10);
        to = today.toISOString().slice(0,10); label = "month";
      } else if (period === "custom") {
        from = signupFilters.dateFrom || null;
        to = signupFilters.dateTo || null;
        label = "custom";
        if (!from || !to) { toast.error("Set custom From/To dates first"); return; }
      }
      const rowsInRange = signups.filter(s => {
        if (!s.date) return false;
        const d = (typeof s.date === "string" ? s.date : new Date(s.date).toISOString()).slice(0,10);
        return (!from || d >= from) && (!to || d <= to);
      });
      if (rowsInRange.length === 0) { toast.error(`No signups in ${label} range`); return; }
      const headers = ["Date","Fundraiser","Campaign","Type","Amount","Payment Date","Tracking ID","Delivery Status","First Name","Last Name","Email","Phone"];
      const rows = rowsInRange.map(s => [
        new Date(s.date).toLocaleString(),
        s.fundraiserName || "", s.campaignName || "",
        (s.conversionType || "").replace("_"," "),
        s.amount ?? "",
        s.paymentDatePreference || "",
        s.trackingId || "", s.deliveryStatus || "",
        s.firstName || "", s.lastName || "", s.email || "", s.phone || ""
      ]);
      const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `engage_signups_${label}_${today.toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Exported ${rowsInRange.length} signup${rowsInRange.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error("Failed to export"); }
  };

  // Printable summary of currently-loaded signups
  const printSignupsSummary = () => {
    const byType = signups.reduce((a, s) => {
      const k = (s.conversionType || "unknown").replace("_"," ");
      a[k] = (a[k] || 0) + 1; return a;
    }, {});
    const total = signups.length;
    const revenue = signups.reduce((a, s) => a + (Number(s.amount) || 0), 0);
    const win = window.open("", "_blank");
    if (!win) { toast.error("Pop-up blocked — allow pop-ups to print summary"); return; }
    const html = `<!doctype html><html><head><title>Signups Summary</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#0f172a} h1{font-size:20px}
      table{width:100%;border-collapse:collapse;margin-top:12px} th,td{border:1px solid #e2e8f0;padding:6px 8px;font-size:12px;text-align:left}
      th{background:#f1f5f9}</style></head><body>
      <h1>Engage — Signups Summary</h1>
      <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
      <p><strong>Total signups:</strong> ${total} · <strong>Est. revenue:</strong> £${revenue.toFixed(2)}</p>
      <h3>By type</h3>
      <table><thead><tr><th>Type</th><th>Count</th></tr></thead><tbody>
      ${Object.entries(byType).map(([k,v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join("")}
      </tbody></table>
      <h3>Recent rows</h3>
      <table><thead><tr><th>Date</th><th>Fundraiser</th><th>Campaign</th><th>Type</th><th>Amount</th></tr></thead><tbody>
      ${signups.slice(0,50).map(s => `<tr><td>${new Date(s.date).toLocaleString()}</td><td>${s.fundraiserName||""}</td><td>${s.campaignName||""}</td><td>${(s.conversionType||"").replace("_"," ")}</td><td>£${s.amount ?? "-"}</td></tr>`).join("")}
      </tbody></table>
      <script>window.onload=()=>window.print()</script></body></html>`;
    win.document.write(html); win.document.close();
  };

  // Toggle signup selection
  const toggleSignupSelection = (signupId) => {
    setSelectedSignups(prev => 
      prev.includes(signupId) 
        ? prev.filter(id => id !== signupId) 
        : [...prev, signupId]
    );
  };

  // View signup detail
  const viewSignupDetail = (signup) => {
    setSelectedSignupDetail(signup);
    setShowSignupDetailModal(true);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  const summaryStats = [
    { 
      label: "Total Signups", 
      value: metrics?.totalSignups || 0,
      icon: Users,
      color: "text-emerald-600"
    },
    { 
      label: "Active", 
      value: metrics?.activeCount || 0,
      icon: UserCheck,
      color: "text-blue-600"
    },
    { 
      label: "Cancelled", 
      value: metrics?.cancelledCount || 0,
      icon: UserX,
      color: "text-red-600"
    },
    { 
      label: "Avg Conversion", 
      value: `${((metrics?.avgConversionRate || 0) * 100).toFixed(0)}%`,
      icon: TrendingUp,
      color: "text-violet-600"
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: "bg-emerald-100 text-emerald-700",
      failed: "bg-red-100 text-red-700",
      pending: "bg-amber-100 text-amber-700"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="owner-dashboard">
      {/* Header - Mobile Responsive */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          {/* Desktop: Single Row / Mobile: Stacked Rows */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Row 1: Logo + Campaign Filter */}
            <div className="flex items-center justify-between lg:justify-start gap-4">
              <TapTrackLogo size="default" />
              {/* Campaign Filter - full width on mobile */}
              <div className="flex items-center gap-2 flex-1 lg:flex-none lg:ml-4">
                <Building2 className="h-4 w-4 text-slate-400 hidden sm:block" />
                <Select 
                  value={selectedCampaignFilter || "all"} 
                  onValueChange={(v) => setSelectedCampaignFilter(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="w-full lg:w-[180px] h-9" data-testid="campaign-filter-select">
                    <SelectValue placeholder="All Campaigns" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Campaigns</SelectItem>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 2: Date Filter + Export CSV */}
            <div className="flex items-center gap-2 flex-wrap">
              <CalendarDays className="h-4 w-4 text-slate-400 hidden sm:block" />
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[130px] sm:w-[150px] h-9" data-testid="date-filter-select">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last5">Last 5 Days</SelectItem>
                  <SelectItem value="last7">Last 7 Days</SelectItem>
                  <SelectItem value="last14">Last 14 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
              {dateFilter === "custom" && (
                <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="h-9 flex-1 sm:w-[120px]"
                    data-testid="start-date-input"
                  />
                  <span className="text-slate-400">-</span>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="h-9 flex-1 sm:w-[120px]"
                    data-testid="end-date-input"
                  />
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="h-9 px-3 border-slate-200 ml-auto sm:ml-0"
                data-testid="export-csv-btn"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>
            
            {/* Row 3: Training + Logout */}
            <div className="flex items-center gap-2 justify-end">
              <ScopeIndicator />
              <DualViewSwitcher />
              <ContextSwitcher compact />
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/training")}
                className="h-9 px-3 border-slate-200"
                data-testid="training-btn"
              >
                <BookOpen className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Training</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/operational-record")}
                className="h-9 px-3 border-emerald-200 text-emerald-700"
                data-testid="op-record-entry-btn"
              >
                <Activity className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Operational Record</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="h-9 px-3 text-slate-500"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* ─── OCI V2.5 Navigation Rationalisation ───
              7 top-level groups + contextual sub-pills.
              Legacy TabsTrigger elements are kept (hidden) for back-compat. */}
          <V25TopNav activeTab={activeTab} setActiveTab={setActiveTab} user={user} />

          {/* LEGACY TABSLISTS — kept hidden to preserve all references */}
          <TabsList className="hidden" data-testid="owner-tabs-list">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">Analytics</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Campaigns & Locations</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
            <TabsTrigger value="workforce" data-testid="tab-workforce">Workforce & Compliance</TabsTrigger>
            <TabsTrigger value="donor-hub" data-testid="tab-donor-hub">Donor Hub</TabsTrigger>
            {isOwnerTier && (
              <TabsTrigger value="trustee" data-testid="tab-trustee">Trustee</TabsTrigger>
            )}
            <TabsTrigger value="delivery-centre" data-testid="tab-delivery-centre">Delivery Centre</TabsTrigger>
            {user?.role !== "manager" && (
              <TabsTrigger value="users" data-testid="tab-users">Accounts</TabsTrigger>
            )}
            {(user?.role !== "manager" || user?.managerLevel === "elevated") && (
              <TabsTrigger value="guidance" data-testid="tab-guidance">Messages</TabsTrigger>
            )}
            <TabsTrigger value="schedule" data-testid="tab-schedule">Shift Planner</TabsTrigger>
            <TabsTrigger value="scheduling" data-testid="tab-scheduling">Assignments</TabsTrigger>
            <TabsTrigger value="signups" data-testid="tab-signups">Sign-Ups</TabsTrigger>
            <TabsTrigger value="flagged" data-testid="tab-flagged" onClick={() => fetchFlaggedSubmissions()}>Flagged</TabsTrigger>
            <TabsTrigger value="cancellations" data-testid="tab-cancellations">Cancellations</TabsTrigger>
            <TabsTrigger value="gift-aid" data-testid="tab-gift-aid">Gift Aid</TabsTrigger>
          </TabsList>
          <TabsList className="hidden">
            {(user?.role !== "manager" || user?.managerLevel === "elevated") && (
              <TabsTrigger value="systemlog" data-testid="tab-systemlog">System Log</TabsTrigger>
            )}
            <TabsTrigger value="activity-locations" data-testid="tab-activity-locations" onClick={() => { if (!activityLocationsData) loadActivityLocations(); }}>Heat Map</TabsTrigger>
            {user?.role !== "manager" && (
              <>
                <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
                <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
                <TabsTrigger value="charity-billing" data-testid="tab-charity-billing">Charity Billing</TabsTrigger>
                <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
              </>
            )}
            <TabsTrigger value="delivery" data-testid="tab-delivery">Signup Delivery</TabsTrigger>
            {user?.role !== "manager" && (
              <>
                <TabsTrigger value="landing" data-testid="tab-landing">Public Page</TabsTrigger>
                <TabsTrigger value="api-access" data-testid="tab-api-access">API</TabsTrigger>
              </>
            )}
            {(user?.role !== "manager" || user?.managerLevel === "elevated") && (
              <TabsTrigger value="activity" data-testid="tab-activity">Audit Trail</TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          {/* Overview Tab — 5-row command centre (V2) */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* OCI V2.18b — Operations Overview (Now → Next → So far → Actions) for
                ALL org-dashboard roles (owner + campaign/regional managers). Heat map
                lifted to top as a first-class surface. Fundraiser home stays separate. */}
            <ManagerOperationsHome
              onNavigate={(tab) => setActiveTab(tab)}
              onOpenFundraiser={(fid) => navigate(`/owner/fundraiser/${fid}`)}
              readOnly={user?.role === "manager"}
            />
          </TabsContent>

          {/* Gift Aid Tab (OCI V2) */}
          <TabsContent value="gift-aid" className="space-y-6 mt-6">
            <GiftAidPanel scope="organisation" />
          </TabsContent>

          {/* Insights Tab — deep intelligence layer */}
          <TabsContent value="insights" className="space-y-6 mt-6">
            <OwnerInsightsTab
              onNavigate={(tab) => setActiveTab(tab)}
              onOpenFundraiser={(fid) => navigate(`/owner/fundraiser/${fid}`)}
              readOnly={user?.role === "manager"}
            />
          </TabsContent>

          {/* Workforce Tab — Staff Profiles · Compliance · Alerts · V2.3 Ops Centre */}
          <TabsContent value="workforce" className="space-y-6 mt-6">
            <WorkforceOperationsCentre />
            <ReferralQueue />
            <WorkforceTab isOwner={user?.role === "owner" || user?.role === "platform_owner"} />
          </TabsContent>

          {/* Donor Hub Tab — V2.3 */}
          <TabsContent value="donor-hub" className="space-y-6 mt-6">
            <DonorHubPanel />
          </TabsContent>

          {/* Trustee Dashboard Tab — V2.3 */}
          <TabsContent value="trustee" className="space-y-6 mt-6">
            <TrusteeDashboardSection />
          </TabsContent>

          {/* Delivery Centre Tab — V2.4 */}
          <TabsContent value="delivery-centre" className="space-y-6 mt-6">
            <DeliveryCentrePanel />
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6 mt-6">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UserCog className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold text-lg text-slate-900">Team Management</h2>
                      <p className="text-sm text-slate-500">Manage fundraisers, view performance, and assign leaders</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="invite-fundraiser-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite Fundraiser
                  </Button>
                </div>

                {/* ORG TREE (Reports-To hierarchy) */}
                <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50/40 p-4" data-testid="org-tree-card">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900">Reporting Structure</h3>
                      <p className="text-xs text-slate-500">National › Regional › Area › Team Leader › Fundraiser</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={loadOrgTree} data-testid="refresh-org-tree-btn">
                      Refresh
                    </Button>
                  </div>
                  {loadingOrgTree ? (
                    <div className="text-sm text-slate-500 py-6 text-center">Loading tree…</div>
                  ) : orgTree.length === 0 ? (
                    <div className="text-sm text-slate-500 py-6 text-center" data-testid="org-tree-empty">No team members yet.</div>
                  ) : (
                    <div className="space-y-0.5">
                      <OrgTreeNode roots={orgTree} />
                    </div>
                  )}
                </div>

                {/* Filters Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <Label className="text-slate-700 font-medium text-sm">Filter by Campaign</Label>
                    <Select value={teamFilterCampaign} onValueChange={setTeamFilterCampaign}>
                      <SelectTrigger className="h-10 mt-1.5" data-testid="team-filter-campaign">
                        <SelectValue placeholder="All Campaigns" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Campaigns</SelectItem>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 font-medium text-sm">Filter by Team Leader</Label>
                    <Select value={teamLeaderFilter} onValueChange={setTeamLeaderFilter}>
                      <SelectTrigger className="h-10 mt-1.5" data-testid="team-leader-filter">
                        <SelectValue placeholder="All Leaders" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Team Members</SelectItem>
                        <SelectItem value="unassigned">Unassigned Only</SelectItem>
                        {getTeamLeaders().map((leader) => (
                          <SelectItem key={leader.id} value={leader.id}>{leader.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      onClick={loadAllTeamMetrics}
                      disabled={loadingMetrics}
                      className="h-10"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loadingMetrics ? 'animate-spin' : ''}`} />
                      Refresh Metrics
                    </Button>
                  </div>
                </div>

                {/* Location Assignment Bar - ALWAYS VISIBLE */}
                <div className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-700">
                        Assign Locations:
                      </span>
                      
                      {/* Location Multi-Select Dropdown */}
                      <div className="relative">
                        <button
                          className="h-9 px-3 border border-slate-200 rounded-lg bg-white flex items-center gap-2 text-sm hover:bg-slate-50"
                          onClick={() => document.getElementById('location-multiselect-dropdown-main')?.classList.toggle('hidden')}
                          data-testid="location-multiselect-trigger-main"
                        >
                          <MapPin className="h-4 w-4 text-slate-500" />
                          {selectedLocationsForAssignment.length === 0 
                            ? "Select Locations..." 
                            : `${selectedLocationsForAssignment.length} selected`
                          }
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        </button>
                        <div 
                          id="location-multiselect-dropdown-main"
                          className="hidden absolute top-full left-0 mt-1 w-64 max-h-60 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg z-50"
                        >
                          <div className="p-2 border-b border-slate-100">
                            <span className="text-xs font-medium text-slate-500 uppercase">Select Locations</span>
                          </div>
                          {locations.map(loc => (
                            <label 
                              key={loc.id}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedLocationsForAssignment.includes(loc.id)}
                                onChange={() => toggleLocationSelection(loc.id)}
                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">{loc.name}</span>
                            </label>
                          ))}
                          {locations.length === 0 && (
                            <div className="px-3 py-4 text-sm text-slate-500 text-center">
                              No locations available
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleBulkLocationAssignment}
                        disabled={assigningLocations || selectedTeamMembers.length === 0 || selectedLocationsForAssignment.length === 0}
                        className="h-9 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        data-testid="assign-locations-btn-main"
                      >
                        {assigningLocations ? "Assigning..." : "Assign to Selected"}
                      </Button>
                      
                      <Button
                        onClick={handleClearLocationAssignments}
                        disabled={assigningLocations || selectedTeamMembers.length === 0}
                        variant="outline"
                        className="h-9 text-slate-600 disabled:opacity-50"
                        data-testid="clear-locations-btn-main"
                      >
                        Clear Restrictions
                      </Button>
                    </div>
                    
                    {/* Selection Status */}
                    <div className="text-sm text-slate-500">
                      {selectedTeamMembers.length === 0 ? (
                        <span className="text-amber-600">Select team members to assign locations</span>
                      ) : (
                        <span className="text-blue-600 font-medium">{selectedTeamMembers.length} user(s) selected</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bulk Actions Bar - Shows when users selected */}
                {selectedTeamMembers.length > 0 && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-blue-900">
                        {selectedTeamMembers.length} user{selectedTeamMembers.length !== 1 ? 's' : ''} selected
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedTeamMembers([]);
                          setSelectedLocationsForAssignment([]);
                        }}
                        className="text-slate-500"
                      >
                        Clear Selection
                      </Button>
                    </div>
                    
                    <div className="flex flex-wrap gap-3">
                      {/* Campaign Assignment */}
                      <Select onValueChange={(campaignId) => assignSelectedToCampaign(campaignId)}>
                        <SelectTrigger className="h-9 w-48" data-testid="bulk-assign-campaign">
                          <SelectValue placeholder="Assign Campaign..." />
                        </SelectTrigger>
                        <SelectContent>
                          {campaigns.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Team Leader Assignment */}
                      <Select onValueChange={(leaderId) => {
                        selectedTeamMembers.forEach(userId => {
                          assignToTeamLeader(userId, leaderId);
                        });
                        setSelectedTeamMembers([]);
                      }}>
                        <SelectTrigger className="h-9 w-48" data-testid="bulk-assign-leader">
                          <SelectValue placeholder="Assign Leader..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {getTeamLeaders().map((l) => (
                            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Team Hierarchy View */}
                {loadingTeam ? (
                  <div className="text-center py-8 text-slate-500">Loading team...</div>
                ) : filteredTeamMembers.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No team members found. Invite fundraisers to get started.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Team Leaders with their fundraisers */}
                    {teamLeaderFilter !== "unassigned" && teamHierarchy?.hierarchy?.filter(leader => 
                      teamLeaderFilter === "all" || leader.id === teamLeaderFilter
                    ).map((leader) => {
                      const isExpanded = expandedLeaders[leader.id] !== false;
                      const leaderMetrics = fundraiserMetrics[leader.id] || {};
                      const teamMetrics = {
                        totalSignups: 0,
                        totalConversions: 0,
                        totalValue: 0
                      };
                      
                      // Aggregate team metrics
                      leader.fundraisers?.forEach(f => {
                        const m = fundraiserMetrics[f.id] || {};
                        teamMetrics.totalSignups += m.totalConversions || 0;
                        teamMetrics.totalConversions += m.totalConversions || 0;
                      });
                      
                      return (
                        <div key={leader.id} className="border rounded-xl overflow-hidden">
                          {/* Team Leader Header */}
                          <div 
                            className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 cursor-pointer"
                            onClick={() => toggleLeaderExpand(leader.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                                  {leader.name?.charAt(0) || "L"}
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                                    {leader.name}
                                    <span className="text-xs px-2 py-0.5 bg-blue-200 text-blue-800 rounded-full">Team Leader</span>
                                  </div>
                                  <div className="text-sm text-slate-500">
                                    {leader.fundraisers?.length || 0} fundraisers • Team Total: {teamMetrics.totalSignups} signups
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right hidden md:block">
                                  <div className="text-sm text-slate-500">Team Conversions</div>
                                  <div className="font-bold text-lg text-blue-600">{teamMetrics.totalConversions}</div>
                                </div>
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-slate-400" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-slate-400" />
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Fundraisers under this leader */}
                          {isExpanded && leader.fundraisers?.length > 0 && (
                            <div className="divide-y divide-slate-100">
                              {leader.fundraisers.filter(f => 
                                teamFilterCampaign === "all" || f.campaignIds?.includes(teamFilterCampaign)
                              ).map((f) => {
                                const metrics = fundraiserMetrics[f.id] || {};
                                const compStatus = getComplianceStatus(f);
                                const availableLeaders = getTeamLeaders();
                                return (
                                  <div key={f.id} className="p-4 pl-12 bg-white hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                      <div className="flex items-center gap-3">
                                        <input
                                          type="checkbox"
                                          checked={selectedTeamMembers.includes(f.id)}
                                          onChange={() => toggleTeamMemberSelection(f.id)}
                                          className="h-4 w-4 rounded border-slate-300"
                                        />
                                        <div>
                                          <div className="font-medium text-slate-900">{f.name}</div>
                                          <div className="text-xs text-slate-500">{f.email}</div>
                                        </div>
                                      </div>
                                      
                                      {/* Performance Metrics */}
                                      <div className="flex items-center gap-4 flex-wrap">
                                        {/* Role Selector */}
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-slate-500">Role:</span>
                                          <Select
                                            value={f.teamRole || "fundraiser"}
                                            onValueChange={(val) => updateTeamRole(f.id, val)}
                                            disabled={updatingRole === f.id}
                                          >
                                            <SelectTrigger className="h-8 w-40 text-xs" data-testid={`role-select-${f.id}`}>
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="fundraiser">Fundraiser</SelectItem>
                                              <SelectItem value="team_leader">Team Leader</SelectItem>
                                              <SelectItem value="area_manager">Area Manager</SelectItem>
                                              <SelectItem value="regional_manager">Regional Manager</SelectItem>
                                              <SelectItem value="national_manager">National Manager</SelectItem>
                                              <SelectItem value="campaign_manager">Campaign Manager</SelectItem>
                                              <SelectItem value="client_viewer">Client Viewer</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>

                                        {/* Reports To (reporting-line hierarchy) */}
                                        {((REPORTS_TO_ROLES[f.teamRole || "fundraiser"] || []).length > 0) && (
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-xs text-slate-500">Reports to:</span>
                                            <Select
                                              value={f.reportsToUserId || "none"}
                                              onValueChange={(val) => updateReportsTo(f.id, val)}
                                            >
                                              <SelectTrigger className="h-8 w-40 text-xs" data-testid={`reports-to-${f.id}`}>
                                                <SelectValue placeholder="— Unassigned —" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">— Unassigned —</SelectItem>
                                                {getEligibleManagers(f.teamRole).map(mgr => (
                                                  <SelectItem key={mgr.id} value={mgr.id}>
                                                    {mgr.name} · {(mgr.teamRole || "").replace(/_/g, " ")}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}

                                        {/* Set Password (owner manual reset) */}
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-8 text-xs gap-1"
                                          onClick={() => {
                                            setSetPasswordTarget({ id: f.id, name: f.name, email: f.email });
                                            setNewPasswordForm({ password: "", confirm: "" });
                                            setShowSetPasswordModal(true);
                                          }}
                                          data-testid={`team-set-password-${f.id}`}
                                        >
                                          <Lock className="h-3 w-3" />
                                          Set password
                                        </Button>
                                        
                                        {/* Team Leader Assignment */}
                                        {(f.teamRole === "fundraiser" || !f.teamRole) && availableLeaders.length > 0 && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-slate-500">Assign to:</span>
                                            <Select
                                              value={f.assignedLeaderId || "none"}
                                              onValueChange={(val) => assignToTeamLeader(f.id, val)}
                                              disabled={assigningLeader === f.id}
                                            >
                                              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`assign-leader-${f.id}`}>
                                                <SelectValue placeholder="Select leader" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">Unassigned</SelectItem>
                                                {availableLeaders.map(l => (
                                                  <SelectItem key={l.id} value={l.id}>
                                                    {l.name}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {/* Location Assignment Display (read-only, edit via bulk action) */}
                                        <div className="flex items-center gap-2 text-xs">
                                          <MapPin className="h-3 w-3 text-slate-400" />
                                          <span className={`${(f.locationIds || []).length === 0 ? 'text-slate-400' : 'text-blue-600 font-medium'}`}>
                                            {(f.locationIds || []).length === 0 
                                              ? "All locations" 
                                              : `${(f.locationIds || []).length} location${(f.locationIds || []).length !== 1 ? 's' : ''}`
                                            }
                                          </span>
                                        </div>
                                        
                                        <div className="hidden lg:grid grid-cols-5 gap-4 text-center">
                                          <div>
                                            <div className="text-xs text-slate-500">Total</div>
                                            <div className="font-semibold text-slate-900">{metrics.totalConversions || 0}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-slate-500">Lottery</div>
                                            <div className="font-semibold text-violet-600">
                                              {(metrics.todayLotterySingle || 0) + ((metrics.todayLotteryDouble || 0) * 2)}
                                            </div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-slate-500">Regular</div>
                                            <div className="font-semibold text-emerald-600">{metrics.todayRegularCount || 0}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-slate-500">One-Off</div>
                                            <div className="font-semibold text-amber-600">{metrics.todayOneOffCount || 0}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-slate-500">Rate</div>
                                            <div className="font-semibold text-blue-600">{metrics.conversionRate || 0}%</div>
                                          </div>
                                        </div>
                                        
                                        {/* Compliance Status */}
                                        <button
                                          onClick={() => openComplianceModal(f)}
                                          className="flex items-center gap-1"
                                          title="Compliance"
                                        >
                                          <span className={`w-2.5 h-2.5 rounded-full ${compStatus.color}`}></span>
                                        </button>
                                        
                                        {/* Approval Toggle */}
                                        <div className="flex items-center gap-1.5" title={f.approved ? "Approved" : "Not Approved"}>
                                          <Switch
                                            checked={f.approved || false}
                                            onCheckedChange={(val) => toggleFundraiserApproval(f.id, val)}
                                            data-testid={`approval-toggle-${f.id}`}
                                          />
                                          <span className={`text-xs font-medium ${f.approved ? 'text-emerald-600' : 'text-red-500'}`}>
                                            {f.approved ? "Approved" : "Pending"}
                                          </span>
                                        </div>
                                        
                                        {/* Last Sync */}
                                        <div className="text-xs text-slate-400 w-20">
                                          {f.lastSyncAt ? new Date(f.lastSyncAt).toLocaleTimeString() : "Never"}
                                        </div>
                                        
                                        {/* Actions */}
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => sendPasswordResetLink(f.email, f.name)}
                                            disabled={sendingPasswordReset === f.email}
                                            title="Send password reset"
                                            data-testid={`send-reset-${f.id}`}
                                          >
                                            <Lock className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              setSelectedFundraiserForGuidance(f.id);
                                              setActiveTab("guidance");
                                            }}
                                            title="Send Guidance"
                                          >
                                            <MessageCircle className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => { setPerfFundraiserId(f.id); setShowFundraiserPerf(true); }}
                                            data-testid={`view-team-member-${f.id}`}
                                          >
                                            View
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Unassigned Fundraisers */}
                    {teamHierarchy?.unassigned?.length > 0 && (teamLeaderFilter === "all" || teamLeaderFilter === "unassigned") && (
                      <div className="border rounded-xl overflow-hidden">
                        <div className="bg-slate-100 p-4">
                          <div className="font-semibold text-slate-700 flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Unassigned Fundraisers
                            <span className="text-xs px-2 py-0.5 bg-slate-300 text-slate-700 rounded-full">
                              {teamHierarchy.unassigned.length}
                            </span>
                          </div>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {teamHierarchy.unassigned.filter(f => 
                            teamFilterCampaign === "all" || f.campaignIds?.includes(teamFilterCampaign)
                          ).map((f) => {
                            const metrics = fundraiserMetrics[f.id] || {};
                            const compStatus = getComplianceStatus(f);
                            const availableLeaders = getTeamLeaders();
                            return (
                              <div key={f.id} className="p-4 bg-white hover:bg-slate-50 transition-colors">
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedTeamMembers.includes(f.id)}
                                      onChange={() => toggleTeamMemberSelection(f.id)}
                                      className="h-4 w-4 rounded border-slate-300"
                                    />
                                    <div>
                                      <div className="font-medium text-slate-900">{f.name}</div>
                                      <div className="text-xs text-slate-500">{f.email}</div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-4 flex-wrap">
                                    {/* Role Selector */}
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-slate-500">Role:</span>
                                      <Select
                                        value={f.teamRole || "fundraiser"}
                                        onValueChange={(val) => updateTeamRole(f.id, val)}
                                        disabled={updatingRole === f.id}
                                      >
                                        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`role-select-${f.id}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="fundraiser">Fundraiser</SelectItem>
                                          <SelectItem value="team_leader">Team Leader</SelectItem>
                                          <SelectItem value="area_manager">Area Manager</SelectItem>
                                          <SelectItem value="regional_manager">Regional Manager</SelectItem>
                                          <SelectItem value="national_manager">National Manager</SelectItem>
                                          <SelectItem value="campaign_manager">Campaign Manager</SelectItem>
                                          <SelectItem value="client_viewer">Client Viewer</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {/* Reports To (reporting-line hierarchy) */}
                                    {((REPORTS_TO_ROLES[f.teamRole || "fundraiser"] || []).length > 0) && (
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-slate-500">Reports to:</span>
                                        <Select
                                          value={f.reportsToUserId || "none"}
                                          onValueChange={(val) => updateReportsTo(f.id, val)}
                                        >
                                          <SelectTrigger className="h-8 w-40 text-xs" data-testid={`reports-to-${f.id}`}>
                                            <SelectValue placeholder="— Unassigned —" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">— Unassigned —</SelectItem>
                                            {getEligibleManagers(f.teamRole).map(mgr => (
                                              <SelectItem key={mgr.id} value={mgr.id}>
                                                {mgr.name} · {(mgr.teamRole || "").replace(/_/g, " ")}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    )}
                                    
                                    {/* Team Leader Assignment */}
                                    {(f.teamRole === "fundraiser" || !f.teamRole) && availableLeaders.length > 0 && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-500">Assign to:</span>
                                        <Select
                                          value={f.assignedLeaderId || "none"}
                                          onValueChange={(val) => assignToTeamLeader(f.id, val)}
                                          disabled={assigningLeader === f.id}
                                        >
                                          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`assign-leader-${f.id}`}>
                                            <SelectValue placeholder="Select leader" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">Unassigned</SelectItem>
                                            {availableLeaders.map(leader => (
                                              <SelectItem key={leader.id} value={leader.id}>
                                                {leader.name}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    )}
                                    
                                    {/* Location Assignment Display (read-only, edit via bulk action) */}
                                    <div className="flex items-center gap-2 text-xs">
                                      <MapPin className="h-3 w-3 text-slate-400" />
                                      <span className={`${(f.locationIds || []).length === 0 ? 'text-slate-400' : 'text-blue-600 font-medium'}`}>
                                        {(f.locationIds || []).length === 0 
                                          ? "All locations" 
                                          : `${(f.locationIds || []).length} location${(f.locationIds || []).length !== 1 ? 's' : ''}`
                                        }
                                      </span>
                                    </div>
                                    
                                    <div className="hidden lg:grid grid-cols-4 gap-4 text-center">
                                      <div>
                                        <div className="text-xs text-slate-500">Total</div>
                                        <div className="font-semibold">{metrics.totalConversions || 0}</div>
                                      </div>
                                      <div>
                                        <div className="text-xs text-slate-500">Lottery</div>
                                        <div className="font-semibold text-violet-600">
                                          {(metrics.todayLotterySingle || 0) + ((metrics.todayLotteryDouble || 0) * 2)}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-xs text-slate-500">Regular</div>
                                        <div className="font-semibold text-emerald-600">{metrics.todayRegularCount || 0}</div>
                                      </div>
                                      <div>
                                        <div className="text-xs text-slate-500">One-Off</div>
                                        <div className="font-semibold text-amber-600">{metrics.todayOneOffCount || 0}</div>
                                      </div>
                                    </div>
                                    
                                    <button
                                      onClick={() => openComplianceModal(f)}
                                      className="flex items-center gap-1"
                                    >
                                      <span className={`w-2.5 h-2.5 rounded-full ${compStatus.color}`}></span>
                                    </button>
                                    
                                    {/* Approval Toggle */}
                                    <div className="flex items-center gap-1.5" title={f.approved ? "Approved" : "Not Approved"}>
                                      <Switch
                                        checked={f.approved || false}
                                        onCheckedChange={(val) => toggleFundraiserApproval(f.id, val)}
                                        data-testid={`approval-toggle-${f.id}`}
                                      />
                                      <span className={`text-xs font-medium ${f.approved ? 'text-emerald-600' : 'text-red-500'}`}>
                                        {f.approved ? "Approved" : "Pending"}
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedFundraiserForGuidance(f.id);
                                          setActiveTab("guidance");
                                        }}
                                        title="Send Guidance"
                                      >
                                        <MessageCircle className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => { setPerfFundraiserId(f.id); setShowFundraiserPerf(true); }}
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6 mt-6">
            {/* View toggle: User View (existing) vs Campaign View */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="planner-view-toggle">
              <button
                onClick={() => setPlannerView("user")}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${plannerView === "user" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="planner-view-user"
              >User View</button>
              <button
                onClick={() => setPlannerView("campaign")}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${plannerView === "campaign" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="planner-view-campaign"
              >Campaign View</button>
            </div>

            {plannerView === "campaign" ? (
              <CampaignPlannerView
                campaigns={campaigns}
                userNameMap={Object.fromEntries((teamMembers || []).map(u => [u.id, u.name]))}
                onOpenShift={(pre) => {
                  setEditingShift(null);
                  setPendingEditMode(null);
                  resetShiftForm();
                  setShiftForm(prev => ({ ...prev, locationId: pre.locationId, campaignId: pre.campaignId, date: pre.date }));
                  setShowAddShiftModal(true);
                }}
                onRefresh={loadSchedules}
              />
            ) : (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold text-lg text-slate-900">Weekly Schedule</h2>
                      <p className="text-sm text-slate-500">Assign fundraisers to locations and time slots</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={openAutoPlanNextWeek}
                      className="text-slate-700 border-emerald-300 hover:bg-emerald-50"
                      data-testid="auto-plan-next-week-btn"
                    >
                      <Zap className="h-4 w-4 mr-2 text-emerald-600" />
                      Auto-Plan Next Week
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowDuplicateWeek(true)}
                      className="text-slate-600"
                      data-testid="duplicate-week-btn"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate Week
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingShift(null);
                        setPendingEditMode(null);
                        resetShiftForm();
                        setShowAddShiftModal(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="add-shift-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Shift
                    </Button>
                  </div>
                </div>

                {/* Campaign Filter + Week Navigation */}
                <div className="flex items-center gap-3 mb-6">
                  <Select value={plannerCampaignFilter || "all"} onValueChange={(v) => setPlannerCampaignFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[200px] h-9" data-testid="planner-campaign-filter">
                      <SelectValue placeholder="All Campaigns" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Campaigns</SelectItem>
                      {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex-1 flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                      <ChevronRight className="h-4 w-4 rotate-180" />
                      Previous Week
                    </Button>
                    <span className="font-medium text-slate-700">
                      {new Date(scheduleWeekStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      {' – '}
                      {new Date(new Date(scheduleWeekStart).setDate(new Date(scheduleWeekStart).getDate() + 6)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                      Next Week
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Schedule Grid */}
                {loadingSchedules ? (
                  <div className="text-center py-12">
                    <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-slate-500 mt-2">Loading schedule...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(getSchedulesByDay()).map(([date, daySchedules]) => {
                      const dateObj = new Date(date);
                      const isToday = date === new Date().toISOString().split('T')[0];
                      
                      return (
                        <div 
                          key={date} 
                          className={`border rounded-xl overflow-hidden ${isToday ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'}`}
                          data-testid={`schedule-day-${date}`}
                        >
                          {/* Day Header */}
                          <div className={`px-4 py-3 ${isToday ? 'bg-blue-100' : 'bg-slate-50'} border-b border-slate-200`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900">
                                  {dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
                                </span>
                                {isToday && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">Today</span>
                                )}
                              </div>
                              <span className="text-sm text-slate-500">
                                {daySchedules.length} shift{daySchedules.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          
                          {/* Day Shifts */}
                          <div className="divide-y divide-slate-100">
                            {daySchedules.length === 0 ? (
                              <div className="px-4 py-6 text-center text-slate-400 text-sm">
                                No shifts scheduled
                              </div>
                            ) : (
                              daySchedules.map(shift => (
                                <div 
                                  key={shift.id}
                                  className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                                  onClick={() => handleEditShift(shift)}
                                  data-testid={`shift-${shift.id}`}
                                >
                                  <div className="flex items-center gap-4">
                                    <div className="text-sm font-mono font-medium text-slate-700 bg-slate-100 px-2 py-1 rounded">
                                      {shift.startTime}–{shift.endTime}
                                    </div>
                                    <div>
                                      <div className="font-medium text-slate-900 flex items-center gap-2">
                                        {shift.isUnassigned ? (
                                          <span className="text-amber-600 flex items-center gap-1">
                                            <UserMinus className="h-3.5 w-3.5" />
                                            Unassigned
                                          </span>
                                        ) : shift.userNames && shift.userNames.length > 1 ? (
                                          <span className="flex items-center gap-1">
                                            <Users className="h-3.5 w-3.5 text-blue-500" />
                                            {shift.userNames.length} fundraisers
                                          </span>
                                        ) : (
                                          shift.userName || "Unknown"
                                        )}
                                        {shift.isRecurring && (
                                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded flex items-center gap-0.5">
                                            <Repeat className="h-3 w-3" />
                                            Weekly
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm text-slate-500 flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {shift.locationName}
                                        {shift.campaignName && (
                                          <span className="ml-2 text-violet-600">• {shift.campaignName}</span>
                                        )}
                                      </div>
                                      {shift.userNames && shift.userNames.length > 1 && (
                                        <div className="text-xs text-slate-400 mt-0.5">
                                          {shift.userNames.join(", ")}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteShift(shift);
                                    }}
                                    className="text-slate-400 hover:text-red-500"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Payroll Tab */}
          <TabsContent value="payroll" className="space-y-6 mt-6">
            {/* Package 6F — Agency-configurable fundraiser bond engine */}
            <BondManagement fundraisers={teamMembers.filter(m => m.role === "fundraiser")} onPolicyState={setBond6DActive} canWrite={user?.role === "owner" || user?.role === "platform_owner"} />
            {/* Pay Settings per Fundraiser */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Fundraiser Pay Settings</h2>
                    <p className="text-sm text-slate-500">{bond6DActive ? "Set pay per signup — bond deductions are managed by the Bond Engine policy above" : "Set pay per signup and bond deduction per fundraiser"}</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="pay-settings-table">
                    <thead>
                      <tr className="border-b text-left text-slate-500">
                        <th className="pb-2 pr-4">Fundraiser</th>
                        <th className="pb-2 pr-4">Pay per Signup</th>
                        {!bond6DActive && <th className="pb-2 pr-4">Bond Amount</th>}
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamMembers.filter(m => m.role === "fundraiser").map(m => (
                        <tr key={m.id} className="border-b border-slate-100">
                          <td className="py-2 pr-4 font-medium text-slate-800">{m.name}</td>
                          {editingPaySettings === m.id ? (
                            <>
                              <td className="py-2 pr-4">
                                <Input type="number" step="0.01" min="0" className="w-24 h-8 text-sm" defaultValue={m.payPerSignup || 0} id={`pay-${m.id}`} data-testid={`pay-input-${m.id}`} />
                              </td>
                              {!bond6DActive && (
                                <td className="py-2 pr-4">
                                  <Input type="number" step="0.01" min="0" className="w-24 h-8 text-sm" defaultValue={m.bondAmount || 0} id={`bond-${m.id}`} data-testid={`bond-input-${m.id}`} />
                                </td>
                              )}
                              <td className="py-2 flex gap-1">
                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={savingPaySettings} data-testid={`save-pay-${m.id}`}
                                  onClick={() => savePaySettings(m.id, document.getElementById(`pay-${m.id}`).value, document.getElementById(`bond-${m.id}`)?.value ?? (m.bondAmount || 0))}>
                                  {savingPaySettings ? "..." : "Save"}
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingPaySettings(null)}>Cancel</Button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-2 pr-4 text-slate-600">{m.payPerSignup ? `£${Number(m.payPerSignup).toFixed(2)}` : "£0.00"}</td>
                              {!bond6DActive && <td className="py-2 pr-4 text-slate-600">{m.bondAmount ? `£${Number(m.bondAmount).toFixed(2)}` : "£0.00"}</td>}
                              <td className="py-2">
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600" onClick={() => setEditingPaySettings(m.id)} data-testid={`edit-pay-${m.id}`} disabled={user?.role === "manager"}>Edit</Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Generate Payroll — write roles only */}
            {(user?.role === "owner" || user?.role === "platform_owner") && (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Generate Payroll</h2>
                    <p className="text-sm text-slate-500">Calculate pay for a weekly period</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-4 mb-4">
                  <div>
                    <Label className="text-slate-700 text-sm">From</Label>
                    <Input type="date" value={payrollDateRange.start} onChange={(e) => setPayrollDateRange({...payrollDateRange, start: e.target.value})} className="mt-1 w-44" data-testid="payroll-date-from" />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm">To</Label>
                    <Input type="date" value={payrollDateRange.end} onChange={(e) => setPayrollDateRange({...payrollDateRange, end: e.target.value})} className="mt-1 w-44" data-testid="payroll-date-to" />
                  </div>
                  <Button onClick={generatePayroll} disabled={generatingPayroll} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="generate-payroll-btn">
                    <Plus className="h-4 w-4" />{generatingPayroll ? "Generating..." : "Generate Payroll"}
                  </Button>
                </div>

                {/* Previous Payroll Periods */}
                {payrollPeriods.length > 0 && (
                  <div className="mt-4">
                    <Label className="text-slate-700 text-sm mb-2 block">Previous Payroll Runs</Label>
                    <div className="flex flex-wrap gap-2">
                      {payrollPeriods.map(p => (
                        <Button key={p.payrollId} size="sm" variant={selectedPayrollPeriod?.weekStart === p.weekStart ? "default" : "outline"} className="text-xs"
                          onClick={() => { setSelectedPayrollPeriod(p); loadPayrollRecords(p.weekStart, p.weekEnd); }}
                          data-testid={`payroll-period-${p.weekStart}`}>
                          {p.weekStart} to {p.weekEnd} ({p.recordCount} records, £{p.totalNet.toFixed(2)})
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Payroll Table */}
            {selectedPayrollPeriod && (
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-heading font-bold text-lg text-slate-900">Payroll: {selectedPayrollPeriod.weekStart} to {selectedPayrollPeriod.weekEnd}</h2>
                      <p className="text-sm text-slate-500">{payrollRecords.length} fundraiser(s)</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={exportPayrollCsv} className="gap-1" data-testid="export-payroll-btn">
                        <Download className="h-4 w-4" />Export CSV
                      </Button>
                      {(user?.role === "owner" || user?.role === "platform_owner") && (
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => deletePayroll(selectedPayrollPeriod.payrollId)} data-testid="delete-payroll-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      )}
                    </div>
                  </div>
                  {loadingPayroll ? (
                    <div className="text-center py-8 text-slate-400"><RefreshCw className="h-5 w-5 animate-spin inline mr-2" />Loading...</div>
                  ) : payrollRecords.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No payroll records for this period</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="payroll-table">
                        <thead>
                          <tr className="border-b text-left text-slate-500">
                            <th className="pb-2 pr-4">Fundraiser</th>
                            <th className="pb-2 pr-4 text-right">Total Signups</th>
                            <th className="pb-2 pr-4 text-right">Cancelled</th>
                            <th className="pb-2 pr-4 text-right">Net Signups</th>
                            <th className="pb-2 pr-4 text-right">Pay/Signup</th>
                            <th className="pb-2 pr-4 text-right">Gross</th>
                            <th className="pb-2 pr-4 text-right">Bond</th>
                            <th className="pb-2 text-right">Net Pay</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payrollRecords.map(r => (
                            <tr key={r.id} className="border-b border-slate-100">
                              <td className="py-2 pr-4 font-medium text-slate-800">{r.fundraiserName}</td>
                              <td className="py-2 pr-4 text-right">{r.totalSignups}</td>
                              <td className="py-2 pr-4 text-right text-red-600">{r.cancelledSignups}</td>
                              <td className="py-2 pr-4 text-right font-medium">{r.netSignups}</td>
                              <td className="py-2 pr-4 text-right">£{Number(r.payPerSignup).toFixed(2)}</td>
                              <td className="py-2 pr-4 text-right">£{Number(r.gross).toFixed(2)}</td>
                              <td className="py-2 pr-4 text-right text-amber-600">£{Number(r.bond).toFixed(2)}</td>
                              <td className="py-2 text-right font-bold text-emerald-700">£{Number(r.net).toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr className="border-t-2 border-slate-300 font-bold">
                            <td className="py-2 pr-4">TOTAL</td>
                            <td className="py-2 pr-4 text-right">{payrollRecords.reduce((s, r) => s + r.totalSignups, 0)}</td>
                            <td className="py-2 pr-4 text-right text-red-600">{payrollRecords.reduce((s, r) => s + r.cancelledSignups, 0)}</td>
                            <td className="py-2 pr-4 text-right">{payrollRecords.reduce((s, r) => s + r.netSignups, 0)}</td>
                            <td className="py-2 pr-4"></td>
                            <td className="py-2 pr-4 text-right">£{payrollRecords.reduce((s, r) => s + r.gross, 0).toFixed(2)}</td>
                            <td className="py-2 pr-4 text-right text-amber-600">£{payrollRecords.reduce((s, r) => s + r.bond, 0).toFixed(2)}</td>
                            <td className="py-2 text-right text-emerald-700">£{payrollRecords.reduce((s, r) => s + r.net, 0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Campaign Settings Tab */}
          <TabsContent value="settings" className="space-y-6 mt-6">
            <CharityManagementCentre />
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Settings className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Campaign Control Panel</h2>
                    <p className="text-sm text-slate-500">Configure donation modes, consent options, and form behaviour</p>
                  </div>
                </div>

                {/* Campaign Selector with Create Button */}
                <div className="mb-6">
                  <div className="flex items-end gap-2 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-slate-700 font-medium">Select Campaign</Label>
                      <Select value={settingsCampaignId} onValueChange={setSettingsCampaignId}>
                        <SelectTrigger className="h-12 mt-1.5" data-testid="settings-campaign-select">
                          <SelectValue placeholder="Choose a campaign" />
                        </SelectTrigger>
                        <SelectContent>
                          {campaigns.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}{c.isArchived ? " (Archived)" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!isStandardManager && (
                      <>
                        <Button
                          onClick={() => setShowCreateCampaignModal(true)}
                          className="h-12 bg-emerald-500 hover:bg-emerald-600 text-white"
                          data-testid="create-campaign-btn"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create Campaign
                        </Button>
                        <Button
                          onClick={() => setShowCreateLocationModal(true)}
                          variant="outline"
                          className="h-12 border-blue-300 text-blue-600 hover:bg-blue-50"
                          data-testid="create-location-btn"
                        >
                          <MapPin className="h-4 w-4 mr-2" />
                          Create Location
                        </Button>
                        {settingsCampaignId && (
                          campaigns.find(c => c.id === settingsCampaignId)?.isArchived ? (
                            <Button
                              onClick={() => toggleArchiveCampaign(false)}
                              variant="outline"
                              className="h-12 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                              data-testid="restore-campaign-btn"
                            >
                              Restore Campaign
                            </Button>
                          ) : (
                            <Button
                              onClick={() => toggleArchiveCampaign(true)}
                              variant="outline"
                              className="h-12 border-amber-300 text-amber-600 hover:bg-amber-50"
                              data-testid="archive-campaign-btn"
                            >
                              Archive Campaign
                            </Button>
                          )
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-8">
                  {settingsCampaignId && (
                    <CampaignBeneficiarySelector campaignId={settingsCampaignId} canEdit={isOwnerTier} />
                  )}
                  {/* 1. Donation Modes */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Heart className="h-5 w-5 text-emerald-500" />
                      Donation Modes
                      {!isOwnerTier && (
                        <span className="text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="donation-modes-owner-only">
                          Owner only — read-only
                        </span>
                      )}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center justify-between p-4 bg-violet-50 rounded-xl border border-violet-200">
                        <div className="flex items-center gap-3">
                          <Ticket className="h-5 w-5 text-violet-600" />
                          <div>
                            <div className="font-medium text-violet-900">Lottery</div>
                            <div className="text-xs text-violet-600">Enable lottery entries</div>
                          </div>
                        </div>
                        <Switch
                          checked={campaignSettings.enableLottery}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, enableLottery: v }))}
                          disabled={!isOwnerTier}
                          data-testid="toggle-lottery"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                        <div className="flex items-center gap-3">
                          <Heart className="h-5 w-5 text-emerald-600" />
                          <div>
                            <div className="font-medium text-emerald-900">Regular Giving</div>
                            <div className="text-xs text-emerald-600">Enable monthly donations</div>
                          </div>
                        </div>
                        <Switch
                          checked={campaignSettings.enableRegularGiving}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, enableRegularGiving: v }))}
                          disabled={!isOwnerTier}
                          data-testid="toggle-regular-giving"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                        <div className="flex items-center gap-3">
                          <Gift className="h-5 w-5 text-amber-600" />
                          <div>
                            <div className="font-medium text-amber-900">One-Off</div>
                            <div className="text-xs text-amber-600">Enable single donations</div>
                          </div>
                        </div>
                        <Switch
                          checked={campaignSettings.enableOneOff}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, enableOneOff: v }))}
                          disabled={!isOwnerTier}
                          data-testid="toggle-oneoff"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 2. Lottery Settings */}
                  {campaignSettings.enableLottery && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Ticket className="h-5 w-5 text-violet-500" />
                        Lottery Pricing
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 font-medium">Single Entry Price (£)</Label>
                          <NumericInput
                            min="0"
                            step="0.01"
                            fallback={0}
                            value={campaignSettings.lotterySinglePrice}
                            onValueChange={(v) => setCampaignSettings(prev => ({ ...prev, lotterySinglePrice: v }))}
                            className="h-12 mt-1.5"
                            data-testid="lottery-single-price"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-700 font-medium">Double Entry Price (£)</Label>
                          <NumericInput
                            min="0"
                            step="0.01"
                            fallback={0}
                            value={campaignSettings.lotteryDoublePrice}
                            onValueChange={(v) => setCampaignSettings(prev => ({ ...prev, lotteryDoublePrice: v }))}
                            className="h-12 mt-1.5"
                            data-testid="lottery-double-price"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 3. Regular Giving Settings */}
                  {campaignSettings.enableRegularGiving && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Heart className="h-5 w-5 text-emerald-500" />
                        Regular Giving Settings
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-slate-700 font-medium">Preset Amounts (comma-separated, £)</Label>
                          <Input
                            value={presetsInput}
                            onChange={(e) => setPresetsInput(e.target.value)}
                            placeholder="5, 10, 15, 20, 25"
                            className="h-12 mt-1.5"
                            data-testid="regular-presets"
                          />
                          <p className="text-xs text-slate-500 mt-1">Example: 5, 10, 15, 20, 25</p>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <div>
                            <div className="font-medium text-slate-900">Allow Custom Amount</div>
                            <div className="text-sm text-slate-500">Allow donors to enter their own amount</div>
                          </div>
                          <Switch
                            checked={campaignSettings.allowCustomAmount}
                            onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, allowCustomAmount: v }))}
                            data-testid="toggle-custom-amount"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 4. One-Off Settings */}
                  {campaignSettings.enableOneOff && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Gift className="h-5 w-5 text-amber-500" />
                        One-Off Settings
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <div>
                            <div className="font-medium text-slate-900">Enable Contactless</div>
                            <div className="text-sm text-slate-500">Allow Apple Pay / Google Pay</div>
                          </div>
                          <Switch
                            checked={campaignSettings.enableContactless}
                            onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, enableContactless: v }))}
                            data-testid="toggle-contactless"
                          />
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <div>
                            <div className="font-medium text-slate-900">Require Details</div>
                            <div className="text-sm text-slate-500">Require name/email/phone for one-off payments</div>
                          </div>
                          <Switch
                            checked={campaignSettings.oneOffRequireDetails}
                            onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, oneOffRequireDetails: v }))}
                            data-testid="toggle-oneoff-details"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 5. Contact Consent Options */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-500" />
                      Contact Consent Options
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">Choose which consent checkboxes appear on the form</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-500" />
                          <span className="font-medium text-slate-700">Email</span>
                        </div>
                        <Switch
                          checked={campaignSettings.allowEmailConsent}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, allowEmailConsent: v }))}
                          data-testid="toggle-email-consent"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-slate-500" />
                          <span className="font-medium text-slate-700">SMS</span>
                        </div>
                        <Switch
                          checked={campaignSettings.allowSmsConsent}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, allowSmsConsent: v }))}
                          data-testid="toggle-sms-consent"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-slate-500" />
                          <span className="font-medium text-slate-700">Phone</span>
                        </div>
                        <Switch
                          checked={campaignSettings.allowPhoneConsent}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, allowPhoneConsent: v }))}
                          data-testid="toggle-phone-consent"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-500" />
                          <span className="font-medium text-slate-700">Post</span>
                        </div>
                        <Switch
                          checked={campaignSettings.allowPostConsent}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, allowPostConsent: v }))}
                          data-testid="toggle-post-consent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 6. Form Requirements */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Target className="h-5 w-5 text-red-500" />
                      Form Requirements
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">Choose which fields are required on the form</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <div className="font-medium text-slate-900">Require Phone</div>
                          <div className="text-xs text-slate-500">Make phone mandatory</div>
                        </div>
                        <Switch
                          checked={campaignSettings.requirePhone}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, requirePhone: v }))}
                          data-testid="toggle-require-phone"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <div className="font-medium text-slate-900">Require Email</div>
                          <div className="text-xs text-slate-500">Make email mandatory</div>
                        </div>
                        <Switch
                          checked={campaignSettings.requireEmail}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, requireEmail: v }))}
                          data-testid="toggle-require-email"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <div className="font-medium text-slate-900">Require Address</div>
                          <div className="text-xs text-slate-500">Make address mandatory</div>
                        </div>
                        <Switch
                          checked={campaignSettings.requireAddress}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, requireAddress: v }))}
                          data-testid="toggle-require-address"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 7. SMS Settings */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-emerald-500" />
                      SMS Settings
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">Control SMS behaviour for this campaign</p>
                    {!featureMode.platformDonorCommsAllowed ? (
                      <SafetyFreezeNotice
                        title="Automatic donor SMS is temporarily unavailable"
                        message="Automatic donor SMS is disabled until charity-owned Twilio is configured. Backend guards are authoritative — no SMS will be sent even if these controls are enabled."
                        testId="campaign-sms-safety-notice"
                      />
                    ) : (
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                        <div>
                          <div className="font-medium text-emerald-900">Enable SMS</div>
                          <div className="text-xs text-emerald-600">Master toggle for all SMS features</div>
                        </div>
                        <Switch
                          checked={campaignSettings.enableSMS}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, enableSMS: v }))}
                          data-testid="toggle-enable-sms"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <div className="font-medium text-slate-900">Auto Thank You SMS</div>
                          <div className="text-xs text-slate-500">Send thank you SMS after conversion (if consent given)</div>
                        </div>
                        <Switch
                          checked={campaignSettings.enableAutoThankYouSMS}
                          onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, enableAutoThankYouSMS: v }))}
                          disabled={!campaignSettings.enableSMS}
                          data-testid="toggle-auto-thankyou-sms"
                        />
                      </div>
                    </div>
                    {!campaignSettings.enableSMS && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        SMS is disabled. No SMS will be sent for this campaign.
                      </p>
                    )}
                    </>
                    )}
                  </div>

                  {/* 7b. Gift Aid (additive) */}
                  <div data-testid="gift-aid-settings">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Gift className="h-5 w-5 text-emerald-500" />
                      Gift Aid
                    </h3>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Label htmlFor="enableGiftAid" className="font-medium text-slate-900">Enable Gift Aid</Label>
                        <p className="text-xs text-slate-500">Show Gift Aid checkbox on Lottery, Regular Giving and One-Off forms.</p>
                      </div>
                      <Switch
                        id="enableGiftAid"
                        checked={!!campaignSettings.enableGiftAid}
                        onCheckedChange={(v) => setCampaignSettings(prev => ({ ...prev, enableGiftAid: v }))}
                        data-testid="enable-gift-aid-toggle"
                      />
                    </div>
                    {campaignSettings.enableGiftAid && (
                      <div>
                        <Label className="text-sm text-slate-700">Gift Aid Statement</Label>
                        <Textarea
                          value={campaignSettings.giftAidStatement}
                          onChange={(e) => setCampaignSettings(prev => ({ ...prev, giftAidStatement: e.target.value }))}
                          placeholder="I am a UK taxpayer and want this charity to reclaim tax on this donation and any donations I make in the future or have made in the past four years. I understand I must pay enough UK tax to cover the amount reclaimed."
                          className="min-h-[120px] mt-1.5"
                          data-testid="gift-aid-statement-input"
                        />
                        <p className="text-xs text-slate-500 mt-1.5">Shown to donors when they tick Gift Aid. Leave blank to use the default UK wording.</p>
                      </div>
                    )}
                  </div>

                  {/* 7c. Campaign Control Centre (read-only, owner + manager) — OCI V2.17B / V2.24B compact summary */}
                  {settingsCampaignId && (user?.role === "owner" || user?.role === "manager") && (
                    <div data-testid="campaign-control-centre-section">
                      <CampaignControlCentre campaignId={settingsCampaignId} />
                    </div>
                  )}

                  {/* OCI V2.24B — campaign-level Stripe/payment plumbing removed from campaign ops.
                      Payment destinations are managed by the charity (charity dashboard → Connect)
                      and Platform → Payment Destinations. */}

                  {/* 8. Disclosure Text */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      Disclosure Text
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      This text appears in the yellow/amber disclosure box on the form. Leave empty for default GDPR text.
                    </p>
                    <Textarea
                      value={campaignSettings.disclosureText}
                      onChange={(e) => setCampaignSettings(prev => ({ ...prev, disclosureText: e.target.value }))}
                      placeholder="Enter custom GDPR/disclosure text..."
                      className="min-h-[120px]"
                      data-testid="disclosure-text"
                    />
                    {campaignSettings.disclosureText && (
                      <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">{campaignSettings.disclosureText}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 8b. Direct Debit Guarantee Text */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-500" />
                      Direct Debit Guarantee Text
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Shown on regular donation forms alongside the disclosure text and signature. Leave empty to omit.
                    </p>
                    <Textarea
                      value={campaignSettings.directDebitGuaranteeText}
                      onChange={(e) => setCampaignSettings(prev => ({ ...prev, directDebitGuaranteeText: e.target.value }))}
                      placeholder="Enter Direct Debit Guarantee text..."
                      className="min-h-[120px]"
                      data-testid="dd-guarantee-text"
                    />
                    {campaignSettings.directDebitGuaranteeText && (
                      <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-200">
                        <div className="flex items-start gap-2">
                          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-800">{campaignSettings.directDebitGuaranteeText}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 8c. One-Off Disclosure Text */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      One-Off Donation Disclosure Text
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Shown above the Pay button on one-off donation forms. Leave empty to omit.
                    </p>
                    <Textarea
                      value={campaignSettings.oneOffDisclosureText}
                      onChange={(e) => setCampaignSettings(prev => ({ ...prev, oneOffDisclosureText: e.target.value }))}
                      placeholder="Enter one-off donation disclosure text..."
                      className="min-h-[120px]"
                      data-testid="oneoff-disclosure-text"
                    />
                    {campaignSettings.oneOffDisclosureText && (
                      <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">{campaignSettings.oneOffDisclosureText}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 9. Age Flagging (Compliance) */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      Age Flagging (Compliance)
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Conversions with ages outside this range will be flagged for review
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 font-medium">Minimum Age</Label>
                        <NumericInput
                          integer
                          fallback={18}
                          value={campaignSettings.minAge}
                          onValueChange={(v) => setCampaignSettings(prev => ({ ...prev, minAge: v }))}
                          min={18}
                          max={100}
                          className="h-12 mt-1.5"
                          data-testid="min-age-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 font-medium">Maximum Age</Label>
                        <NumericInput
                          integer
                          fallback={100}
                          value={campaignSettings.maxAge}
                          onValueChange={(v) => setCampaignSettings(prev => ({ ...prev, maxAge: v }))}
                          min={18}
                          max={120}
                          className="h-12 mt-1.5"
                          data-testid="max-age-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Default: 18-100. Flagged conversions appear in the Flagged tab.
                    </p>
                  </div>
                  
                  {/* 9.1 Over-Age Rule Engine */}
                  <div className="border-t pt-8">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-orange-500" />
                      Over-Age Rule Engine
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Configure what happens when a donor's age reaches or exceeds a threshold
                    </p>
                    
                    <div className="mb-4">
                      <Label className="text-slate-700 font-medium">Over-Age Threshold</Label>
                      <NumericInput
                        integer
                        fallback={80}
                        value={campaignSettings.overAgeThreshold}
                        onValueChange={(v) => setCampaignSettings(prev => ({ ...prev, overAgeThreshold: v }))}
                        min={50}
                        max={100}
                        className="h-12 mt-1.5 w-32"
                        data-testid="over-age-threshold-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Rule triggers when donor age is greater than or equal to this value
                      </p>
                    </div>
                    
                    <div>
                      <Label className="text-slate-700 font-medium">Over-Age Action</Label>
                      <Select
                        value={campaignSettings.overAgeAction}
                        onValueChange={(v) => setCampaignSettings(prev => ({ ...prev, overAgeAction: v }))}
                      >
                        <SelectTrigger className="h-12 mt-1.5" data-testid="over-age-action-select">
                          <SelectValue placeholder="Select action" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="allow">Allow Normal Signup</SelectItem>
                          <SelectItem value="block">Block Signup</SelectItem>
                          <SelectItem value="callback">Require Callback (No Bank Details)</SelectItem>
                          <SelectItem value="self_complete">Require Self-Completion (Referral)</SelectItem>
                          <SelectItem value="callback_or_self_complete">Callback OR Self-Completion (Choice)</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="mt-2 p-3 rounded-lg bg-slate-50 text-sm text-slate-600">
                        {campaignSettings.overAgeAction === "allow" && (
                          <span>No restrictions. Normal signup process.</span>
                        )}
                        {campaignSettings.overAgeAction === "block" && (
                          <span className="text-red-600">Signup blocked. Donor shown message to contact directly.</span>
                        )}
                        {campaignSettings.overAgeAction === "callback" && (
                          <span className="text-amber-600">Bank fields hidden. Submission flagged for callback.</span>
                        )}
                        {campaignSettings.overAgeAction === "self_complete" && (
                          <span className="text-amber-600">Fundraiser completion blocked. Donor uses referral link.</span>
                        )}
                        {campaignSettings.overAgeAction === "callback_or_self_complete" && (
                          <span className="text-amber-600">Donor chooses between callback or self-completion.</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* 9.2 Payment Frequency Options */}
                  <div className="border-t pt-8">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-green-500" />
                      Payment Frequency
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Allow donors to choose between monthly and annual payment options
                    </p>
                    
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <span className="font-medium text-slate-900">Enable Annual Payments</span>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Shows Monthly/Annual selector for Regular Giving & Lottery
                        </p>
                      </div>
                      <Switch
                        checked={campaignSettings.enableAnnualPayments}
                        onCheckedChange={(checked) => setCampaignSettings(prev => ({ ...prev, enableAnnualPayments: checked }))}
                        data-testid="enable-annual-payments-toggle"
                      />
                    </div>
                  </div>

                  {/* 10. Organisation Branding (Fundraiser White Label) */}
                  <div className="border-t pt-8">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-blue-500" />
                      Organisation Branding
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Customize how your organisation appears to fundraisers on their login and dashboard
                    </p>
                    <div className="space-y-4">
                      {/* Organisation Display Name */}
                      <div>
                        <Label className="text-slate-700 font-medium">Organisation Display Name</Label>
                        <Input
                          value={orgBranding.orgDisplayName}
                          onChange={(e) => setOrgBranding(prev => ({ ...prev, orgDisplayName: e.target.value }))}
                          placeholder="Your Organisation Name"
                          className="h-12 mt-1.5"
                          data-testid="org-display-name-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          This name appears on fundraiser login and dashboard screens
                        </p>
                      </div>

                      {/* Organisation Logo Upload */}
                      <div>
                        <Label className="text-slate-700 font-medium">Organisation Logo</Label>
                        <div className="mt-1.5 flex items-center gap-4">
                          {orgBranding.orgLogoUrl ? (
                            <div className="w-16 h-16 rounded-lg border border-slate-200 overflow-hidden bg-white">
                              <img 
                                src={orgBranding.orgLogoUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${orgBranding.orgLogoUrl}` : orgBranding.orgLogoUrl} 
                                alt="Organisation Logo" 
                                className="w-full h-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                              <Building2 className="h-6 w-6 text-slate-400" />
                            </div>
                          )}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && uploadOrgLogo(e.target.files[0])}
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              disabled={uploadingOrgLogo}
                              className="pointer-events-none"
                            >
                              {uploadingOrgLogo ? "Uploading..." : "Upload Logo"}
                            </Button>
                          </label>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Square logo recommended (256x256 or larger)
                        </p>
                      </div>

                      <Button
                        onClick={saveOrgBranding}
                        disabled={savingOrgBranding}
                        className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white"
                        data-testid="save-org-branding-btn"
                      >
                        {savingOrgBranding ? "Saving..." : "Save Organisation Branding"}
                      </Button>
                    </div>
                  </div>

                  {/* 11. Campaign Branding / White Label */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Palette className="h-5 w-5 text-violet-500" />
                      Campaign Branding / White Label
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Customize how your campaign forms appear to donors
                    </p>
                    <div className="space-y-4">
                      {/* Brand Name */}
                      <div>
                        <Label className="text-slate-700 font-medium">Brand Name</Label>
                        <Input
                          value={brandingSettings.brandName}
                          onChange={(e) => { setBrandingSettings(prev => ({ ...prev, brandName: e.target.value })); setBrandingDirty(true); }}
                          placeholder="Your Organisation Name"
                          className="h-12 mt-1.5"
                          data-testid="brand-name-input"
                        />
                      </div>

                      {/* Logo Upload */}
                      <div>
                        <Label className="text-slate-700 font-medium">Logo</Label>
                        <div className="mt-1.5 flex items-center gap-4">
                          {brandingSettings.logoUrl && !logoBroken ? (
                            <div className="w-16 h-16 rounded-lg border border-slate-200 overflow-hidden">
                              <img 
                                src={brandingSettings.logoUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${brandingSettings.logoUrl}` : brandingSettings.logoUrl} 
                                alt="Logo" 
                                data-testid="branding-logo-img"
                                className="w-full h-full object-contain"
                                onError={() => setLogoBroken(true)}
                              />
                            </div>
                          ) : brandingSettings.logoUrl && logoBroken ? (
                            <div className="w-16 h-16 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 flex items-center justify-center text-center" data-testid="branding-logo-missing">
                              <span className="text-[10px] text-amber-700 px-1">Image unavailable — re-upload</span>
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                              <Image className="h-6 w-6 text-slate-400" />
                            </div>
                          )}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              disabled={uploadingLogo}
                              className="pointer-events-none"
                            >
                              {uploadingLogo ? "Uploading..." : "Upload Logo"}
                            </Button>
                          </label>
                        </div>
                      </div>

                      {/* Header Image Upload */}
                      <div>
                        <Label className="text-slate-700 font-medium">Header Image</Label>
                        <div className="mt-1.5">
                          {brandingSettings.headerImageUrl && !headerBroken ? (
                            <div className="w-full h-32 rounded-lg border border-slate-200 overflow-hidden mb-2">
                              <img 
                                src={brandingSettings.headerImageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${brandingSettings.headerImageUrl}` : brandingSettings.headerImageUrl} 
                                alt="Header" 
                                data-testid="branding-header-img"
                                className="w-full h-full object-cover"
                                onError={() => setHeaderBroken(true)}
                              />
                            </div>
                          ) : brandingSettings.headerImageUrl && headerBroken ? (
                            <div className="w-full h-32 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 flex items-center justify-center mb-2" data-testid="branding-header-missing">
                              <span className="text-xs text-amber-700">Header image unavailable — please re-upload</span>
                            </div>
                          ) : null}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && uploadHeader(e.target.files[0])}
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              disabled={uploadingHeader}
                              className="pointer-events-none"
                            >
                              {uploadingHeader ? "Uploading..." : "Upload Header Image"}
                            </Button>
                          </label>
                        </div>
                      </div>

                      {/* Colors */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 font-medium">Primary Color</Label>
                          <div className="flex items-center gap-2 mt-1.5">
                            <input
                              type="color"
                              value={brandingSettings.primaryColor}
                              onChange={(e) => { setBrandingSettings(prev => ({ ...prev, primaryColor: e.target.value })); setBrandingDirty(true); }}
                              className="w-12 h-12 rounded border cursor-pointer"
                            />
                            <Input
                              value={brandingSettings.primaryColor}
                              onChange={(e) => { setBrandingSettings(prev => ({ ...prev, primaryColor: e.target.value })); setBrandingDirty(true); }}
                              className="h-12 flex-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-medium">Button Color</Label>
                          <div className="flex items-center gap-2 mt-1.5">
                            <input
                              type="color"
                              value={brandingSettings.buttonColor}
                              onChange={(e) => { setBrandingSettings(prev => ({ ...prev, buttonColor: e.target.value })); setBrandingDirty(true); }}
                              className="w-12 h-12 rounded border cursor-pointer"
                            />
                            <Input
                              value={brandingSettings.buttonColor}
                              onChange={(e) => { setBrandingSettings(prev => ({ ...prev, buttonColor: e.target.value })); setBrandingDirty(true); }}
                              className="h-12 flex-1"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Hide TapTrack Branding */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div>
                          <div className="font-medium text-slate-900">Hide Engage Branding</div>
                          <div className="text-sm text-slate-500">Remove Engage logo and footer from donor-facing screens</div>
                        </div>
                        <Switch
                          checked={brandingSettings.hideTapTrackBranding}
                          onCheckedChange={(v) => { setBrandingSettings(prev => ({ ...prev, hideTapTrackBranding: v })); setBrandingDirty(true); }}
                          data-testid="toggle-hide-taptrack"
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        {brandingDirty ? (
                          <span className="text-xs text-amber-600 font-medium" data-testid="branding-unsaved-indicator">
                            ● Unsaved changes — click Save Branding to persist
                          </span>
                        ) : (
                          <span className="text-xs text-emerald-600 font-medium" data-testid="branding-saved-indicator">
                            ✓ Saved
                          </span>
                        )}
                      </div>
                      <Button
                        onClick={saveBranding}
                        disabled={savingBranding}
                        className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white"
                        data-testid="save-branding-btn"
                      >
                        {savingBranding ? "Saving..." : "Save Branding"}
                      </Button>
                    </div>
                  </div>

                  {/* 10. Integrations / API */}
                  <div className="border-t pt-8">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Key className="h-5 w-5 text-blue-500" />
                      API Integration
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Use your API key to integrate with payroll, accounting, or other systems
                    </p>
                    
                    {loadingApiKey ? (
                      <div className="text-center py-4 text-slate-500">Loading...</div>
                    ) : apiKey?.apiKey || apiKey?.fullKey ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-slate-100 rounded-xl">
                          <Label className="text-xs text-slate-500">API Key</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="flex-1 font-mono text-sm bg-white p-2 rounded border">
                              {apiKey.fullKey || apiKey.apiKey}
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const ok = await safeCopyToClipboard(apiKey.fullKey || apiKey.apiKey);
                                toast.success(ok ? "API key copied" : "Could not copy — select the key manually");
                              }}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                          {apiKey.fullKey && (
                            <p className="text-xs text-amber-600 mt-2">
                              Save this key now - it won't be shown in full again!
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={generateApiKey}
                            disabled={generatingApiKey}
                            className="flex-1"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate Key
                          </Button>
                          <Button
                            variant="outline"
                            onClick={revokeApiKey}
                            className="text-red-600 hover:text-red-700"
                          >
                            Revoke Key
                          </Button>
                        </div>
                        <div className="text-xs text-slate-500">
                          <strong>Endpoint:</strong> GET /api/exports/performance<br />
                          <strong>Auth:</strong> Authorization: Bearer {'{your-api-key}'}<br />
                          <strong>Params:</strong> dateFrom, dateTo, campaignId, fundraiserIds
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-slate-500 mb-4">No API key generated yet</p>
                        <Button
                          onClick={generateApiKey}
                          disabled={generatingApiKey}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          {generatingApiKey ? "Generating..." : "Generate API Key"}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={saveCampaignSettings}
                    disabled={savingCampaignSettings || !settingsCampaignId}
                    className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white font-semibold"
                    data-testid="save-settings-btn"
                  >
                    {savingCampaignSettings ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Save className="h-4 w-4" />
                        Save Campaign Settings
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

          </TabsContent>

          {/* Guidance Tab */}
          <TabsContent value="guidance" className="space-y-6 mt-6">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Send Guidance</h2>
                    <p className="text-sm text-slate-500">Provide coaching and feedback to your fundraisers</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Target Type Selector */}
                  <div>
                    <Label className="text-slate-700 font-medium">Send To</Label>
                    <Select value={guidanceTargetType} onValueChange={(v) => {
                      setGuidanceTargetType(v);
                      setSelectedFundraiserForGuidance("");
                      setGuidanceTargetTeam("");
                      setGuidanceTargetCampaign("");
                    }}>
                      <SelectTrigger className="h-12 mt-1.5" data-testid="guidance-target-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual Fundraiser</SelectItem>
                        <SelectItem value="team">Entire Team</SelectItem>
                        <SelectItem value="campaign">All in Campaign</SelectItem>
                        <SelectItem value="all">All Fundraisers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Individual Fundraiser Select */}
                  {guidanceTargetType === "individual" && (
                    <div>
                      <Label className="text-slate-700 font-medium">Select Fundraiser</Label>
                      <Select value={selectedFundraiserForGuidance} onValueChange={setSelectedFundraiserForGuidance}>
                        <SelectTrigger className="h-12 mt-1.5" data-testid="guidance-fundraiser-select">
                          <SelectValue placeholder="Choose a fundraiser" />
                        </SelectTrigger>
                        <SelectContent>
                          {fundraisers.map((f) => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Team Select */}
                  {guidanceTargetType === "team" && (
                    <div>
                      <Label className="text-slate-700 font-medium">Select Team Leader</Label>
                      <Select value={guidanceTargetTeam} onValueChange={setGuidanceTargetTeam}>
                        <SelectTrigger className="h-12 mt-1.5" data-testid="guidance-team-select">
                          <SelectValue placeholder="Choose a team leader" />
                        </SelectTrigger>
                        <SelectContent>
                          {getTeamLeaders().length > 0 ? (
                            getTeamLeaders().map((l) => (
                              <SelectItem key={l.id} value={l.id}>{l.name}'s Team</SelectItem>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-sm text-slate-500">No team members available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Campaign Select */}
                  {guidanceTargetType === "campaign" && (
                    <div>
                      <Label className="text-slate-700 font-medium">Select Campaign</Label>
                      <Select value={guidanceTargetCampaign} onValueChange={setGuidanceTargetCampaign}>
                        <SelectTrigger className="h-12 mt-1.5" data-testid="guidance-campaign-select">
                          <SelectValue placeholder="Choose a campaign" />
                        </SelectTrigger>
                        <SelectContent>
                          {campaigns.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* All Fundraisers Warning */}
                  {guidanceTargetType === "all" && (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-800">
                        <Users className="h-5 w-5" />
                        <span className="font-medium">This will send to ALL fundraisers in your organisation</span>
                      </div>
                    </div>
                  )}

                  {/* Suggested Insight */}
                  {guidanceTargetType === "individual" && selectedFundraiserForGuidance && getSuggestedInsight() && (
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900 mb-1">Suggested Insight</h4>
                          <p className="text-sm text-blue-700">{getSuggestedInsight()}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                            onClick={() => setGuidanceMessage(getSuggestedInsight())}
                            data-testid="use-suggestion-btn"
                          >
                            Use this suggestion
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Message Input */}
                  <div>
                    <Label className="text-slate-700 font-medium">Guidance Message</Label>
                    <Textarea
                      value={guidanceMessage}
                      onChange={(e) => setGuidanceMessage(e.target.value)}
                      placeholder="Enter your guidance message..."
                      className="mt-1.5 min-h-[120px]"
                      data-testid="guidance-message-input"
                    />
                  </div>

                  {/* Send Button */}
                  <Button
                    onClick={sendGuidance}
                    disabled={sendingGuidance || !guidanceMessage.trim() || 
                      (guidanceTargetType === "individual" && !selectedFundraiserForGuidance) ||
                      (guidanceTargetType === "team" && !guidanceTargetTeam) ||
                      (guidanceTargetType === "campaign" && !guidanceTargetCampaign)}
                    className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                    data-testid="send-guidance-btn"
                  >
                    {sendingGuidance ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        {guidanceTargetType === "all" ? "Send to All Fundraisers" : 
                         guidanceTargetType === "team" ? "Send to Team" :
                         guidanceTargetType === "campaign" ? "Send to Campaign" : "Send Guidance"}
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Guidance History */}
            {selectedFundraiserForGuidance && guidanceHistory.length > 0 && (
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-slate-900 mb-4">Previous Guidance</h3>
                  <div className="space-y-3" data-testid="guidance-history">
                    {guidanceHistory.map((g) => (
                      <div key={g.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <p className="text-slate-700 mb-2">{g.message}</p>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{new Date(g.createdAt).toLocaleDateString()}</span>
                          {g.acknowledged ? (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle2 className="h-3 w-3" />
                              Acknowledged {g.acknowledgedAt && `on ${new Date(g.acknowledgedAt).toLocaleDateString()}`}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Clock className="h-3 w-3" />
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Delivery Settings Tab */}
          <TabsContent value="delivery" className="space-y-6 mt-6">
            {/* Package 0 Safety Freeze — Signup Delivery configuration disabled
                while external donor delivery is blocked. Backend guards remain
                authoritative even if this notice is removed. */}
            {!featureMode.externalDonorDeliveryAllowed ? (
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-6">
                  <SafetyFreezeNotice
                    title="Signup Delivery is temporarily unavailable"
                    message="Signup delivery configuration (email, webhook and external API destinations) is disabled while Engage is operating in Secure Export MVP mode. Backend guards are authoritative — even if these controls were re-enabled, no delivery would fire."
                    testId="signup-delivery-safety-notice"
                  />
                </CardContent>
              </Card>
            ) : (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Send className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Delivery Settings</h2>
                    <p className="text-sm text-slate-500">Configure how conversions are delivered to your systems</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Campaign Select */}
                  <div>
                    <Label className="text-slate-700 font-medium">Campaign</Label>
                    <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                      <SelectTrigger className="h-12 mt-1.5" data-testid="delivery-campaign-select">
                        <SelectValue placeholder="Select campaign" />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Delivery Method */}
                  <div>
                    <Label className="text-slate-700 font-medium">Delivery Method</Label>
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => setDeliveryConfig({ ...deliveryConfig, method: "email" })}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          deliveryConfig.method === "email"
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid="method-email"
                      >
                        <Mail className={`h-6 w-6 mx-auto mb-2 ${deliveryConfig.method === "email" ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <div className={`font-medium ${deliveryConfig.method === "email" ? 'text-emerald-700' : 'text-slate-700'}`}>Email</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryConfig({ ...deliveryConfig, method: "webhook" })}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          deliveryConfig.method === "webhook"
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid="method-webhook"
                      >
                        <Globe className={`h-6 w-6 mx-auto mb-2 ${deliveryConfig.method === "webhook" ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <div className={`font-medium ${deliveryConfig.method === "webhook" ? 'text-emerald-700' : 'text-slate-700'}`}>Webhook</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryConfig({ ...deliveryConfig, method: "api" })}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          deliveryConfig.method === "api"
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid="method-api"
                      >
                        <Server className={`h-6 w-6 mx-auto mb-2 ${deliveryConfig.method === "api" ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <div className={`font-medium ${deliveryConfig.method === "api" ? 'text-emerald-700' : 'text-slate-700'}`}>API</div>
                      </button>
                    </div>
                  </div>

                  {/* Email Configuration */}
                  {deliveryConfig.method === "email" && (
                    <div>
                      <Label className="text-slate-700 font-medium">Email Address</Label>
                      <Input
                        type="email"
                        value={deliveryConfig.emailAddress}
                        onChange={(e) => setDeliveryConfig({ ...deliveryConfig, emailAddress: e.target.value })}
                        placeholder="delivery@yourorg.com"
                        className="h-12 mt-1.5"
                        data-testid="email-input"
                      />
                    </div>
                  )}

                  {/* Webhook Configuration */}
                  {deliveryConfig.method === "webhook" && (
                    <div>
                      <Label className="text-slate-700 font-medium">Webhook URL</Label>
                      <Input
                        type="url"
                        value={deliveryConfig.webhookUrl}
                        onChange={(e) => setDeliveryConfig({ ...deliveryConfig, webhookUrl: e.target.value })}
                        placeholder="https://your-webhook.com/endpoint"
                        className="h-12 mt-1.5"
                        data-testid="webhook-input"
                      />
                    </div>
                  )}

                  {/* API Configuration */}
                  {deliveryConfig.method === "api" && (
                    <>
                      <div>
                        <Label className="text-slate-700 font-medium">API URL</Label>
                        <Input
                          type="url"
                          value={deliveryConfig.apiUrl}
                          onChange={(e) => setDeliveryConfig({ ...deliveryConfig, apiUrl: e.target.value })}
                          placeholder="https://api.yourorg.com/conversions"
                          className="h-12 mt-1.5"
                          data-testid="api-url-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 font-medium">API Key</Label>
                        <Input
                          type="password"
                          value={deliveryConfig.apiKey}
                          onChange={(e) => setDeliveryConfig({ ...deliveryConfig, apiKey: e.target.value })}
                          placeholder="Enter API key (leave blank to keep existing)"
                          className="h-12 mt-1.5"
                          data-testid="api-key-input"
                        />
                      </div>
                    </>
                  )}

                  {/* Active Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div>
                      <div className="font-medium text-slate-900">Enable Delivery</div>
                      <div className="text-sm text-slate-500">Automatically deliver conversions when saved</div>
                    </div>
                    <Switch
                      checked={deliveryConfig.active}
                      onCheckedChange={(checked) => setDeliveryConfig({ ...deliveryConfig, active: checked })}
                      data-testid="active-toggle"
                    />
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={saveDeliveryConfig}
                    disabled={savingConfig}
                    className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                    data-testid="save-config-btn"
                  >
                    {savingConfig ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      "Save Configuration"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Retention Upload Tab */}
          <TabsContent value="retention" className="space-y-6 mt-6">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Retention Upload</h2>
                    <p className="text-sm text-slate-500">Upload CSV to update retention/cancellation status</p>
                  </div>
                </div>

                {/* CSV Format Info */}
                <div className="bg-slate-50 rounded-xl p-4 mb-6">
                  <h3 className="font-medium text-slate-700 mb-2">CSV Format</h3>
                  <div className="font-mono text-sm bg-white p-3 rounded-lg border border-slate-200">
                    <div className="text-slate-500">trackingId,status</div>
                    <div>tt_ab12_cd34_1234,active</div>
                    <div>tt_ab12_cd34_5678,cancelled</div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Status values: <span className="font-medium text-emerald-600">active</span> or <span className="font-medium text-red-600">cancelled</span>
                  </p>
                </div>

                {/* File Upload */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700 font-medium">Select CSV File</Label>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => {
                          setRetentionFile(e.target.files?.[0] || null);
                          setRetentionResult(null);
                        }}
                        className="block w-full text-sm text-slate-500
                          file:mr-4 file:py-3 file:px-4
                          file:rounded-xl file:border-0
                          file:text-sm file:font-medium
                          file:bg-slate-100 file:text-slate-700
                          hover:file:bg-slate-200
                          cursor-pointer"
                        data-testid="retention-file-input"
                      />
                    </div>
                    {retentionFile && (
                      <p className="text-sm text-slate-600 mt-2">
                        Selected: <span className="font-medium">{retentionFile.name}</span>
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={handleRetentionUpload}
                    disabled={!retentionFile || uploadingRetention}
                    className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white font-semibold"
                    data-testid="upload-retention-btn"
                  >
                    {uploadingRetention ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Upload className="h-5 w-5" />
                        Upload and Process
                      </span>
                    )}
                  </Button>
                </div>

                {/* Results */}
                {retentionResult && !retentionResult.error && (
                  <div className="mt-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200" data-testid="retention-result">
                    <h3 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5" />
                      Upload Complete
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="font-heading font-black text-2xl text-slate-900">{retentionResult.processed}</div>
                        <div className="text-xs text-slate-500">Processed</div>
                      </div>
                      <div>
                        <div className="font-heading font-black text-2xl text-emerald-600">{retentionResult.updated}</div>
                        <div className="text-xs text-slate-500">Updated</div>
                      </div>
                      <div>
                        <div className="font-heading font-black text-2xl text-red-600">{retentionResult.failed}</div>
                        <div className="text-xs text-slate-500">Failed</div>
                      </div>
                    </div>
                    {retentionResult.failedDetails && retentionResult.failedDetails.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Failed Items:</h4>
                        <div className="max-h-32 overflow-y-auto text-xs bg-white rounded-lg p-2 border border-slate-200">
                          {retentionResult.failedDetails.slice(0, 10).map((item, idx) => (
                            <div key={item.trackingId || `fail-${idx}`} className="flex justify-between py-1 border-b border-slate-100 last:border-0">
                              <span className="font-mono text-slate-600">{item.trackingId || 'N/A'}</span>
                              <span className="text-red-600">{item.reason}</span>
                            </div>
                          ))}
                          {retentionResult.failedDetails.length > 10 && (
                            <div className="text-slate-500 pt-1">... and {retentionResult.failedDetails.length - 10} more</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {retentionResult?.error && (
                  <div className="mt-6 p-4 bg-red-50 rounded-xl border border-red-200" data-testid="retention-error">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">{retentionResult.error}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Current Retention Metrics */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">Current Retention Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="font-heading font-black text-2xl text-emerald-600">
                      {metrics?.activeCount || 0}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Active</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="font-heading font-black text-2xl text-red-600">
                      {metrics?.cancelledCount || 0}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Cancelled</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="font-heading font-black text-2xl text-blue-600">
                      {((metrics?.avgRetention || 0) * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Retention Rate</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="font-heading font-black text-2xl text-slate-900">
                      {metrics?.totalSignups || 0}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Total Signups</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Signups Tab */}
          <TabsContent value="signups" className="space-y-6 mt-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4" data-testid="signup-summary">
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4 text-center">
                  <div className="font-heading font-black text-2xl text-slate-900">{signupSummary.totalSignups || 0}</div>
                  <div className="text-xs text-slate-500 mt-1">Total Signups</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-none" data-testid="kpi-gift-aid-eligible">
                <CardContent className="p-4 text-center">
                  <div className="font-heading font-black text-2xl text-emerald-700">{signups.filter(s => s.giftAid).length}</div>
                  <div className="text-xs text-slate-500 mt-1">Gift Aid Eligible</div>
                  <div className="text-[10px] text-amber-600 mt-0.5" data-testid="kpi-gift-aid-value">
                    Est. £{Math.round(signups.filter(s => s.giftAid).reduce((sum, s) => {
                      const amt = Number(s.amount || 0);
                      const base = (s.conversionType === "regular_giving") ? amt * 12 : amt;
                      return sum + base * 0.25;
                    }, 0)).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4 text-center">
                  <div className="font-heading font-black text-2xl text-emerald-600">{signupSummary.deliveredCount || 0}</div>
                  <div className="text-xs text-slate-500 mt-1">Delivered</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4 text-center">
                  <div className="font-heading font-black text-2xl text-red-600">{signupSummary.failedCount || 0}</div>
                  <div className="text-xs text-slate-500 mt-1">Failed</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4 text-center">
                  <div className="font-heading font-black text-2xl text-amber-600">{signupSummary.pendingCount || 0}</div>
                  <div className="text-xs text-slate-500 mt-1">Pending</div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-none">
                <CardContent className="p-4 text-center">
                  <div className="font-heading font-black text-2xl text-blue-600">{signupSummary.successRate || 0}%</div>
                  <div className="text-xs text-slate-500 mt-1">Success Rate</div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Filters</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <Select value={signupFilters.campaignId || "all"} onValueChange={(v) => setSignupFilters({ ...signupFilters, campaignId: v === "all" ? "" : v })}>
                    <SelectTrigger className="h-10" data-testid="filter-campaign">
                      <SelectValue placeholder="All Campaigns" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Campaigns</SelectItem>
                      {campaigns.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={signupFilters.teamLeaderId || "all"} onValueChange={(v) => setSignupFilters({ ...signupFilters, teamLeaderId: v === "all" ? "" : v })}>
                    <SelectTrigger className="h-10" data-testid="filter-team-leader">
                      <SelectValue placeholder="All Teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Teams</SelectItem>
                      {getTeamLeaders().map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name} (Team)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={signupFilters.fundraiserId || "all"} onValueChange={(v) => setSignupFilters({ ...signupFilters, fundraiserId: v === "all" ? "" : v })}>
                    <SelectTrigger className="h-10" data-testid="filter-fundraiser">
                      <SelectValue placeholder="All Fundraisers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Fundraisers</SelectItem>
                      {fundraisers.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={signupFilters.deliveryStatus || "all"} onValueChange={(v) => setSignupFilters({ ...signupFilters, deliveryStatus: v === "all" ? "" : v })}>
                    <SelectTrigger className="h-10" data-testid="filter-status">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={loadSignups}
                    className="h-10"
                    data-testid="refresh-signups"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingSignups ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                {/* Date Range Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1">From Date</Label>
                    <Input
                      type="date"
                      value={signupFilters.dateFrom}
                      onChange={(e) => setSignupFilters({ ...signupFilters, dateFrom: e.target.value })}
                      className="h-10"
                      data-testid="filter-date-from"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1">To Date</Label>
                    <Input
                      type="date"
                      value={signupFilters.dateTo}
                      onChange={(e) => setSignupFilters({ ...signupFilters, dateTo: e.target.value })}
                      className="h-10"
                      data-testid="filter-date-to"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      onClick={() => setSignupFilters({
                        campaignId: "",
                        fundraiserId: "",
                        teamLeaderId: "",
                        deliveryStatus: "",
                        dateFrom: "",
                        dateTo: ""
                      })}
                      className="h-10"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
                {/* Performance Export Button */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowPerformanceExportModal(true)}
                    className="h-10"
                    data-testid="performance-export-btn"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Performance Export
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Period exports + printable summary */}
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="signups-export-bar">
              <span className="text-xs text-slate-500 mr-1">Export</span>
              <Button size="sm" variant="outline" onClick={() => exportSignupsByPeriod("today")} data-testid="signups-export-today">Today</Button>
              <Button size="sm" variant="outline" onClick={() => exportSignupsByPeriod("week")} data-testid="signups-export-week">This Week</Button>
              <Button size="sm" variant="outline" onClick={() => exportSignupsByPeriod("month")} data-testid="signups-export-month">This Month</Button>
              <Button size="sm" variant="outline" onClick={() => exportSignupsByPeriod("custom")} data-testid="signups-export-custom">Custom range</Button>
              <div className="h-5 w-px bg-slate-200 mx-1" />
              <Button size="sm" variant="outline" onClick={printSignupsSummary} data-testid="signups-print-summary">
                <Printer className="h-3.5 w-3.5 mr-1.5" />
                Printable summary
              </Button>
            </div>

            {/* Export & Selection Controls */}
            {selectedSignups.length > 0 && (
              <div className="mb-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedSignups.length} signup{selectedSignups.length > 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleExportSelectedSignups}
                      className="h-9 bg-blue-500 hover:bg-blue-600 text-white"
                      data-testid="export-selected-signups"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export Selected
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedSignups([])}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Signups Table */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-0">
                {loadingSignups ? (
                  <div className="p-8 text-center text-slate-500">
                    <div className="animate-pulse">Loading signups...</div>
                  </div>
                ) : signups.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table data-testid="signups-table">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-12">
                            <input
                              type="checkbox"
                              checked={selectedSignups.length === signups.length && signups.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSignups(signups.map(s => s.id));
                                } else {
                                  setSelectedSignups([]);
                                }
                              }}
                              className="h-4 w-4 rounded border-slate-300"
                            />
                          </TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold">Fundraiser</TableHead>
                          <TableHead className="font-semibold">Campaign</TableHead>
                          <TableHead className="font-semibold">Type</TableHead>
                          <TableHead className="font-semibold text-right">Amount</TableHead>
                          <TableHead className="font-semibold">Tracking ID</TableHead>
                          <TableHead className="font-semibold">Gift Aid</TableHead>
                          <TableHead className="font-semibold">Submission Status</TableHead>
                          <TableHead className="font-semibold">Handoff Route</TableHead>
                          <TableHead className="font-semibold text-right">Attempts</TableHead>
                          <TableHead className="font-semibold">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {signups.map((signup) => (
                          <TableRow 
                            key={signup.id} 
                            data-testid={`signup-row-${signup.id}`}
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={(e) => {
                              // Don't open detail if clicking checkbox or button
                              if (e.target.type === 'checkbox' || e.target.closest('button')) return;
                              viewSignupDetail(signup);
                            }}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedSignups.includes(signup.id)}
                                onChange={() => toggleSignupSelection(signup.id)}
                                className="h-4 w-4 rounded border-slate-300"
                              />
                            </TableCell>
                            <TableCell className="text-sm">
                              {new Date(signup.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="font-medium">{signup.fundraiserName}</TableCell>
                            <TableCell>{signup.campaignName}</TableCell>
                            <TableCell className="capitalize">{signup.conversionType?.replace("_", " ")}</TableCell>
                            <TableCell className="text-right">
                              {signup.amount ? `£${signup.amount}` : '-'}
                            </TableCell>
                            <TableCell className="font-mono text-xs">{signup.trackingId}</TableCell>
                            <TableCell data-testid={`gift-aid-cell-${signup.id}`}>
                              {signup.giftAid ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Yes</span>
                              ) : (
                                <span className="text-slate-400 text-xs">No</span>
                              )}
                            </TableCell>
                            <TableCell data-testid={`payment-status-cell-${signup.id}`}>
                              {signup.paymentStatus ? (
                                <span className="text-xs capitalize text-slate-700">{String(signup.paymentStatus).replace(/_/g, ' ')}</span>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getStatusIcon(signup.deliveryStatus)}
                                {getStatusBadge(signup.deliveryStatus)}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{signup.deliveryAttempts}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {signup.deliveryStatus !== "sent" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => retryDelivery(signup.id)}
                                    disabled={retryingId === signup.id}
                                    className="h-8 px-3"
                                    data-testid={`retry-btn-${signup.id}`}
                                  >
                                    {retryingId === signup.id ? (
                                      <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewTimeline(signup.id)}
                                  className="h-8 px-3 text-slate-500 hover:text-slate-900"
                                  data-testid={`timeline-btn-${signup.id}`}
                                  title="View Timeline"
                                >
                                  <Clock className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No signups found</p>
                    <p className="text-sm mt-1">Adjust filters or wait for new conversions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Billing Tab */}
          <TabsContent value="charity-billing" className="space-y-6 mt-6">
            <AgencyBillingPanel />
          </TabsContent>

          <TabsContent value="billing" className="space-y-6 mt-6">
            {/* System diagnostics + send-test */}
            <AdminDiagnosticsCard />

            {/* Failed-Payment Recovery — scope-aware */}
            <FailedPaymentsPanel />

            {/* Billing Overview Card */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold text-lg text-slate-900">Billing</h2>
                      <p className="text-sm text-slate-500">Current usage and projected charges</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { loadPricingPlans(); setShowUpgradeModal(true); }}
                      className="gap-1.5"
                      data-testid="change-plan-btn"
                    >
                      <TrendingUp className="h-4 w-4" />
                      Change plan
                    </Button>
                    {!billingData && !loadingBilling && (
                      <Button variant="outline" size="sm" onClick={loadBilling} data-testid="load-billing-btn">
                        Load Billing
                      </Button>
                    )}
                  </div>
                </div>

                {loadingBilling ? (
                  <div className="text-center py-12">
                    <div className="h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-slate-500 mt-2">Calculating...</p>
                  </div>
                ) : billingData ? (
                  <div className="space-y-6" data-testid="billing-panel">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
                        <p className="text-sm text-emerald-700 font-medium mb-1">Monthly Total</p>
                        <p className="text-3xl font-bold text-emerald-800" data-testid="projected-total">
                          £{billingData.totalAmount?.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center">
                        <p className="text-sm text-slate-600 font-medium mb-1">Active Users</p>
                        <p className="text-3xl font-bold text-slate-800" data-testid="current-usage">
                          {billingData.activeUserCount ?? 0}
                          <span className="text-base font-normal text-slate-500"> / {billingData.includedUsers ?? 0} included</span>
                        </p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center">
                        <p className="text-sm text-slate-600 font-medium mb-1">Next Billing Date</p>
                        <p className="text-xl font-bold text-slate-800" data-testid="next-billing-date">
                          {billingData.nextBillingDate ? new Date(billingData.nextBillingDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                      <div className="flex items-center justify-between px-5 py-3">
                        <span className="text-sm text-slate-600">Base Subscription ({billingData.plan})</span>
                        <span className="font-medium">£{billingData.basePlanPrice?.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between px-5 py-3">
                        <span className="text-sm text-slate-600">Included Users</span>
                        <span className="font-medium">{billingData.includedUsers ?? 0}</span>
                      </div>
                      {(billingData.extraUsers > 0) && (
                        <div className="flex items-center justify-between px-5 py-3">
                          <span className="text-sm text-slate-600">Extra Users ({billingData.extraUsers} x £{billingData.extraUserPrice?.toFixed(2)})</span>
                          <span className="font-medium text-amber-600">£{billingData.extraUserCharge?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between px-5 py-3 bg-slate-50">
                        <span className="text-sm font-semibold text-slate-900">Total</span>
                        <span className="font-bold text-lg text-slate-900">£{billingData.totalAmount?.toFixed(2)}</span>
                      </div>
                    </div>

                    {billingData.history && billingData.history.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-3">Billing History</h3>
                        <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                          {billingData.history.map((rec) => (
                            <div key={rec.id} className="flex items-center justify-between px-5 py-3" data-testid={`billing-record-${rec.id}`}>
                              <div>
                                <span className="text-sm font-medium text-slate-800">
                                  {new Date(rec.periodStart).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-medium">£{rec.total?.toFixed(2)}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  rec.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                                  rec.status === "failed" ? "bg-red-100 text-red-700" :
                                  rec.status === "pending" ? "bg-amber-100 text-amber-700" :
                                  "bg-slate-100 text-slate-600"
                                }`}>
                                  {rec.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Click "Load Billing" to see your projected charges</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Usage Summary Card */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Usage Summary</h2>
                    <p className="text-sm text-slate-500">Signup-based usage for current billing period</p>
                  </div>
                </div>
                {loadingUsage ? (
                  <div className="text-center py-8 text-slate-400"><RefreshCw className="h-5 w-5 animate-spin inline mr-2" />Loading usage data...</div>
                ) : usageData ? (
                  <div data-testid="usage-summary">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-slate-500 mb-1">Total Signups</p>
                        <p className="text-2xl font-bold text-slate-800" data-testid="usage-total-signups">{usageData.totalSignups}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-emerald-600 mb-1">Included</p>
                        <p className="text-2xl font-bold text-emerald-700" data-testid="usage-included">{usageData.includedSignups}</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-amber-600 mb-1">Billable</p>
                        <p className="text-2xl font-bold text-amber-700" data-testid="usage-billable">{usageData.billableSignups}</p>
                      </div>
                    </div>
                    <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                      <div className="flex items-center justify-between px-5 py-3">
                        <span className="text-sm text-slate-600">Plan</span>
                        <span className="font-medium text-slate-800">{usageData.planName}</span>
                      </div>
                      <div className="flex items-center justify-between px-5 py-3">
                        <span className="text-sm text-slate-600">Base Price</span>
                        <span className="font-medium">£{usageData.basePrice?.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between px-5 py-3">
                        <span className="text-sm text-slate-600">Price per Signup</span>
                        <span className="font-medium" data-testid="usage-price-per-signup">£{usageData.pricePerSignup?.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between px-5 py-3">
                        <span className="text-sm text-slate-600">Usage Charge ({usageData.billableSignups} x £{usageData.pricePerSignup?.toFixed(2)})</span>
                        <span className="font-medium text-amber-600" data-testid="usage-charge">£{usageData.usageCharge?.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between px-5 py-3 bg-slate-50">
                        <span className="text-sm font-semibold text-slate-900">Estimated Monthly Total</span>
                        <span className="font-bold text-lg text-emerald-700" data-testid="usage-total-estimated">£{usageData.totalEstimated?.toFixed(2)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Period: {usageData.periodStart} to {usageData.periodEnd}</p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-6">Usage data unavailable</p>
                )}
              </CardContent>
            </Card>

            {/* Manual Charge Section — MOVED to Platform Dashboard (platform_owner only) */}

            {/* Early Access Pricing Lock-In */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Lock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Secure Your Early Access Pricing</h2>
                    <p className="text-sm text-slate-500">Lock in your current rate before prices increase</p>
                  </div>
                </div>

                {lockStatus?.pricing_locked ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6" data-testid="pricing-locked-banner">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                      <h3 className="font-bold text-emerald-800 text-lg">Pricing Locked</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Locked Price</p>
                        <p className="text-xl font-bold text-emerald-800">£{Number(lockStatus.locked_price || 0).toFixed(2)}/mo</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Lock Type</p>
                        <p className="text-lg font-semibold text-emerald-800">
                          {lockStatus.lock_type === "monthly" ? "Monthly" : lockStatus.lock_type === "6_month" ? "6 Months" : "12 Months"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">
                          {lockStatus.lock_expiry_date ? "Expires" : "Billing Cycle"}
                        </p>
                        <p className="text-lg font-semibold text-emerald-800">
                          {lockStatus.lock_expiry_date
                            ? new Date(lockStatus.lock_expiry_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                            : lockStatus.billing_cycle === "monthly" ? "Monthly" : "Prepaid"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : selectedLockPlan ? (
                  <div className="border border-amber-300 bg-amber-50 rounded-xl p-5" data-testid="lock-confirm-panel">
                    <div className="flex items-start gap-3 mb-4">
                      <Lock className="h-5 w-5 text-amber-700 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-amber-900">Confirm your selection</h3>
                        <p className="text-sm text-amber-800 mt-1">
                          {selectedLockPlan === "monthly" && "Monthly — no discount, no commitment."}
                          {selectedLockPlan === "6_month" && `6 Months Upfront — 10% discount, £${billingData?.basePlanPrice ? (billingData.basePlanPrice * 6 * 0.9).toFixed(2) : "—"} total.`}
                          {selectedLockPlan === "12_month" && `12 Months Upfront — 20% discount, £${billingData?.basePlanPrice ? (billingData.basePlanPrice * 12 * 0.8).toFixed(2) : "—"} total.`}
                        </p>
                        <p className="text-xs text-amber-700 mt-2">
                          You will be redirected to Stripe to complete payment. You can change your selection before proceeding.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedLockPlan(null)}
                        disabled={lockingPricing}
                        data-testid="lock-back-btn"
                      >
                        ← Back to pricing options
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedLockPlan(null)}
                        disabled={lockingPricing}
                        data-testid="lock-change-btn"
                      >
                        Change selection
                      </Button>
                      <Button
                        onClick={() => lockPricing(selectedLockPlan)}
                        disabled={lockingPricing}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
                        data-testid="lock-confirm-btn"
                      >
                        {lockingPricing ? "Processing..." : "Continue to payment"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-lock-options">
                    {/* Monthly */}
                    <div className="border border-slate-200 rounded-xl p-5 hover:border-slate-400 transition-colors">
                      <div className="text-center mb-4">
                        <h3 className="font-bold text-slate-900 text-lg">Monthly</h3>
                        <p className="text-xs text-slate-500 mt-1">No discount</p>
                        <p className="text-2xl font-bold text-slate-800 mt-3">
                          £{billingData?.basePlanPrice?.toFixed(2) || "—"}<span className="text-sm font-normal text-slate-500">/mo</span>
                        </p>
                      </div>
                      <Button
                        onClick={() => setSelectedLockPlan("monthly")}
                        disabled={lockingPricing}
                        variant="outline"
                        className="w-full"
                        data-testid="lock-monthly-btn"
                      >
                        Lock Monthly Price
                      </Button>
                    </div>
                    {/* 6 Months */}
                    <div className="border-2 border-blue-300 rounded-xl p-5 bg-blue-50/50 relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-0.5 rounded-full">
                        SAVE 10%
                      </div>
                      <div className="text-center mb-4">
                        <h3 className="font-bold text-slate-900 text-lg">6 Months Upfront</h3>
                        <p className="text-xs text-slate-500 mt-1">10% discount</p>
                        <p className="text-2xl font-bold text-blue-700 mt-3">
                          £{billingData?.basePlanPrice ? (billingData.basePlanPrice * 6 * 0.9).toFixed(2) : "—"}
                          <span className="text-sm font-normal text-slate-500"> total</span>
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          £{billingData?.basePlanPrice ? (billingData.basePlanPrice * 0.9).toFixed(2) : "—"}/mo effective
                        </p>
                      </div>
                      <Button
                        onClick={() => setSelectedLockPlan("6_month")}
                        disabled={lockingPricing}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="lock-6month-btn"
                      >
                        Select 6 Months
                      </Button>
                    </div>
                    {/* 12 Months */}
                    <div className="border-2 border-emerald-300 rounded-xl p-5 bg-emerald-50/50 relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-bold px-3 py-0.5 rounded-full">
                        SAVE 20%
                      </div>
                      <div className="text-center mb-4">
                        <h3 className="font-bold text-slate-900 text-lg">12 Months Upfront</h3>
                        <p className="text-xs text-slate-500 mt-1">20% discount</p>
                        <p className="text-2xl font-bold text-emerald-700 mt-3">
                          £{billingData?.basePlanPrice ? (billingData.basePlanPrice * 12 * 0.8).toFixed(2) : "—"}
                          <span className="text-sm font-normal text-slate-500"> total</span>
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          £{billingData?.basePlanPrice ? (billingData.basePlanPrice * 0.8).toFixed(2) : "—"}/mo effective
                        </p>
                      </div>
                      <Button
                        onClick={() => setSelectedLockPlan("12_month")}
                        disabled={lockingPricing}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="lock-12month-btn"
                      >
                        Select 12 Months
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Flagged Submissions Tab */}
          <TabsContent value="flagged" className="space-y-6 mt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-800">Flagged Submissions</h2>
              <p className="text-sm text-slate-500">Conversions flagged for age review</p>
            </div>

            {/* Date Filter */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label className="text-slate-700 text-sm">Start Date</Label>
                    <Input
                      type="date"
                      value={flaggedDateFilter.startDate}
                      onChange={(e) => setFlaggedDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                      className="mt-1 w-40"
                      data-testid="flagged-start-date"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm">End Date</Label>
                    <Input
                      type="date"
                      value={flaggedDateFilter.endDate}
                      onChange={(e) => setFlaggedDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                      className="mt-1 w-40"
                      data-testid="flagged-end-date"
                    />
                  </div>
                  <Button 
                    onClick={fetchFlaggedSubmissions}
                    disabled={loadingFlagged}
                    variant="outline"
                    className="gap-2"
                    data-testid="flagged-filter-btn"
                  >
                    <Filter className="h-4 w-4" />
                    Apply Filter
                  </Button>
                  <Button 
                    onClick={exportFlaggedSubmissions}
                    disabled={exportingFlagged || flaggedSubmissions.length === 0}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="flagged-export-btn"
                  >
                    <Download className="h-4 w-4" />
                    {exportingFlagged ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Flagged Submissions Table */}
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {loadingFlagged ? (
                  <div className="flex justify-center items-center py-12">
                    <RefreshCw className="h-6 w-6 text-slate-400 animate-spin" />
                  </div>
                ) : flaggedSubmissions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold text-slate-700">Name</TableHead>
                          <TableHead className="font-semibold text-slate-700">Age</TableHead>
                          <TableHead className="font-semibold text-slate-700">Campaign</TableHead>
                          <TableHead className="font-semibold text-slate-700">Fundraiser</TableHead>
                          <TableHead className="font-semibold text-slate-700">Flag Reason</TableHead>
                          <TableHead className="font-semibold text-slate-700">Type</TableHead>
                          <TableHead className="font-semibold text-slate-700">Amount</TableHead>
                          <TableHead className="font-semibold text-slate-700">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {flaggedSubmissions.map((submission) => (
                          <TableRow key={submission.id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">
                              {submission.firstName} {submission.lastName}
                            </TableCell>
                            <TableCell>
                              <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                                {submission.age}
                              </span>
                            </TableCell>
                            <TableCell className="text-slate-600">{submission.campaignName}</TableCell>
                            <TableCell className="text-slate-600">{submission.fundraiserName}</TableCell>
                            <TableCell>
                              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                {submission.flagReason}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="capitalize text-slate-600">
                                {submission.conversionType?.replace(/_/g, ' ')}
                              </span>
                            </TableCell>
                            <TableCell className="text-slate-600">
                              {submission.amount ? `£${submission.amount.toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm">
                              {submission.createdAt ? new Date(submission.createdAt).toLocaleDateString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <AlertCircle className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p>No flagged submissions found</p>
                    <p className="text-sm mt-1">Submissions with ages outside campaign range will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab — Organisation User Management */}
          <TabsContent value="users" className="space-y-6 mt-6" data-testid="users-tab-content">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold text-lg text-slate-900">User Management</h2>
                      <p className="text-sm text-slate-500">All users in your organisation</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Activity Window Setting */}
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 whitespace-nowrap">Active Window</Label>
                      <Select value={String(activityWindowMinutes)} onValueChange={(v) => saveActivityWindow(Number(v))}>
                        <SelectTrigger className="w-[90px] h-8 text-xs" data-testid="activity-window-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5 min</SelectItem>
                          <SelectItem value="15">15 min</SelectItem>
                          <SelectItem value="30">30 min</SelectItem>
                          <SelectItem value="60">60 min</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Auto Sync Interval */}
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 whitespace-nowrap">Auto Sync</Label>
                      <Select value={syncIntervalMinutes ? String(syncIntervalMinutes) : "off"} onValueChange={saveSyncInterval}>
                        <SelectTrigger className="w-[100px] h-8 text-xs" data-testid="sync-interval-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off">Off</SelectItem>
                          <SelectItem value="15">15 mins</SelectItem>
                          <SelectItem value="30">30 mins</SelectItem>
                          <SelectItem value="60">60 mins</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={() => { setCreatedCredentials(null); setShowCreateFundraiserModal(true); }}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="create-fundraiser-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create Fundraiser
                    </Button>
                  </div>
                </div>

                {loadingOrgUsers ? (
                  <div className="py-8 text-center text-slate-500">Loading users...</div>
                ) : orgUsers.length === 0 ? (
                  <div className="py-8 text-center text-slate-400">No users in this organisation</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead className="text-slate-600 font-semibold">Name</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Email</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Role</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Status</TableHead>
                        <TableHead className="text-slate-600 font-semibold">Last Login</TableHead>
                        <TableHead className="text-slate-600 font-semibold text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orgUsers.map((u) => (
                        <TableRow key={u.id} data-testid={`org-user-row-${u.id}`}>
                          <TableCell>
                            <div className="relative group" title={getActivityTooltip(u.lastActiveAt)}>
                              <span className={`inline-block w-2.5 h-2.5 rounded-full ${isUserActive(u.lastActiveAt) ? 'bg-emerald-500' : 'bg-slate-300'}`} data-testid={`activity-dot-${u.id}`} />
                              <span className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                {getActivityTooltip(u.lastActiveAt)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">{u.name || '—'}</TableCell>
                          <TableCell className="text-slate-600">{u.email}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              u.role === 'owner' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            }`}>
                              {u.role === 'owner' ? 'Owner' : 'Fundraiser'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {u.isDeactivated ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                <XCircle className="h-3 w-3" /> Inactive
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="h-3 w-3" /> Active
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never'}
                          </TableCell>
                          <TableCell className="text-right">
                            {u.id !== user?.id && (
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-blue-600"
                                  onClick={() => handleOrgResetPassword(u.id, u.email)}
                                  disabled={orgUserActionLoading === u.id} title="Send Reset Link"
                                  data-testid={`org-reset-pwd-${u.id}`}>
                                  <Key className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-orange-600"
                                  onClick={() => {
                                    setSetPasswordTarget({ id: u.id, email: u.email, name: u.name });
                                    setNewPasswordForm({ password: "", confirm: "" });
                                    setShowSetPasswordModal(true);
                                  }}
                                  title="Set Password Manually"
                                  data-testid={`org-set-pwd-${u.id}`}>
                                  <Lock className="h-4 w-4" />
                                </Button>
                                {u.isDeactivated ? (
                                  <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-emerald-600"
                                    onClick={() => handleOrgActivateUser(u.id, u.email)}
                                    disabled={orgUserActionLoading === u.id} title="Activate"
                                    data-testid={`org-activate-${u.id}`}>
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-amber-600"
                                    onClick={() => handleOrgDeactivateUser(u.id, u.email)}
                                    disabled={orgUserActionLoading === u.id} title="Deactivate"
                                    data-testid={`org-deactivate-${u.id}`}>
                                    <UserMinus className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-red-600"
                                  onClick={() => handleOrgDeleteUser(u.id, u.email)}
                                  disabled={orgUserActionLoading === u.id} title="Delete"
                                  data-testid={`org-delete-${u.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Landing Page Tab — hidden until the feature is ready. Retained
              as an empty tab so existing tab lists and data-testids continue
              to resolve; content is intentionally minimal. */}
          <TabsContent value="landing" className="space-y-6 mt-6" data-testid="landing-tab-content">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-10 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Globe className="h-8 w-8 text-slate-400" />
                </div>
                <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Landing Page Builder</h2>
                <p className="text-slate-500 max-w-md mx-auto">
                  Branded landing pages are not part of the current release.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Activity Log
                </CardTitle>
                <p className="text-sm text-slate-500">Financial, billing, and communication events</p>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <Select value={eventFilters.source || "all"} onValueChange={(v) => setEventFilters({...eventFilters, source: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-[140px]" data-testid="activity-source-filter">
                      <SelectValue placeholder="Source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="resend">Resend</SelectItem>
                      <SelectItem value="twilio">Twilio</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={eventFilters.status || "all"} onValueChange={(v) => setEventFilters({...eventFilters, status: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-[130px]" data-testid="activity-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={eventFilters.dateFrom}
                    onChange={(e) => setEventFilters({...eventFilters, dateFrom: e.target.value})}
                    className="w-[160px]"
                    data-testid="activity-date-from"
                  />
                  <Input
                    type="date"
                    value={eventFilters.dateTo}
                    onChange={(e) => setEventFilters({...eventFilters, dateTo: e.target.value})}
                    className="w-[160px]"
                    data-testid="activity-date-to"
                  />
                  <Button variant="outline" size="sm" onClick={loadEvents} data-testid="activity-refresh-btn">
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                </div>

                {/* Table */}
                {loadingEvents ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : activityEvents.length === 0 ? (
                  <div className="text-center py-12 text-slate-400" data-testid="activity-empty">
                    <p>No activity events found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="activity-table">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="pb-2 pr-4">Time</th>
                          <th className="pb-2 pr-4">Source</th>
                          <th className="pb-2 pr-4">Event Type</th>
                          <th className="pb-2 pr-4">Status</th>
                          <th className="pb-2 pr-4">User</th>
                          <th className="pb-2 pr-4">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityEvents.map((evt) => (
                          <tr key={evt.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">
                              {new Date(evt.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </td>
                            <td className="py-2.5 pr-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                evt.source === 'stripe' ? 'bg-purple-100 text-purple-700' :
                                evt.source === 'billing' ? 'bg-amber-100 text-amber-700' :
                                evt.source === 'resend' ? 'bg-blue-100 text-blue-700' :
                                evt.source === 'twilio' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {evt.source || 'system'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-4 text-slate-900 font-medium">{evt.type}</td>
                            <td className="py-2.5 pr-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                evt.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {evt.status || 'unknown'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-4 text-slate-500 text-xs">
                              {evt.userId ? evt.userId.substring(0, 8) + '...' : '—'}
                            </td>
                            <td className="py-2.5 pr-4 text-slate-600">
                              {evt.amount != null ? `£${Number(evt.amount).toFixed(2)}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Log Tab */}
          <TabsContent value="systemlog" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  System Log
                </CardTitle>
                <p className="text-sm text-slate-500">System and operations events</p>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <Select value={eventFilters.source || "all"} onValueChange={(v) => setEventFilters({...eventFilters, source: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-[140px]" data-testid="syslog-source-filter">
                      <SelectValue placeholder="Source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="resend">Resend</SelectItem>
                      <SelectItem value="twilio">Twilio</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={eventFilters.status || "all"} onValueChange={(v) => setEventFilters({...eventFilters, status: v === "all" ? "" : v})}>
                    <SelectTrigger className="w-[130px]" data-testid="syslog-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={eventFilters.dateFrom}
                    onChange={(e) => setEventFilters({...eventFilters, dateFrom: e.target.value})}
                    className="w-[160px]"
                    data-testid="syslog-date-from"
                  />
                  <Input
                    type="date"
                    value={eventFilters.dateTo}
                    onChange={(e) => setEventFilters({...eventFilters, dateTo: e.target.value})}
                    className="w-[160px]"
                    data-testid="syslog-date-to"
                  />
                  <Button variant="outline" size="sm" onClick={loadEvents} data-testid="syslog-refresh-btn">
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                </div>

                {/* Table */}
                {loadingEvents ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : systemEvents.length === 0 ? (
                  <div className="text-center py-12 text-slate-400" data-testid="syslog-empty">
                    <p>No system events found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="syslog-table">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="pb-2 pr-4">Time</th>
                          <th className="pb-2 pr-4">Event Type</th>
                          <th className="pb-2 pr-4">Source</th>
                          <th className="pb-2 pr-4">Status</th>
                          <th className="pb-2 pr-4">User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {systemEvents.map((evt) => (
                          <tr key={evt.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">
                              {new Date(evt.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </td>
                            <td className="py-2.5 pr-4 text-slate-900 font-medium">{evt.type}</td>
                            <td className="py-2.5 pr-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                evt.source === 'stripe' ? 'bg-purple-100 text-purple-700' :
                                evt.source === 'billing' ? 'bg-amber-100 text-amber-700' :
                                evt.source === 'resend' ? 'bg-blue-100 text-blue-700' :
                                evt.source === 'twilio' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {evt.source || 'system'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                evt.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {evt.status || 'unknown'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-4 text-slate-500 text-xs">
                              {evt.userId ? evt.userId.substring(0, 8) + '...' : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Earnings Tab (DERIVED — no storage) */}
          <TabsContent value="earnings" className="space-y-6 mt-6">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold text-lg text-slate-900">Earnings</h2>
                      <p className="text-sm text-slate-500">Derived from conversions, bonds, and cancellations</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (earningsFilter.campaignId) params.append("campaign_id", earningsFilter.campaignId);
                        const res = await apiClient.get(`/earnings/export/csv?${params}`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement("a"); a.href = url; a.download = "earnings.csv"; a.click();
                      } catch (e) { toast.error("Export failed"); }
                    }} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1" />CSV</Button>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (earningsFilter.campaignId) params.append("campaign_id", earningsFilter.campaignId);
                        const res = await apiClient.get(`/earnings/export/json?${params}`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement("a"); a.href = url; a.download = "earnings.json"; a.click();
                      } catch (e) { toast.error("Export failed"); }
                    }} data-testid="export-json-btn"><Download className="h-4 w-4 mr-1" />JSON</Button>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (earningsFilter.campaignId) params.append("campaign_id", earningsFilter.campaignId);
                        const res = await apiClient.get(`/earnings/export/pdf?${params}`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
                        window.open(url, "_blank");
                      } catch (e) { toast.error("Export failed"); }
                    }} data-testid="export-pdf-btn"><Download className="h-4 w-4 mr-1" />PDF</Button>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {campaigns && campaigns.length > 0 && (
                    <Select value={earningsFilter.campaignId || "all"} onValueChange={(v) => setEarningsFilter({...earningsFilter, campaignId: v === "all" ? "" : v})}>
                      <SelectTrigger className="w-[200px]" data-testid="earnings-campaign-filter">
                        <SelectValue placeholder="All Campaigns" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Campaigns</SelectItem>
                        {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Button variant="outline" size="sm" onClick={loadEarnings} data-testid="refresh-earnings-btn">
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                </div>

                {loadingEarnings ? (
                  <div className="flex items-center justify-center py-12"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : earningsData ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="earnings-summary">
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-slate-500">Total Signups</p>
                        <p className="text-2xl font-bold text-slate-800">{earningsData.totals.signups}</p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-emerald-600">Active</p>
                        <p className="text-2xl font-bold text-emerald-700">{earningsData.totals.active}</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-red-600">Cancelled</p>
                        <p className="text-2xl font-bold text-red-700">{earningsData.totals.cancelled}</p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-blue-600">Gross</p>
                        <p className="text-2xl font-bold text-blue-700">£{earningsData.totals.gross.toFixed(2)}</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-amber-600">Net</p>
                        <p className="text-2xl font-bold text-amber-700">£{earningsData.totals.net.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Fundraiser Summary Table */}
                    {earningsData.fundraiserSummary?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-slate-800 mb-3">Per Fundraiser</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid="earnings-fundraiser-table">
                            <thead>
                              <tr className="border-b text-left text-slate-500">
                                <th className="pb-2 pr-4">Fundraiser</th>
                                <th className="pb-2 pr-4">Signups</th>
                                <th className="pb-2 pr-4">Active</th>
                                <th className="pb-2 pr-4">Cancelled</th>
                                <th className="pb-2 pr-4">Gross</th>
                                <th className="pb-2 pr-4">Bond</th>
                                <th className="pb-2 pr-4">Net</th>
                                <th className="pb-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {earningsData.fundraiserSummary.map(fs => (
                                <tr key={fs.fundraiserId} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="py-2.5 pr-4 font-medium text-slate-900">{fs.fundraiserName || "Unknown"}</td>
                                  <td className="py-2.5 pr-4">{fs.signups}</td>
                                  <td className="py-2.5 pr-4 text-emerald-600">{fs.active}</td>
                                  <td className="py-2.5 pr-4 text-red-600">{fs.cancelled}</td>
                                  <td className="py-2.5 pr-4">£{fs.gross.toFixed(2)}</td>
                                  <td className="py-2.5 pr-4 text-amber-600">£{fs.bond.toFixed(2)}</td>
                                  <td className="py-2.5 pr-4 font-semibold">£{fs.net.toFixed(2)}</td>
                                  <td className="py-2.5">
                                    <Button variant="ghost" size="sm" onClick={() => setEarningsDrilldown(earningsDrilldown === fs.fundraiserId ? null : fs.fundraiserId)} data-testid={`drilldown-${fs.fundraiserId}`}>
                                      <ChevronDown className={`h-4 w-4 transition-transform ${earningsDrilldown === fs.fundraiserId ? "rotate-180" : ""}`} />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Drill-down */}
                    {earningsDrilldown && earningsData.records && (
                      <div className="border border-slate-200 rounded-xl p-4" data-testid="earnings-drilldown">
                        <h4 className="font-semibold text-slate-800 mb-3">Sign-up Detail</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-left text-slate-500">
                                <th className="pb-2 pr-3">Engage Tracking ID</th>
                                <th className="pb-2 pr-3">Date</th>
                                <th className="pb-2 pr-3">Type</th>
                                <th className="pb-2 pr-3">Status</th>
                                <th className="pb-2 pr-3">Gross</th>
                                <th className="pb-2 pr-3">Bond</th>
                                <th className="pb-2">Net</th>
                              </tr>
                            </thead>
                            <tbody>
                              {earningsData.records.filter(r => r.fundraiserId === earningsDrilldown).map(r => (
                                <tr key={r.conversionId} className="border-b border-slate-100">
                                  <td className="py-2 pr-3 font-mono text-xs">{r.taptractId}</td>
                                  <td className="py-2 pr-3 text-slate-600">{r.date ? new Date(r.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}</td>
                                  <td className="py-2 pr-3">{r.type}</td>
                                  <td className="py-2 pr-3">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.status}</span>
                                  </td>
                                  <td className="py-2 pr-3">£{r.gross.toFixed(2)}</td>
                                  <td className="py-2 pr-3 text-amber-600">£{r.bond.toFixed(2)}</td>
                                  <td className="py-2 font-semibold">£{r.net.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400"><p>No earnings data</p></div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cancellations Tab */}
          <TabsContent value="cancellations" className="space-y-6 mt-6">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <X className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Cancellations</h2>
                    <p className="text-sm text-slate-500">Record and manage cancellations</p>
                  </div>
                </div>

                {/* Record single cancellation */}
                <div className="flex flex-wrap items-end gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-slate-700 text-sm">Engage Tracking ID</Label>
                    <Input placeholder="TT-CAMP-abc123" value={cancelForm.taptrack_id} onChange={(e) => setCancelForm({...cancelForm, taptrack_id: e.target.value})} className="mt-1" data-testid="cancel-taptrack-input" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-slate-700 text-sm">Reason</Label>
                    <Input placeholder="Optional" value={cancelForm.reason} onChange={(e) => setCancelForm({...cancelForm, reason: e.target.value})} className="mt-1" data-testid="cancel-reason-input" />
                  </div>
                  <Button onClick={recordCancellation} disabled={submittingCancel} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="record-cancel-btn">
                    <X className="h-4 w-4" />{submittingCancel ? "Recording..." : "Record Cancellation"}
                  </Button>
                </div>

                {/* CSV Upload */}
                <div className="flex items-center gap-4 mb-6">
                  <Label htmlFor="csv-upload" className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                      <Upload className="h-4 w-4 text-slate-500" />
                      <span className="text-sm text-slate-600">Upload CSV (taptrack_id, reason)</span>
                    </div>
                  </Label>
                  <input id="csv-upload" type="file" accept=".csv" onChange={uploadCancellationCSV} className="hidden" data-testid="csv-upload-input" />
                </div>

                {/* Cancellations List */}
                {loadingCancellations ? (
                  <div className="flex items-center justify-center py-8"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : cancellations.length === 0 ? (
                  <div className="text-center py-8 text-slate-400" data-testid="no-cancellations"><p>No cancellations recorded</p></div>
                ) : (
                  <div className="overflow-x-auto" data-testid="cancellations-table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="pb-2 pr-4">Engage Tracking ID</th>
                          <th className="pb-2 pr-4">Date</th>
                          <th className="pb-2 pr-4">Reason</th>
                          <th className="pb-2 pr-4">Status</th>
                          <th className="pb-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {cancellations.map(c => (
                          <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2.5 pr-4 font-mono text-xs">{c.taptractId}</td>
                            <td className="py-2.5 pr-4 text-slate-600">{new Date(c.timestamp).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</td>
                            <td className="py-2.5 pr-4 text-slate-600">{c.reason || "—"}</td>
                            <td className="py-2.5 pr-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === "active" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{c.status}</span>
                            </td>
                            <td className="py-2.5">
                              {c.status === "active" && (
                                <Button variant="ghost" size="sm" onClick={() => reverseCancellation(c.taptractId)} className="text-blue-600 text-xs" data-testid={`reverse-${c.taptractId}`}>
                                  <RotateCcw className="h-3 w-3 mr-1" /> Reverse
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Access Tab */}
          <TabsContent value="api-access" className="space-y-6 mt-6">
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Key className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Data Access / Integrations</h2>
                    <p className="text-sm text-slate-500">Read-only API for earnings data</p>
                  </div>
                </div>

                {apiSettings ? (
                  <div className="space-y-6" data-testid="api-settings-panel">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-600 font-medium">API Status:</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${apiSettings.api_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {apiSettings.api_enabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>

                    {apiSettings.api_key_set && (
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-sm text-slate-600 mb-1">Current Key Prefix</p>
                        <p className="font-mono text-sm text-slate-800" data-testid="api-key-prefix">{apiSettings.api_key_prefix}</p>
                        {apiSettings.api_last_used && <p className="text-xs text-slate-400 mt-1">Last used: {new Date(apiSettings.api_last_used).toLocaleString("en-GB")}</p>}
                      </div>
                    )}

                    {newApiKey && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="new-api-key-display">
                        <p className="text-sm font-medium text-amber-800 mb-2">Copy your API key now — it won't be shown again:</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 p-2 bg-white border rounded text-xs font-mono break-all">{newApiKey}</code>
                          <Button variant="outline" size="sm" onClick={async () => { const ok = await safeCopyToClipboard(newApiKey); toast.success(ok ? "Copied" : "Copy failed — select manually"); }} data-testid="copy-api-key-btn">
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      {!apiSettings.api_key_set ? (
                        <Button onClick={generateApiKey} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="generate-api-key-btn">
                          <Key className="h-4 w-4" /> Generate API Key
                        </Button>
                      ) : (
                        <>
                          <Button onClick={generateApiKey} variant="outline" className="gap-2" data-testid="regenerate-api-key-btn">
                            <RefreshCw className="h-4 w-4" /> Regenerate
                          </Button>
                          <Button onClick={revokeApiKey} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-2" data-testid="revoke-api-key-btn">
                            <X className="h-4 w-4" /> Revoke
                          </Button>
                        </>
                      )}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl">
                      <h3 className="font-semibold text-slate-800 mb-2">Endpoint</h3>
                      <code className="text-xs font-mono text-slate-600">GET {process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/org-api/earnings</code>
                      <p className="text-xs text-slate-400 mt-2">Auth: Bearer [your-api-key]</p>
                      <p className="text-xs text-slate-400">Query params: campaign_id, fundraiser_id, date_from, date_to</p>
                      <p className="text-xs text-slate-400">Returns: totals (gross, bond, net, signups) + records per signup</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400"><p>Loading API settings...</p></div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduling Tab */}
          <TabsContent value="scheduling" className="space-y-6 mt-6">
            {/* Quarter Overview */}
            {quarterOverview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quarter-overview">
                <Card className="shadow-none">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-slate-500">Available Days</p>
                    <p className="text-2xl font-bold text-emerald-700">{quarterOverview.totalAvailableDays}</p>
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-slate-500">Assigned Days</p>
                    <p className="text-2xl font-bold text-blue-700">{quarterOverview.totalAssignedDays}</p>
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-slate-500">Gaps</p>
                    <p className="text-2xl font-bold text-amber-700">{quarterOverview.gaps}</p>
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-slate-500">Utilisation</p>
                    <p className="text-2xl font-bold text-slate-800">{quarterOverview.utilisation}%</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Create Assignment */}
            {!isStandardManager && (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-lg text-slate-900">Create Assignment</h2>
                    <p className="text-sm text-slate-500">Assign fundraisers or teams to campaigns, locations & dates</p>
                  </div>
                </div>

                {/* Assignment Mode Toggle */}
                <div className="flex gap-2 mb-4" data-testid="assign-mode-toggle">
                  <Button
                    variant={assignForm.assign_mode === "individual" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAssignForm({...assignForm, assign_mode: "individual", team_id: "", fundraiser_id: ""})}
                    data-testid="assign-mode-individual"
                  >
                    Individual Fundraiser
                  </Button>
                  <Button
                    variant={assignForm.assign_mode === "team" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAssignForm({...assignForm, assign_mode: "team", team_id: "", fundraiser_id: ""})}
                    data-testid="assign-mode-team"
                  >
                    <Users className="h-4 w-4 mr-1" />Full Team
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Campaign Selector (required) */}
                  {campaigns && (
                    <div>
                      <Label className="text-slate-700 text-sm">Campaign <span className="text-red-500">*</span></Label>
                      <Select value={assignForm.campaign_id || ""} onValueChange={(v) => setAssignForm({...assignForm, campaign_id: v})}>
                        <SelectTrigger className="mt-1" data-testid="assign-campaign-select">
                          <SelectValue placeholder="Select campaign" />
                        </SelectTrigger>
                        <SelectContent>
                          {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Location Selector (required) */}
                  <div>
                    <Label className="text-slate-700 text-sm">Location <span className="text-red-500">*</span></Label>
                    <Select value={assignForm.location_id || ""} onValueChange={(v) => setAssignForm({...assignForm, location_id: v})}>
                      <SelectTrigger className="mt-1" data-testid="assign-location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                        {locations.length === 0 && (
                          <div className="px-3 py-2 text-sm text-slate-400">No locations available</div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Fundraiser OR Team Selector */}
                  {assignForm.assign_mode === "individual" ? (
                    teamAvailability?.fundraisers && (
                      <div>
                        <Label className="text-slate-700 text-sm">Fundraiser <span className="text-red-500">*</span></Label>
                        <Select value={assignForm.fundraiser_id || ""} onValueChange={(v) => setAssignForm({...assignForm, fundraiser_id: v})}>
                          <SelectTrigger className="mt-1" data-testid="assign-fundraiser-select">
                            <SelectValue placeholder="Select fundraiser" />
                          </SelectTrigger>
                          <SelectContent>
                            {teamAvailability.fundraisers.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )
                  ) : (
                    <div>
                      <Label className="text-slate-700 text-sm">Team (Leader) <span className="text-red-500">*</span></Label>
                      <Select value={assignForm.team_id || ""} onValueChange={(v) => setAssignForm({...assignForm, team_id: v})}>
                        <SelectTrigger className="mt-1" data-testid="assign-team-select">
                          <SelectValue placeholder="Select team leader" />
                        </SelectTrigger>
                        <SelectContent>
                          {getTeamLeaders().map(l => <SelectItem key={l.id} value={l.id}>{l.name}'s Team</SelectItem>)}
                          {getTeamLeaders().length === 0 && (
                            <div className="px-3 py-2 text-sm text-slate-400">No team leaders found</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Date */}
                  <div>
                    <Label className="text-slate-700 text-sm">Date <span className="text-red-500">*</span></Label>
                    <Input type="date" value={assignForm.date} onChange={(e) => setAssignForm({...assignForm, date: e.target.value})} className="mt-1" data-testid="assign-date-input" />
                  </div>
                </div>

                <Button onClick={createAssignment} disabled={assigningShift} className="bg-blue-600 hover:bg-blue-700 text-white gap-2 w-full sm:w-auto" data-testid="create-assignment-btn">
                  <Plus className="h-4 w-4" />{assigningShift ? "Creating..." : assignForm.assign_mode === "team" ? "Assign Team" : "Assign"}
                </Button>
              </CardContent>
            </Card>
            )}

            {/* Team Availability Calendar */}
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">Team Availability & Assignments</h3>
                {loadingTeamAvail ? (
                  <div className="flex items-center justify-center py-12"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : teamAvailability ? (
                  <div className="overflow-x-auto" data-testid="team-availability-table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="pb-2 pr-4">Fundraiser</th>
                          <th className="pb-2 pr-4">Available Days</th>
                          <th className="pb-2 pr-4">Assigned Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamAvailability.fundraisers.map(f => {
                          const availCount = teamAvailability.availability.filter(a => a.fundraiserId === f.id && a.available).length;
                          const assignCount = teamAvailability.assignments.filter(a => a.fundraiserId === f.id).length;
                          return (
                            <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2.5 pr-4 font-medium text-slate-900">{f.name}</td>
                              <td className="py-2.5 pr-4 text-emerald-600">{availCount}</td>
                              <td className="py-2.5 pr-4 text-blue-600">{assignCount}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400"><p>No scheduling data</p></div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== ACTIVITY LOCATIONS TAB ==================== */}
          <TabsContent value="activity-locations" className="space-y-6 mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Activity Locations</CardTitle>
                <p className="text-sm text-slate-500">All logged fundraiser activity with location/coordinate data</p>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="activity-locations-filters">
                  <Select value={actLocFilters.eventType || "all"} onValueChange={(v) => { const nf = { ...actLocFilters, eventType: v === "all" ? "" : v }; setActLocFilters(nf); loadActivityLocations(nf); }}>
                    <SelectTrigger data-testid="activity-loc-filter-type"><SelectValue placeholder="Event Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="sync">Sync</SelectItem>
                      <SelectItem value="conversion">Conversion</SelectItem>
                      <SelectItem value="tracker">Tracker</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={actLocFilters.fundraiserId || "all"} onValueChange={(v) => { const nf = { ...actLocFilters, fundraiserId: v === "all" ? "" : v }; setActLocFilters(nf); loadActivityLocations(nf); }}>
                    <SelectTrigger data-testid="activity-loc-filter-fundraiser"><SelectValue placeholder="Fundraiser" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Fundraisers</SelectItem>
                      {(activityLocationsData?.filters?.fundraisers || fundraisers).map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name || f.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={actLocFilters.campaignId || "all"} onValueChange={(v) => { const nf = { ...actLocFilters, campaignId: v === "all" ? "" : v }; setActLocFilters(nf); loadActivityLocations(nf); }}>
                    <SelectTrigger data-testid="activity-loc-filter-campaign"><SelectValue placeholder="Campaign" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Campaigns</SelectItem>
                      {campaigns.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="date" value={actLocFilters.dateFrom} onChange={(e) => { const nf = { ...actLocFilters, dateFrom: e.target.value }; setActLocFilters(nf); loadActivityLocations(nf); }} placeholder="From" data-testid="activity-loc-filter-from" />
                  <Input type="date" value={actLocFilters.dateTo} onChange={(e) => { const nf = { ...actLocFilters, dateTo: e.target.value }; setActLocFilters(nf); loadActivityLocations(nf); }} placeholder="To" data-testid="activity-loc-filter-to" />
                </div>

                {loadingActivityLocations ? (
                  <div className="text-center py-12 text-slate-400">Loading activity locations...</div>
                ) : !activityLocationsData?.entries?.length ? (
                  <div className="text-center py-12 text-slate-400"><p>No activity location data found</p></div>
                ) : (
                  <>
                    <div className="text-sm text-slate-500 mb-3">{activityLocationsData.total} entries</div>
                    <div className="overflow-x-auto" data-testid="activity-locations-table">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-left">
                            <th className="pb-3 pr-4 font-medium text-slate-600">Location</th>
                            <th className="pb-3 pr-4 font-medium text-slate-600">Type</th>
                            <th className="pb-3 pr-4 font-medium text-slate-600">Fundraiser</th>
                            <th className="pb-3 pr-4 font-medium text-slate-600">Campaign</th>
                            <th className="pb-3 pr-4 font-medium text-slate-600">Date/Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityLocationsData.entries.map((entry, idx) => (
                            <tr key={`actloc-${idx}-${entry.timestamp}`} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 pr-4">
                                {entry.locationName ? (
                                  <div>
                                    <span className="font-medium text-slate-900">{entry.locationName}</span>
                                    {entry.locationAddress && <span className="block text-xs text-slate-400">{entry.locationAddress}</span>}
                                  </div>
                                ) : entry.latitude && entry.longitude ? (
                                  <span className="font-mono text-xs text-slate-600">{Number(entry.latitude).toFixed(5)}, {Number(entry.longitude).toFixed(5)}</span>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>
                              <td className="py-3 pr-4">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  entry.eventType === "sync" ? "bg-blue-100 text-blue-700" :
                                  entry.eventType === "conversion" ? "bg-emerald-100 text-emerald-700" :
                                  "bg-amber-100 text-amber-700"
                                }`}>{entry.eventType}</span>
                              </td>
                              <td className="py-3 pr-4 text-slate-700">{entry.fundraiserName || "-"}</td>
                              <td className="py-3 pr-4 text-slate-600">{entry.campaignName || "-"}</td>
                              <td className="py-3 pr-4 text-slate-500 text-xs">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Location Insights Panel */}
            <Card className="border-slate-200" data-testid="location-insights-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  Location Insights
                </CardTitle>
                <p className="text-sm text-slate-500">Select a location to view performance metrics</p>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Select value={insightsLocationId || ""} onValueChange={(v) => { setInsightsLocationId(v); loadLocationInsights(v); }}>
                    <SelectTrigger data-testid="insights-location-select"><SelectValue placeholder="Choose location..." /></SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}{loc.address ? ` — ${loc.address}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {loadingInsights && <div className="text-center py-8 text-slate-400">Loading insights...</div>}

                {locationInsights && !loadingInsights && (
                  <div className="space-y-6">
                    {/* Core Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="insights-core-metrics">
                      <div className="p-3 bg-slate-50 rounded-xl text-center">
                        <div className="text-2xl font-bold text-slate-900">{locationInsights.coreMetrics.totalVisits}</div>
                        <div className="text-xs text-slate-500">Total Visits</div>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-xl text-center">
                        <div className="text-2xl font-bold text-emerald-600">{locationInsights.coreMetrics.totalConversions}</div>
                        <div className="text-xs text-slate-500">Conversions</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-xl text-center">
                        <div className="text-2xl font-bold text-blue-600">{(locationInsights.coreMetrics.conversionRate * 100).toFixed(1)}%</div>
                        <div className="text-xs text-slate-500">Conv. Rate</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl text-center">
                        <div className="text-sm font-bold text-slate-900">{locationInsights.coreMetrics.lastActivity ? new Date(locationInsights.coreMetrics.lastActivity).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</div>
                        <div className="text-xs text-slate-500">Last Activity</div>
                      </div>
                    </div>

                    {/* Best Days */}
                    <div data-testid="insights-best-days">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Best Days</h4>
                      <div className="grid grid-cols-7 gap-1">
                        {locationInsights.bestDays.map(d => (
                          <div key={d.day} className={`p-2 rounded-lg text-center ${d.rate > 0.5 ? "bg-emerald-100" : d.rate > 0 ? "bg-blue-50" : "bg-slate-50"}`}>
                            <div className="text-[10px] font-medium text-slate-500">{d.day.slice(0, 3)}</div>
                            <div className={`text-sm font-bold ${d.rate > 0.5 ? "text-emerald-700" : d.rate > 0 ? "text-blue-700" : "text-slate-400"}`}>{d.visits > 0 ? `${(d.rate * 100).toFixed(0)}%` : "—"}</div>
                            <div className="text-[9px] text-slate-400">{d.visits}v / {d.conversions}c</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Time Bands */}
                    <div data-testid="insights-time-bands">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Time Bands</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {locationInsights.timeBands.map(tb => (
                          <div key={tb.band} className={`p-3 rounded-lg text-center ${tb.rate > 0.5 ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50"}`}>
                            <div className="text-xs font-medium text-slate-600 mb-1">{tb.band}</div>
                            <div className={`text-lg font-bold ${tb.rate > 0.5 ? "text-emerald-700" : "text-slate-800"}`}>{tb.visits > 0 ? `${(tb.rate * 100).toFixed(0)}%` : "—"}</div>
                            <div className="text-[10px] text-slate-400">{tb.visits} visits, {tb.conversions} conv.</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent Activity */}
                    {locationInsights.recentActivity.length > 0 && (
                      <div data-testid="insights-recent-activity">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Recent Activity</h4>
                        <div className="max-h-60 overflow-y-auto space-y-1">
                          {locationInsights.recentActivity.map(a => (
                            <div key={a.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium text-slate-800 truncate">{a.road ? `${a.road} ${a.houseNumber || ""}` : "—"}</span>
                                {a.outcome && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    a.outcome === "converted" ? "bg-emerald-100 text-emerald-700" :
                                    a.outcome === "callback" ? "bg-amber-100 text-amber-700" :
                                    a.outcome === "not_interested" ? "bg-red-100 text-red-700" :
                                    a.outcome === "no_answer" ? "bg-slate-200 text-slate-600" :
                                    "bg-blue-100 text-blue-700"
                                  }`}>{a.outcome.replace("_", " ")}</span>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0 ml-2">
                                <div className="text-xs text-slate-500">{a.fundraiserName}</div>
                                <div className="text-[10px] text-slate-400">{a.createdAt ? new Date(a.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Deployment Guidance */}
                    {locationInsights.guidance && (
                      <div data-testid="insights-guidance">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Deployment Guidance</h4>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="p-3 bg-emerald-50 rounded-xl text-center border border-emerald-100">
                            <div className="text-[10px] font-semibold text-emerald-600 mb-1 uppercase">Best Day</div>
                            <div className="text-sm font-bold text-emerald-800">{locationInsights.guidance.bestDayToDeploy || "—"}</div>
                          </div>
                          <div className="p-3 bg-blue-50 rounded-xl text-center border border-blue-100">
                            <div className="text-[10px] font-semibold text-blue-600 mb-1 uppercase">Best Time</div>
                            <div className="text-sm font-bold text-blue-800">{locationInsights.guidance.bestTimeWindow || "—"}</div>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-xl text-center border border-slate-200">
                            <div className="text-[10px] font-semibold text-slate-500 mb-1 uppercase">Next Visit</div>
                            <div className="text-sm font-bold text-slate-800">{locationInsights.guidance.suggestedNextVisit ? new Date(locationInsights.guidance.suggestedNextVisit).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Road Performance */}
                    {locationInsights.roadPerformance?.length > 0 && (
                      <div data-testid="insights-road-performance">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Road Performance</h4>
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                              <tr>
                                <th className="text-left pb-2 pr-2">Road</th>
                                <th className="text-center pb-2 px-2">Visits</th>
                                <th className="text-center pb-2 px-2">Conv.</th>
                                <th className="text-right pb-2 pl-2">Rate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {locationInsights.roadPerformance.map(rp => (
                                <tr key={rp.road} className="border-b border-slate-100 last:border-0">
                                  <td className="py-1.5 pr-2 font-medium text-slate-800 truncate max-w-[200px]">{rp.road}</td>
                                  <td className="py-1.5 px-2 text-center text-slate-600">{rp.visits}</td>
                                  <td className="py-1.5 px-2 text-center text-emerald-600 font-medium">{rp.conversions}</td>
                                  <td className="py-1.5 pl-2 text-right">
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${rp.rate > 0.5 ? "bg-emerald-100 text-emerald-700" : rp.rate > 0 ? "bg-blue-50 text-blue-700" : "text-slate-400"}`}>
                                      {(rp.rate * 100).toFixed(0)}%
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!insightsLocationId && !loadingInsights && (
                  <div className="text-center py-8 text-slate-400">Select a location above to see insights</div>
                )}
              </CardContent>
            </Card>

            {/* Fundraiser Trail Map (V1 — Leaflet + OpenStreetMap) */}
            <ActivityTrailMap fundraisers={activityLocationsData?.filters?.fundraisers || fundraisers} />

            {/* Location Profitability (V1) */}
            <LocationProfitability />

            {/* Territory Planning (V1) */}
            <TerritoryPlanning />
          </TabsContent>

        </Tabs>
      </main>

      {/* Invite Fundraiser Modal */}
      <Dialog open={showInviteModal} onOpenChange={(open) => {
        if (!open) {
          setInviteCredentials(null);
        }
        setShowInviteModal(open);
      }}>
        <DialogContent className="sm:max-w-md">
          {inviteCredentials ? (
            // Show credentials after successful invite
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="h-5 w-5" />
                  Fundraiser Invited!
                </DialogTitle>
                <DialogDescription>
                  Share these login credentials with {inviteCredentials.name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <div className="font-mono text-sm bg-white p-2 rounded border mt-1">
                      {inviteCredentials.email}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Temporary Password</Label>
                    <div className="font-mono text-sm bg-white p-2 rounded border mt-1">
                      {inviteCredentials.password}
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={async () => {
                    const ok = await safeCopyToClipboard(`Email: ${inviteCredentials.email}\nPassword: ${inviteCredentials.password}`);
                    toast.success(ok ? "Credentials copied to clipboard" : "Copy failed — select the credentials above manually");
                  }}
                  variant="outline"
                  className="w-full"
                  data-testid="copy-credentials-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Copy Credentials
                </Button>
                
                <p className="text-xs text-amber-600 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  The fundraiser should change this password after first login.
                </p>
              </div>
              
              <DialogFooter>
                <Button
                  onClick={() => {
                    setInviteCredentials(null);
                    setShowInviteModal(false);
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          ) : (
            // Show invite form
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-emerald-600" />
                  Invite Fundraiser
                </DialogTitle>
                <DialogDescription>
                  Enter the fundraiser's details to add them to your team.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">First Name *</Label>
                    <Input
                      value={inviteForm.firstName}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, firstName: e.target.value }))}
                      placeholder="John"
                      className="mt-1.5"
                      data-testid="invite-first-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Last Name *</Label>
                    <Input
                      value={inviteForm.lastName}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, lastName: e.target.value }))}
                      placeholder="Smith"
                      className="mt-1.5"
                      data-testid="invite-last-name"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-700">Email Address *</Label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    className="mt-1.5"
                    data-testid="invite-email"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">Profile Photo <span className="text-xs text-slate-400">(optional)</span></Label>
                  <div className="flex items-center gap-4 mt-1.5">
                    <div className="h-14 w-14 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="invite-photo-preview">
                      {inviteForm.profileImageUrl ? (
                        <img src={inviteForm.profileImageUrl.startsWith('/') ? authedFileUrl(inviteForm.profileImageUrl) : inviteForm.profileImageUrl} alt="Preview" className="h-full w-full object-cover" />
                      ) : (
                        <UserPlus className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Button type="button" variant="outline" size="sm" disabled={uploadingProfilePhoto}
                        onClick={() => document.getElementById('invite-photo-input').click()}
                        className="h-7 text-xs" data-testid="invite-photo-upload-btn">
                        {uploadingProfilePhoto ? "Uploading..." : inviteForm.profileImageUrl ? "Change" : "Upload"}
                      </Button>
                      {inviteForm.profileImageUrl && (
                        <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 px-2 text-xs"
                          onClick={() => setInviteForm(prev => ({ ...prev, profileImageUrl: "" }))}
                          data-testid="invite-photo-remove-btn">
                          <X className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                    <input id="invite-photo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleProfilePhotoUpload(file, (url) => setInviteForm(prev => ({ ...prev, profileImageUrl: url })));
                        e.target.value = "";
                      }} />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG, or WebP. Max 5MB.</p>
                </div>

                <div>
                  <Label className="text-slate-700">Assign to Campaigns</Label>
                  <p className="text-xs text-slate-500 mb-2">Select one or more campaigns (or leave empty for all)</p>
                  <div className="mt-1.5 border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
                    {campaigns.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500 text-center">No campaigns available</div>
                    ) : (
                      campaigns.map((c) => (
                        <label
                          key={c.id}
                          className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={inviteForm.campaignIds?.includes(c.id) || false}
                            onChange={() => toggleCampaignSelection(c.id)}
                            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            data-testid={`invite-campaign-checkbox-${c.id}`}
                          />
                          <span className="text-sm text-slate-700">{c.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {inviteForm.campaignIds?.length > 0 && (
                    <div className="mt-2 text-xs text-emerald-600">
                      {inviteForm.campaignIds.length} campaign{inviteForm.campaignIds.length > 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={inviteFundraiser}
                  disabled={inviting}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="invite-submit-btn"
                >
                  {inviting ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Inviting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Invite Fundraiser
                    </span>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Fundraiser Modal (from Users tab) */}
      <Dialog open={showCreateFundraiserModal} onOpenChange={(open) => {
        if (!open) setCreatedCredentials(null);
        setShowCreateFundraiserModal(open);
      }}>
        <DialogContent className="sm:max-w-md">
          {createdCredentials ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="h-5 w-5" />
                  {createdCredentials?.role === "manager" ? "Manager" : "Fundraiser"} Created!
                </DialogTitle>
                <DialogDescription>
                  Share these login credentials with {createdCredentials.name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <div className="font-mono text-sm bg-white p-2 rounded border mt-1">{createdCredentials.email}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Password</Label>
                    <div className="font-mono text-sm bg-white p-2 rounded border mt-1">{createdCredentials.password}</div>
                  </div>
                </div>
                <Button variant="outline" className="w-full" data-testid="copy-created-credentials-btn"
                  onClick={async () => {
                    const ok = await safeCopyToClipboard(`Email: ${createdCredentials.email}\nPassword: ${createdCredentials.password}`);
                    toast.success(ok ? "Credentials copied to clipboard" : "Copy failed — select the credentials above manually");
                  }}>
                  <Download className="h-4 w-4 mr-2" /> Copy Credentials
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => { setCreatedCredentials(null); setShowCreateFundraiserModal(false); }} className="bg-emerald-500 hover:bg-emerald-600">Done</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-emerald-600" />
                  Create Team Member
                </DialogTitle>
                <DialogDescription>Add a new fundraiser or manager to your organisation.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Role Selector */}
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={createFundraiserForm.createRole} onValueChange={(v) => setCreateFundraiserForm({ ...createFundraiserForm, createRole: v })}>
                    <SelectTrigger data-testid="create-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fundraiser">Fundraiser</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {createFundraiserForm.createRole === "manager" && (
                  <div className="space-y-2">
                    <Label>Manager Level</Label>
                    <Select value={createFundraiserForm.managerLevel} onValueChange={(v) => setCreateFundraiserForm({ ...createFundraiserForm, managerLevel: v })}>
                      <SelectTrigger data-testid="create-user-manager-level">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard (read-only)</SelectItem>
                        <SelectItem value="elevated">Elevated (can edit campaigns, assign)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input placeholder="e.g. Sarah Johnson" value={createFundraiserForm.name}
                    onChange={(e) => setCreateFundraiserForm({ ...createFundraiserForm, name: e.target.value })}
                    data-testid="create-fr-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" placeholder="e.g. sarah@example.com" value={createFundraiserForm.email}
                    onChange={(e) => setCreateFundraiserForm({ ...createFundraiserForm, email: e.target.value })}
                    data-testid="create-fr-email" />
                </div>
                <div className="space-y-2">
                  <Label>Password <span className="text-xs text-slate-400">(leave blank to auto-generate)</span></Label>
                  <Input type="text" placeholder="Auto-generated if empty" value={createFundraiserForm.password}
                    onChange={(e) => setCreateFundraiserForm({ ...createFundraiserForm, password: e.target.value })}
                    data-testid="create-fr-password" />
                </div>
                <div className="space-y-2">
                  <Label>Profile Photo <span className="text-xs text-slate-400">(optional)</span></Label>
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="create-fr-photo-preview">
                      {createFundraiserForm.profileImageUrl ? (
                        <img src={createFundraiserForm.profileImageUrl.startsWith('/') ? authedFileUrl(createFundraiserForm.profileImageUrl) : createFundraiserForm.profileImageUrl} alt="Preview" className="h-full w-full object-cover" />
                      ) : (
                        <UserPlus className="h-6 w-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button type="button" variant="outline" size="sm" disabled={uploadingProfilePhoto}
                        onClick={() => document.getElementById('create-fr-photo-input').click()}
                        data-testid="create-fr-photo-upload-btn">
                        {uploadingProfilePhoto ? "Uploading..." : createFundraiserForm.profileImageUrl ? "Change Photo" : "Upload Photo"}
                      </Button>
                      {createFundraiserForm.profileImageUrl && (
                        <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 px-2"
                          onClick={() => setCreateFundraiserForm({ ...createFundraiserForm, profileImageUrl: "" })}
                          data-testid="create-fr-photo-remove-btn">
                          <X className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                    <input id="create-fr-photo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleProfilePhotoUpload(file, (url) => setCreateFundraiserForm(prev => ({ ...prev, profileImageUrl: url })));
                        e.target.value = "";
                      }} />
                  </div>
                  <p className="text-xs text-slate-400">JPG, PNG, or WebP. Max 5MB.</p>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowCreateFundraiserModal(false)}>Cancel</Button>
                <Button onClick={handleCreateFundraiser} disabled={creatingFundraiser}
                  className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-fr-submit">
                  {creatingFundraiser ? "Creating..." : createFundraiserForm.createRole === "manager" ? "Create Manager" : "Create Fundraiser"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Fundraiser Detail Panel */}
      <Dialog open={showDetailPanel} onOpenChange={setShowDetailPanel}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              {selectedFundraiserDetail?.name || "Fundraiser"} - Performance
            </DialogTitle>
          </DialogHeader>
          {selectedFundraiserDetail && (
            <div className="flex justify-end -mt-2 mb-2">
              <Button
                size="sm"
                variant="outline"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                onClick={() => {
                  setPerfFundraiserId(selectedFundraiserDetail.id);
                  setShowFundraiserPerf(true);
                }}
                data-testid="open-fundraiser-perf-btn"
              >
                Full Performance Report →
              </Button>
            </div>
          )}
          
          {selectedFundraiserDetail && (
            <div className="space-y-6 py-4">
              {/* Profile Photo */}
              <div className="flex items-center gap-4" data-testid="detail-panel-photo-section">
                <div className="h-20 w-20 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="detail-panel-photo-preview">
                  {selectedFundraiserDetail.profileImageUrl ? (
                    <img src={selectedFundraiserDetail.profileImageUrl.startsWith('/') ? authedFileUrl(selectedFundraiserDetail.profileImageUrl) : selectedFundraiserDetail.profileImageUrl} alt={selectedFundraiserDetail.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-slate-400">{selectedFundraiserDetail.name?.charAt(0)?.toUpperCase() || "?"}</span>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="font-heading font-bold text-lg text-slate-900">{selectedFundraiserDetail.name}</span>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={uploadingProfilePhoto}
                      onClick={() => document.getElementById('detail-panel-photo-input').click()}
                      className="h-7 text-xs" data-testid="detail-panel-photo-upload-btn">
                      <Upload className="h-3 w-3 mr-1" />
                      {uploadingProfilePhoto ? "Uploading..." : selectedFundraiserDetail.profileImageUrl ? "Change" : "Add Photo"}
                    </Button>
                    {selectedFundraiserDetail.profileImageUrl && (
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 text-xs px-2"
                        onClick={async () => {
                          try {
                            await apiClient.put(`/team/${selectedFundraiserDetail.id}/profile-photo`, { profileImageUrl: null });
                            setSelectedFundraiserDetail(prev => ({ ...prev, profileImageUrl: null }));
                            loadTeamMembers();
                            toast.success("Photo removed");
                          } catch (e) { toast.error("Failed to remove photo"); }
                        }}
                        data-testid="detail-panel-photo-remove-btn">
                        <Trash2 className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <input id="detail-panel-photo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleProfilePhotoUpload(file, async (url) => {
                        try {
                          await apiClient.put(`/team/${selectedFundraiserDetail.id}/profile-photo`, { profileImageUrl: url });
                          setSelectedFundraiserDetail(prev => ({ ...prev, profileImageUrl: url }));
                          loadTeamMembers();
                          toast.success("Photo updated");
                        } catch (err) { toast.error("Failed to save photo"); }
                      });
                      e.target.value = "";
                    }} />
                </div>
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <div className="font-heading font-black text-xl text-slate-900">
                    {selectedFundraiserDetail.signups}
                  </div>
                  <div className="text-xs text-slate-500">Signups</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <div className={`font-heading font-black text-xl ${selectedFundraiserDetail.conversionRate >= 0.5 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {(selectedFundraiserDetail.conversionRate * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-slate-500">Conv. Rate</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <div className="font-heading font-black text-xl text-slate-900">
                    {(selectedFundraiserDetail.noShowRate * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-slate-500">No-Show</div>
                </div>
              </div>
              
              {/* Retention Metrics */}
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Retention</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">30-Day Cancel</span>
                      <span className={`font-bold ${selectedFundraiserDetail.cancellation30d > 0.2 ? 'text-red-600' : 'text-slate-900'}`}>
                        {(selectedFundraiserDetail.cancellation30d * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">90-Day Cancel</span>
                      <span className="font-bold text-slate-900">
                        {(selectedFundraiserDetail.cancellation90d * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quality Metrics */}
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Quality Scores</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-slate-50 rounded-xl flex justify-between items-center">
                    <span className="text-sm text-slate-500">Avg Donation</span>
                    <span className="font-bold text-slate-900">
                      £{selectedFundraiserDetail.avgDonation || 0}
                    </span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl flex justify-between items-center">
                    <span className="text-sm text-slate-500">Avg Age</span>
                    <span className="font-bold text-slate-900">
                      {selectedFundraiserDetail.avgAge || '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Interaction Metrics */}
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Performance Metrics</h4>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{selectedFundraiserDetail.totalInteractions || 0}</div>
                      <div className="text-xs text-slate-500">Total Interactions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-600">{(selectedFundraiserDetail.conversionRate * 100).toFixed(0)}%</div>
                      <div className="text-xs text-slate-500">Conversion Rate</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDetailPanel(false);
                    setSelectedFundraiserForGuidance(selectedFundraiserDetail.id);
                    setActiveTab("guidance");
                  }}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Guidance
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDetailPanel(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateCampaignModal} onOpenChange={(o) => { setShowCreateCampaignModal(o); if (o) loadCharityOptions(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-500" />
              Create New Campaign
            </DialogTitle>
            <DialogDescription>
              Create a new campaign to start collecting donations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700 font-medium">Campaign Name *</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Summer Fundraiser 2026"
                className="h-12 mt-1.5"
                data-testid="new-campaign-name"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Description</Label>
              <Textarea
                value={newCampaign.description}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the campaign..."
                className="mt-1.5"
                data-testid="new-campaign-description"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Charity Client</Label>
              <select
                value={newCampaign.charityId || ""}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, charityId: e.target.value }))}
                className="w-full h-12 mt-1.5 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="new-campaign-charity"
              >
                <option value="">No charity (internal)</option>
                {charityOptions.map((ch) => (
                  <option key={ch.id} value={ch.id}>{ch.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">Charities are visibility clients · manage them in Campaigns → Charities.</p>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <div className="font-medium text-slate-900">Active</div>
                <div className="text-sm text-slate-500">Campaign is visible and accepting donations</div>
              </div>
              <Switch
                checked={newCampaign.active}
                onCheckedChange={(v) => setNewCampaign(prev => ({ ...prev, active: v }))}
                data-testid="new-campaign-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateCampaignModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={createCampaign}
              disabled={creatingCampaign || !newCampaign.name.trim()}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="confirm-create-campaign"
            >
              {creatingCampaign ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Create Campaign
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Location Modal */}
      <Dialog open={showCreateLocationModal} onOpenChange={setShowCreateLocationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-500" />
              Create New Location
            </DialogTitle>
            <DialogDescription>
              Add a new location for fundraising activities
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700 font-medium">Location Name *</Label>
              <Input
                value={newLocation.name}
                onChange={(e) => setNewLocation(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Main Street Corner, Shopping Centre"
                className="h-12 mt-1.5"
                data-testid="new-location-name"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Address (Optional)</Label>
              <Input
                value={newLocation.address}
                onChange={(e) => setNewLocation(prev => ({ ...prev, address: e.target.value }))}
                placeholder="e.g., 123 High Street, London"
                className="h-12 mt-1.5"
                data-testid="new-location-address"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Assign to Campaign (Optional)</Label>
              <Select 
                value={newLocation.campaignId || "none"} 
                onValueChange={(v) => setNewLocation(prev => ({ ...prev, campaignId: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="h-12 mt-1.5" data-testid="new-location-campaign">
                  <SelectValue placeholder="All Campaigns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">All Campaigns</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Leave empty to make location available for all campaigns
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateLocationModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={createLocation}
              disabled={creatingLocation || !newLocation.name.trim()}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="confirm-create-location"
            >
              {creatingLocation ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Create Location
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Shift Modal */}
      <Dialog open={showAddShiftModal} onOpenChange={(open) => {
        setShowAddShiftModal(open);
        if (!open) {
          setEditingShift(null);
          setPendingEditMode(null);
          resetShiftForm();
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              {editingShift ? "Edit Shift" : "Add Shift"}
              {pendingEditMode === "series" && editingShift?.isRecurring && (
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Editing Series</span>
              )}
            </DialogTitle>
            <DialogDescription>
              {editingShift ? "Update shift details" : "Schedule fundraisers for a location and time"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Fundraiser Multi-Select */}
            <div>
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                Fundraisers
                <span className="text-xs font-normal text-slate-400">(leave empty for unassigned shift)</span>
              </Label>
              <div className="mt-2 border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto bg-slate-50/50">
                {teamMembers.length === 0 ? (
                  <p className="text-sm text-slate-400">No team members available</p>
                ) : (
                  <div className="space-y-2">
                    {(() => {
                      // Sort by suggestion score if available
                      const suggMap = {};
                      shiftSuggestions.forEach(s => { suggMap[s.fundraiserId] = s; });
                      const sorted = [...teamMembers].sort((a, b) => {
                        const sa = suggMap[a.id]?.score ?? 50;
                        const sb = suggMap[b.id]?.score ?? 50;
                        return sb - sa;
                      });
                      return sorted.map(m => {
                        const dayPref = shiftForm.date ? getUserDayPreference(m.id, shiftForm.date) : null;
                        const sugg = suggMap[m.id];
                        // OCI V2.39 — simple, honest preferred-area signal (case-insensitive text match; no maps)
                        const areas = Array.isArray(m.preferredOperatingAreas) ? m.preferredOperatingAreas : [];
                        const campName = (campaigns.find(c => c.id === shiftForm.campaignId)?.name || "").toLowerCase();
                        const locObj = locations.find(l => l.id === shiftForm.locationId) || {};
                        const locBlob = [locObj.name, locObj.address, locObj.town, locObj.city, locObj.region].filter(Boolean).join(" ").toLowerCase();
                        const blob = `${campName} ${locBlob}`;
                        const areaMatch = areas.length === 0 ? "none" : (areas.some(a => a && blob.includes(String(a).toLowerCase().trim())) ? "match" : "outside");
                        return (
                          <label
                            key={m.id}
                            className={`flex items-center gap-3 cursor-pointer hover:bg-white p-1.5 rounded transition-colors ${
                              sugg?.status === "conflict" ? "bg-red-50 border border-red-200 opacity-60" :
                              sugg?.status === "unavailable" ? "bg-red-50/50 border border-red-100 opacity-60" :
                              dayPref === "not_preferred" ? "bg-amber-50 border border-amber-200" : ""
                            }`}
                          >
                            <Checkbox
                              checked={shiftForm.userIds?.includes(m.id) || false}
                              onCheckedChange={() => toggleShiftUserSelection(m.id)}
                              data-testid={`shift-user-checkbox-${m.id}`}
                            />
                            <span className="text-sm text-slate-700">{m.name}</span>
                            {m.teamRole === "team_leader" && (
                              <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Leader</span>
                            )}
                            {(shiftForm.campaignId || shiftForm.locationId) && areaMatch === "match" && (
                              <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded" data-testid={`shift-area-match-${m.id}`}>Preferred area match</span>
                            )}
                            {(shiftForm.campaignId || shiftForm.locationId) && areaMatch === "outside" && (
                              <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded" data-testid={`shift-area-outside-${m.id}`}>Outside preferred area</span>
                            )}
                            {(shiftForm.campaignId || shiftForm.locationId) && areaMatch === "none" && (
                              <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded" data-testid={`shift-area-none-${m.id}`}>No preferred area</span>
                            )}
                            {sugg?.status === "ideal" && (
                              <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded flex items-center gap-0.5">
                                <CheckCircle2 className="h-3 w-3" />
                                Ideal
                              </span>
                            )}
                            {sugg?.status === "conflict" && (
                              <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded flex items-center gap-0.5">
                                <XCircle className="h-3 w-3" />
                                Has Shift
                              </span>
                            )}
                            {sugg?.status === "unavailable" && (
                              <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded">Unavailable</span>
                            )}
                            {!sugg && dayPref === "preferred" && (
                              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Preferred</span>
                            )}
                            {sugg?.status === "not_preferred" && (
                              <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded flex items-center gap-0.5">
                                <AlertTriangle className="h-3 w-3" />
                                Day Off
                              </span>
                            )}
                            {!sugg && dayPref === "not_preferred" && (
                              <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded flex items-center gap-0.5">
                                <AlertTriangle className="h-3 w-3" />
                                Day Off
                              </span>
                            )}
                          </label>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
              {shiftForm.userIds?.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">{shiftForm.userIds.length} selected</p>
              )}
              {shiftForm.userIds?.length === 0 && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <UserMinus className="h-3 w-3" />
                  This will create an unassigned shift
                </p>
              )}
            </div>
            
            {/* Location */}
            <div>
              <Label className="text-slate-700 font-medium">Location *</Label>
              <Select
                value={shiftForm.locationId}
                onValueChange={(v) => setShiftForm(prev => ({ ...prev, locationId: v }))}
              >
                <SelectTrigger className="h-12 mt-1.5" data-testid="shift-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Campaign */}
            <div>
              <Label className="text-slate-700 font-medium">Campaign (Optional)</Label>
              <Select
                value={shiftForm.campaignId || "none"}
                onValueChange={(v) => setShiftForm(prev => ({ ...prev, campaignId: v === "none" ? "" : v }))}
              >
                <SelectTrigger className="h-12 mt-1.5" data-testid="shift-campaign-select">
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific campaign</SelectItem>
                  {campaigns.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date */}
            <div>
              <Label className="text-slate-700 font-medium">Start Date *</Label>
              <Input
                type="date"
                value={shiftForm.date}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setShiftForm(prev => ({ ...prev, date: newDate }));
                  loadShiftSuggestions(newDate, shiftForm.locationId, shiftForm.campaignId);
                }}
                className="h-12 mt-1.5"
                data-testid="shift-date-input"
              />
            </div>

            {/* End Date (optional — creates a shift per day inclusive, skips duplicates) */}
            <div>
              <Label className="text-slate-700 font-medium">End Date <span className="text-xs text-slate-400">(optional — creates one shift per day inclusive)</span></Label>
              <Input
                type="date"
                value={shiftForm.endDate}
                min={shiftForm.date || undefined}
                onChange={(e) => setShiftForm(prev => ({ ...prev, endDate: e.target.value }))}
                className="h-12 mt-1.5"
                data-testid="shift-end-date-input"
                disabled={shiftForm.isRecurring}
              />
              {shiftForm.endDate && shiftForm.date && (
                <p className="text-xs text-blue-600 mt-1" data-testid="shift-range-preview">
                  Range: {shiftForm.date} → {shiftForm.endDate} ({Math.max(1, Math.floor((new Date(shiftForm.endDate) - new Date(shiftForm.date)) / 86400000) + 1)} days; duplicates auto-skipped)
                </p>
              )}
            </div>
            
            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-medium">Start Time *</Label>
                <Input
                  type="time"
                  value={shiftForm.startTime}
                  onChange={(e) => setShiftForm(prev => ({ ...prev, startTime: e.target.value }))}
                  className="h-12 mt-1.5"
                  data-testid="shift-start-time"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">End Time *</Label>
                <Input
                  type="time"
                  value={shiftForm.endTime}
                  onChange={(e) => setShiftForm(prev => ({ ...prev, endTime: e.target.value }))}
                  className="h-12 mt-1.5"
                  data-testid="shift-end-time"
                />
              </div>
            </div>
            
            {/* Recurrence Options (Only for new shifts) */}
            {!editingShift && (
              <div className="border border-slate-200 rounded-lg p-4 bg-blue-50/30">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-slate-700 font-medium flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-blue-500" />
                    Weekly Recurrence
                  </Label>
                  <Switch
                    checked={shiftForm.isRecurring}
                    onCheckedChange={(checked) => setShiftForm(prev => ({ ...prev, isRecurring: checked }))}
                    data-testid="shift-recurring-toggle"
                  />
                </div>
                {shiftForm.isRecurring && (
                  <div className="mt-3">
                    <Label className="text-slate-600 text-sm">Repeat for how many weeks?</Label>
                    <Select
                      value={String(shiftForm.repeatWeeks)}
                      onValueChange={(v) => setShiftForm(prev => ({ ...prev, repeatWeeks: parseInt(v) }))}
                    >
                      <SelectTrigger className="h-10 mt-1.5" data-testid="shift-repeat-weeks">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 weeks</SelectItem>
                        <SelectItem value="4">4 weeks</SelectItem>
                        <SelectItem value="6">6 weeks</SelectItem>
                        <SelectItem value="8">8 weeks</SelectItem>
                        <SelectItem value="12">12 weeks</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-2">
                      This will create {shiftForm.repeatWeeks} shifts on the same day of each week
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddShiftModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSaveShift(pendingEditMode || "single")}
              disabled={savingShift || !shiftForm.locationId || !shiftForm.date}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="save-shift-btn"
            >
              {savingShift ? "Saving..." : (
                editingShift 
                  ? (pendingEditMode === "series" ? "Update Series" : "Update Shift")
                  : (shiftForm.isRecurring ? `Create ${shiftForm.repeatWeeks} Shifts` : "Add Shift")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Mode Dialog (for recurring shifts) */}
      <Dialog open={showEditModeDialog} onOpenChange={setShowEditModeDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-blue-500" />
              Edit Recurring Shift
            </DialogTitle>
            <DialogDescription>
              This is a recurring shift. What would you like to edit?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button
              variant="outline"
              className="w-full h-14 justify-start"
              onClick={() => handleEditModeSelect("single")}
              data-testid="edit-single-btn"
            >
              <Calendar className="h-5 w-5 mr-3 text-slate-500" />
              <div className="text-left">
                <div className="font-medium">This shift only</div>
                <div className="text-xs text-slate-500">Change just this date</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full h-14 justify-start"
              onClick={() => handleEditModeSelect("series")}
              data-testid="edit-series-btn"
            >
              <Repeat className="h-5 w-5 mr-3 text-blue-500" />
              <div className="text-left">
                <div className="font-medium">All future shifts</div>
                <div className="text-xs text-slate-500">Update entire series from today</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Mode Dialog (for recurring shifts) */}
      <Dialog open={showDeleteModeDialog} onOpenChange={setShowDeleteModeDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Recurring Shift
            </DialogTitle>
            <DialogDescription>
              This is a recurring shift. What would you like to delete?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button
              variant="outline"
              className="w-full h-14 justify-start border-red-200 hover:bg-red-50"
              onClick={() => confirmDeleteShift(pendingDeleteShift?.id, "single")}
              data-testid="delete-single-btn"
            >
              <Calendar className="h-5 w-5 mr-3 text-slate-500" />
              <div className="text-left">
                <div className="font-medium">This shift only</div>
                <div className="text-xs text-slate-500">Remove just this date</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full h-14 justify-start border-red-300 hover:bg-red-50 text-red-700"
              onClick={() => confirmDeleteShift(pendingDeleteShift?.id, "series")}
              data-testid="delete-series-btn"
            >
              <Repeat className="h-5 w-5 mr-3 text-red-500" />
              <div className="text-left">
                <div className="font-medium">All future shifts</div>
                <div className="text-xs text-red-400">Delete entire series from today</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteModeDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preference Warning Dialog */}
      <Dialog open={showPreferenceWarning} onOpenChange={setShowPreferenceWarning}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Schedule Preference Conflict
            </DialogTitle>
            <DialogDescription>
              One or more assigned fundraisers have marked this day as not preferred.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                {checkPreferenceConflict(shiftForm.userIds, shiftForm.date)?.join(", ")} prefer not to work on this day.
              </p>
              <p className="text-xs text-amber-600 mt-2">
                You can still assign them – this is just a heads up.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPreferenceWarning(false);
              setPendingShiftWithWarning(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={continueAfterWarning}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-despite-preference-btn"
            >
              Assign Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Week Forward Dialog */}
      <Dialog open={showDuplicateWeek} onOpenChange={setShowDuplicateWeek}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-blue-500" />
              Duplicate This Week Forward
            </DialogTitle>
            <DialogDescription>
              Clone {plannerCampaignFilter ? "filtered" : "all"} shifts from the current week into future weeks
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <p className="text-sm text-slate-600 mb-1">Source week:</p>
              <p className="font-medium text-slate-900">
                {new Date(scheduleWeekStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                {' – '}
                {new Date(new Date(scheduleWeekStart).setDate(new Date(scheduleWeekStart).getDate() + 6)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              {plannerCampaignFilter && (
                <p className="text-xs text-blue-600 mt-1">Filtered by campaign — only matching shifts will be duplicated</p>
              )}
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Generate forward for</Label>
              <Select value={String(duplicateWeeksForward)} onValueChange={(v) => setDuplicateWeeksForward(parseInt(v))}>
                <SelectTrigger className="mt-1.5" data-testid="duplicate-weeks-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 week</SelectItem>
                  <SelectItem value="2">2 weeks</SelectItem>
                  <SelectItem value="4">4 weeks</SelectItem>
                  <SelectItem value="6">6 weeks</SelectItem>
                  <SelectItem value="8">8 weeks</SelectItem>
                  <SelectItem value="12">12 weeks (quarter)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">
              Shifts with scheduling conflicts will be automatically skipped. Generated shifts are fully editable.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateWeek(false)}>Cancel</Button>
            <Button onClick={handleDuplicateWeek} disabled={duplicating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-duplicate-btn">
              {duplicating ? "Generating..." : `Generate ${duplicateWeeksForward} Week${duplicateWeeksForward > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Plan Next Week Modal */}
      <Dialog open={showAutoPlanModal} onOpenChange={(open) => { if (!autoPlanSaving) { setShowAutoPlanModal(open); if (!open) setAutoPlanData(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="auto-plan-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-emerald-600" />
              Auto-Plan Next Week
            </DialogTitle>
            <DialogDescription>
              {autoPlanLoading
                ? "Generating a draft rota based on territory scores, fundraiser performance and availability..."
                : autoPlanData?.weekStart
                ? `Draft for ${new Date(autoPlanData.weekStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${new Date(autoPlanData.weekEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}. Review, then save. Existing shifts are not overwritten.`
                : "Review the draft before saving."}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {autoPlanLoading && (
              <div className="text-center py-10 text-slate-500" data-testid="auto-plan-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-3"></div>
                Building draft plan...
              </div>
            )}

            {!autoPlanLoading && autoPlanData && autoPlanData.drafts?.length === 0 && (
              <div className="text-center py-8 text-slate-500" data-testid="auto-plan-empty">
                No draft shifts could be generated. {autoPlanData.summary?.reason || "Check you have locations and fundraisers configured."}
              </div>
            )}

            {!autoPlanLoading && autoPlanData?.drafts?.length > 0 && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs" data-testid="auto-plan-summary">
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-emerald-700 font-semibold">{autoPlanData.summary?.created ?? autoPlanData.drafts.length}</div>
                    <div className="text-slate-500">Shifts drafted</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-slate-900 font-semibold">{autoPlanData.summary?.locations ?? "—"}</div>
                    <div className="text-slate-500">Locations</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-slate-900 font-semibold">{autoPlanData.summary?.fundraisers ?? "—"}</div>
                    <div className="text-slate-500">Fundraisers</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="text-amber-700 font-semibold">{autoPlanData.skipped?.length ?? 0}</div>
                    <div className="text-slate-500">Skipped (no fit)</div>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="auto-plan-list">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                        <th className="px-3 py-2">Day</th>
                        <th className="px-3 py-2">Fundraiser</th>
                        <th className="px-3 py-2">Campaign</th>
                        <th className="px-3 py-2">Location</th>
                        <th className="px-3 py-2 text-right">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {autoPlanData.drafts.map((d, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0" data-testid={`auto-plan-row-${i}`}>
                          <td className="px-3 py-2 text-slate-700">
                            <div className="font-medium text-slate-900">{d.dayName}</div>
                            <div className="text-xs text-slate-500">{d.date}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{d.fundraiserName}</td>
                          <td className="px-3 py-2 text-slate-600">{d.campaignName || "—"}</td>
                          <td className="px-3 py-2 text-slate-700">{d.locationName}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">{d.startTime}–{d.endTime}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {autoPlanData.skipped?.length > 0 && (
                  <div className="mt-3 text-xs text-slate-500" data-testid="auto-plan-skipped">
                    {autoPlanData.skipped.length} location-day{autoPlanData.skipped.length === 1 ? "" : "s"} skipped (no available fundraiser).
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAutoPlanModal(false); setAutoPlanData(null); }} disabled={autoPlanSaving} data-testid="auto-plan-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={saveAutoPlan}
              disabled={autoPlanLoading || autoPlanSaving || !autoPlanData?.drafts?.length}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="auto-plan-save-btn"
            >
              {autoPlanSaving ? "Saving..." : `Save Draft Plan${autoPlanData?.drafts?.length ? ` (${autoPlanData.drafts.length})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Set Password Manually Modal */}
      <Dialog open={showSetPasswordModal} onOpenChange={setShowSetPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-orange-500" />
              Set Password Manually
            </DialogTitle>
            <DialogDescription>
              Directly set a new password for {setPasswordTarget?.name || setPasswordTarget?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-slate-700 font-medium">New Password</Label>
              <Input
                type="password"
                value={newPasswordForm.password}
                onChange={(e) => setNewPasswordForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Min 8 characters"
                className="mt-1.5"
                data-testid="set-password-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Confirm Password</Label>
              <Input
                type="password"
                value={newPasswordForm.confirm}
                onChange={(e) => setNewPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                placeholder="Re-enter password"
                className="mt-1.5"
                data-testid="set-password-confirm-input"
              />
            </div>
            {newPasswordForm.password && newPasswordForm.confirm && newPasswordForm.password !== newPasswordForm.confirm && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPasswordModal(false)}>Cancel</Button>
            <Button
              onClick={handleSetPasswordManually}
              disabled={settingPassword || !newPasswordForm.password || !newPasswordForm.confirm || newPasswordForm.password !== newPasswordForm.confirm}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="confirm-set-password-btn"
            >
              {settingPassword ? "Saving..." : "Set Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Plan Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Upgrade Your Plan
            </DialogTitle>
            <DialogDescription>
              Choose the plan that best fits your organisation's needs
            </DialogDescription>
          </DialogHeader>
          
          {/* EMPTY STATE - No pricing plans available */}
          {availablePlans.length === 0 ? (
            <div className="py-12 text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h4 className="font-semibold text-slate-900 mb-2">No Pricing Plans Available</h4>
              <p className="text-sm text-slate-500 mb-4">
                Pricing plans haven't been configured yet. Please contact your platform administrator.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowUpgradeModal(false)}
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-6">
              {/* Dynamic Plans from Platform */}
              {availablePlans.filter(p => p.isActive !== false).slice(0, 3).map((plan, index) => {
                const isCurrentPlan = orgStatus?.plan?.toLowerCase() === plan.name.toLowerCase() || 
                                      orgStatus?.planId === plan.id;
                const isRecommended = index === 1 && !isCurrentPlan; // Middle plan is recommended
                const colorScheme = index === 0 ? 'slate' : index === 1 ? 'emerald' : 'violet';
                
                return (
                  <div 
                    key={plan.id || index} 
                    className={`p-6 rounded-2xl border-2 relative ${
                      isCurrentPlan 
                        ? `border-${colorScheme}-300 bg-${colorScheme}-50` 
                        : isRecommended 
                          ? 'border-emerald-500' 
                          : 'border-slate-200'
                    }`}
                    style={{
                      borderColor: isCurrentPlan ? (colorScheme === 'emerald' ? '#86efac' : colorScheme === 'violet' ? '#c4b5fd' : '#cbd5e1') 
                                 : isRecommended ? '#10b981' : '#e2e8f0',
                      backgroundColor: isCurrentPlan ? (colorScheme === 'emerald' ? '#ecfdf5' : colorScheme === 'violet' ? '#f5f3ff' : '#f8fafc') : 'white'
                    }}
                  >
                    {isRecommended && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">
                        RECOMMENDED
                      </div>
                    )}
                    <div className="text-center mb-4">
                      <div className={`text-xs font-medium uppercase mb-1 ${
                        index === 0 ? 'text-slate-500' : index === 1 ? 'text-emerald-600' : 'text-violet-600'
                      }`}>
                        {plan.name}
                      </div>
                      <div className="font-heading font-black text-3xl text-slate-900">
                        £{plan.monthlyPrice || 0}
                      </div>
                      <div className="text-sm text-slate-500">/month</div>
                    </div>
                    <ul className="space-y-2 text-sm text-slate-600 mb-6">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-500" />
                        Includes {plan.maxUsers || 0} users
                      </li>
                      {(plan.interactionsPerUser || 0) > 0 && (
                        <>
                          <li className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-emerald-500" />
                            Each user includes {plan.interactionsPerUser} interactions
                          </li>
                          <li className="flex items-center gap-2 text-emerald-700 font-semibold">
                            <Check className="h-4 w-4 text-emerald-500" />
                            Total included: {((plan.maxUsers || 0) * plan.interactionsPerUser).toLocaleString()} interactions
                          </li>
                        </>
                      )}
                      {plan.pricePerUser > 0 && (
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-emerald-500" />
                          £{plan.pricePerUser} per additional user
                        </li>
                      )}
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-500" />
                        Full platform access
                      </li>
                      {plan.sortOrder === 3 && (
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-emerald-500" />
                          Priority support
                        </li>
                      )}
                    </ul>
                    <div className="text-center text-xs text-slate-400 mb-2">
                      Scales with your operation
                    </div>
                    {isCurrentPlan ? (
                      <div className="mt-4 text-center">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          colorScheme === 'emerald' ? 'bg-emerald-200 text-emerald-700' 
                          : colorScheme === 'violet' ? 'bg-violet-200 text-violet-700' 
                          : 'bg-slate-200 text-slate-600'
                        }`}>
                          Current Plan
                        </span>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleUpgrade(plan.id || plan.name.toLowerCase())}
                        className={`w-full mt-4 ${
                          index === 1 ? 'bg-emerald-500 hover:bg-emerald-600' 
                          : index === 2 ? 'bg-violet-500 hover:bg-violet-600' 
                          : 'bg-slate-600 hover:bg-slate-700'
                        } text-white`}
                        data-testid={`upgrade-to-${plan.name.toLowerCase()}`}
                      >
                        {plan.monthlyPrice === 0 ? `Switch to ${plan.name}` : `Upgrade to ${plan.name}`}
                      </Button>
                    )}
                  </div>
                );
              })}
              
              {/* Custom Quote option if less than 3 plans */}
              {availablePlans.filter(p => p.isActive !== false).length < 3 && (
                <div className="p-6 rounded-2xl border-2 border-dashed border-slate-200">
                  <div className="text-center mb-4">
                    <div className="text-xs font-medium text-violet-600 uppercase mb-1">Enterprise</div>
                    <div className="font-heading font-black text-3xl text-slate-900">Custom</div>
                    <div className="text-sm text-slate-500">pricing</div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600 mb-6">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Unlimited signups
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Custom integrations
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-500" />
                      Dedicated support
                    </li>
                  </ul>
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={() => {
                      setShowUpgradeModal(false);
                      handleRequestCustomQuote();
                    }}
                    data-testid="request-custom-quote"
                  >
                    Request Custom Quote
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Signup Detail Modal */}
      <Dialog open={showSignupDetailModal} onOpenChange={setShowSignupDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
              Signup Details
            </DialogTitle>
            <DialogDescription>
              Full record for tracking ID: {selectedSignupDetail?.trackingId}
            </DialogDescription>
          </DialogHeader>
          {selectedSignupDetail && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500">Date</div>
                  <div className="font-medium text-slate-900">
                    {new Date(selectedSignupDetail.date).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Fundraiser</div>
                  <div className="font-medium text-slate-900">{selectedSignupDetail.fundraiserName}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Campaign</div>
                  <div className="font-medium text-slate-900">{selectedSignupDetail.campaignName}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Type</div>
                  <div className="font-medium text-slate-900 capitalize">{selectedSignupDetail.conversionType?.replace("_", " ")}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Amount</div>
                  <div className="font-medium text-slate-900">
                    {selectedSignupDetail.amount ? `£${selectedSignupDetail.amount}` : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Delivery Status</div>
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    {getStatusIcon(selectedSignupDetail.deliveryStatus)}
                    {getStatusBadge(selectedSignupDetail.deliveryStatus)}
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-500">First Name</div>
                    <div className="font-medium text-slate-900">{selectedSignupDetail.firstName || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Last Name</div>
                    <div className="font-medium text-slate-900">{selectedSignupDetail.lastName || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Email</div>
                    <div className="font-medium text-slate-900 text-sm">{selectedSignupDetail.email || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Phone</div>
                    <div className="font-medium text-slate-900">{selectedSignupDetail.phone || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Consent</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSignupDetail.consentEmail && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Email</span>}
                  {selectedSignupDetail.consentSms && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">SMS</span>}
                  {selectedSignupDetail.consentPhone && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Phone</span>}
                  {selectedSignupDetail.consentPost && <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Post</span>}
                  {!selectedSignupDetail.consentEmail && !selectedSignupDetail.consentSms && !selectedSignupDetail.consentPhone && !selectedSignupDetail.consentPost && (
                    <span className="text-slate-500 text-xs">No consents recorded</span>
                  )}
                </div>
              </div>

              {selectedSignupDetail.address && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3">Address</h4>
                  <div className="text-sm text-slate-600">
                    {selectedSignupDetail.address.line1}<br />
                    {selectedSignupDetail.address.line2 && <>{selectedSignupDetail.address.line2}<br /></>}
                    {selectedSignupDetail.address.city}, {selectedSignupDetail.address.postcode}
                  </div>
                </div>
              )}

              {/* Frequency */}
              <div className="border-t pt-4" data-testid="signup-detail-frequency">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Frequency</h4>
                <div className="text-sm text-slate-700">
                  {selectedSignupDetail.conversionType === "regular_giving"
                    ? (selectedSignupDetail.billingFrequency || selectedSignupDetail.frequency || "Monthly (regular giving)")
                    : (selectedSignupDetail.conversionType === "lottery"
                        ? (selectedSignupDetail.billingFrequency || "Weekly (lottery)")
                        : "One-off")}
                </div>
                {(selectedSignupDetail.conversionType === "regular_giving" || selectedSignupDetail.conversionType === "lottery") && (
                  <div className="mt-3" data-testid="signup-detail-payment-date">
                    <div className="text-xs text-slate-500">Payment Date</div>
                    <div className="text-sm text-slate-800">
                      {selectedSignupDetail.paymentDatePreference
                        ? (selectedSignupDetail.paymentDatePreference === "last_working_day"
                            ? "Last working day of month"
                            : `${selectedSignupDetail.paymentDatePreference} of each month`)
                        : "—"}
                    </div>
                  </div>
                )}
              </div>

              {/* Signature if stored */}
              {(selectedSignupDetail.signature || selectedSignupDetail.signatureUrl || selectedSignupDetail.signatureImage) && (
                <div className="border-t pt-4" data-testid="signup-detail-signature">
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">Signature</h4>
                  <img
                    src={selectedSignupDetail.signatureUrl || selectedSignupDetail.signatureImage || selectedSignupDetail.signature}
                    alt="Signature"
                    className="max-w-xs border border-slate-200 rounded bg-white"
                  />
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-4" data-testid="signup-detail-timestamps">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Timestamps</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Created</div>
                    <div className="font-mono text-slate-800">
                      {selectedSignupDetail.createdAt ? new Date(selectedSignupDetail.createdAt).toLocaleString() : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Last updated</div>
                    <div className="font-mono text-slate-800">
                      {selectedSignupDetail.updatedAt ? new Date(selectedSignupDetail.updatedAt).toLocaleString() : "-"}
                    </div>
                  </div>
                  {selectedSignupDetail.deliveredAt && (
                    <div>
                      <div className="text-xs text-slate-500">Delivered</div>
                      <div className="font-mono text-slate-800">
                        {new Date(selectedSignupDetail.deliveredAt).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {selectedSignupDetail.trackingId && (
                    <div>
                      <div className="text-xs text-slate-500">Tracking ID</div>
                      <div className="font-mono text-slate-800 text-xs">{selectedSignupDetail.trackingId}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSignupDetailModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Modal */}
      <Dialog open={showTimelineModal} onOpenChange={setShowTimelineModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Conversion Timeline
            </DialogTitle>
            <DialogDescription>
              Full event trace for this conversion
            </DialogDescription>
          </DialogHeader>
          
          {loadingTimeline ? (
            <div className="py-8 text-center text-slate-500">
              <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              Loading timeline...
            </div>
          ) : timelineData ? (
            <div className="space-y-4" data-testid="timeline-content">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Donor</div>
                  <div className="font-medium text-slate-900">
                    {timelineData.conversion?.firstName} {timelineData.conversion?.lastName}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Chain</div>
                  <div className={`font-medium ${timelineData.eventChain?.complete ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {timelineData.eventChain?.complete ? 'Complete' : `Missing: ${timelineData.eventChain?.missing?.join(', ')}`}
                  </div>
                </div>
              </div>
              
              <div className="border-l-2 border-slate-200 ml-3 space-y-0">
                {timelineData.events?.map((event, i) => {
                  const typeColors = {
                    interaction_created: 'bg-blue-500',
                    stage_logged: 'bg-slate-400',
                    conversion_created: 'bg-emerald-500',
                    referral_conversion_created: 'bg-emerald-500',
                    public_conversion_created: 'bg-emerald-500',
                    delivery_attempt: 'bg-amber-500',
                    delivery_success: 'bg-emerald-500',
                    delivery_failed: 'bg-red-500',
                    payment_completed: 'bg-purple-500',
                    system_alert: 'bg-red-500',
                  };
                  const dotColor = typeColors[event.type] || 'bg-slate-400';
                  const meta = event.metadata || {};
                  const metaStr = Object.entries(meta)
                    .filter(([k]) => !['clientIp'].includes(k))
                    .map(([k, v]) => `${k}: ${typeof v === 'string' && v.length > 40 ? v.slice(0, 40) + '...' : v}`)
                    .join(' | ');
                  
                  return (
                    <div key={event.id || i} className="relative pl-6 py-2" data-testid={`timeline-event-${i}`}>
                      <div className={`absolute left-[-5px] top-3 w-2.5 h-2.5 rounded-full ${dotColor}`} />
                      <div className="text-xs text-slate-400 font-mono">{event.timestamp?.slice(11, 19)}</div>
                      <div className="text-sm font-medium text-slate-800">{event.type?.replace(/_/g, ' ')}</div>
                      {metaStr && <div className="text-xs text-slate-500 mt-0.5">{metaStr}</div>}
                    </div>
                  );
                })}
              </div>
              
              <div className="text-xs text-slate-400 text-right">{timelineData.eventCount} events</div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Compliance Tracker Modal */}
      <Dialog open={showComplianceModal} onOpenChange={setShowComplianceModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Fundraiser Compliance
            </DialogTitle>
            <DialogDescription>
              Engage does not store sensitive documents. This records that checks were completed.
            </DialogDescription>
          </DialogHeader>
          
          {loadingCompliance ? (
            <div className="py-8 text-center text-slate-500">Loading compliance data...</div>
          ) : (
            <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
              {/* DBS Check */}
              <div className="p-4 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-slate-900">DBS Check</Label>
                  <Switch
                    checked={complianceData.dbsChecked}
                    onCheckedChange={(v) => setComplianceData({...complianceData, dbsChecked: v})}
                  />
                </div>
                {complianceData.dbsChecked && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Date</Label>
                      <Input
                        type="date"
                        value={complianceData.dbsCheckDate}
                        onChange={(e) => setComplianceData({...complianceData, dbsCheckDate: e.target.value})}
                        className="h-9 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Initials (max 4)</Label>
                      <Input
                        value={complianceData.dbsInitial}
                        onChange={(e) => setComplianceData({...complianceData, dbsInitial: e.target.value.slice(0,4).toUpperCase()})}
                        className="h-9 mt-1"
                        maxLength={4}
                        placeholder="e.g. JS"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ID Verification */}
              <div className="p-4 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-slate-900">ID Verified</Label>
                  <Switch
                    checked={complianceData.idVerified}
                    onCheckedChange={(v) => setComplianceData({...complianceData, idVerified: v})}
                  />
                </div>
                {complianceData.idVerified && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Date</Label>
                      <Input
                        type="date"
                        value={complianceData.idCheckDate}
                        onChange={(e) => setComplianceData({...complianceData, idCheckDate: e.target.value})}
                        className="h-9 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Initials (max 4)</Label>
                      <Input
                        value={complianceData.idInitial}
                        onChange={(e) => setComplianceData({...complianceData, idInitial: e.target.value.slice(0,4).toUpperCase()})}
                        className="h-9 mt-1"
                        maxLength={4}
                        placeholder="e.g. JS"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* References Check */}
              <div className="p-4 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-slate-900">References Checked</Label>
                  <Switch
                    checked={complianceData.referencesChecked}
                    onCheckedChange={(v) => setComplianceData({...complianceData, referencesChecked: v})}
                  />
                </div>
                {complianceData.referencesChecked && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Date</Label>
                      <Input
                        type="date"
                        value={complianceData.referencesCheckDate}
                        onChange={(e) => setComplianceData({...complianceData, referencesCheckDate: e.target.value})}
                        className="h-9 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Initials (max 4)</Label>
                      <Input
                        value={complianceData.referencesInitial}
                        onChange={(e) => setComplianceData({...complianceData, referencesInitial: e.target.value.slice(0,4).toUpperCase()})}
                        className="h-9 mt-1"
                        maxLength={4}
                        placeholder="e.g. JS"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Fundraiser Details - Address, UTR, DBS */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  Fundraiser Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Address</Label>
                    <Input
                      value={complianceData.address || ""}
                      onChange={(e) => setComplianceData({...complianceData, address: e.target.value})}
                      className="h-9 mt-1"
                      placeholder="Full address"
                      data-testid="fundraiser-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">UTR Number</Label>
                      <Input
                        value={complianceData.utrNumber || ""}
                        onChange={(e) => setComplianceData({...complianceData, utrNumber: e.target.value})}
                        className="h-9 mt-1"
                        placeholder="e.g. 1234567890"
                        data-testid="utr-number-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">DBS Number</Label>
                      <Input
                        value={complianceData.dbsNumber || ""}
                        onChange={(e) => setComplianceData({...complianceData, dbsNumber: e.target.value})}
                        className="h-9 mt-1"
                        placeholder="e.g. 001234567890"
                        data-testid="dbs-number-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">UTR Document</Label>
                      <div className="mt-1">
                        {complianceData.utrDocumentUrl ? (
                          <div className="flex items-center gap-2">
                            <a href={authedFileUrl(complianceData.utrDocumentUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">View UTR Doc</a>
                            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setComplianceData({...complianceData, utrDocumentUrl: ""})}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                            <FileUp className="h-4 w-4" />
                            Upload PDF/JPG/PNG
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const fd = new FormData();
                              fd.append("file", file);
                              try {
                                const res = await apiClient.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                setComplianceData({...complianceData, utrDocumentUrl: res.data.url});
                                toast.success("UTR document uploaded");
                              } catch { toast.error("Upload failed"); }
                            }} />
                          </label>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">DBS Document</Label>
                      <div className="mt-1">
                        {complianceData.dbsDocumentUrl ? (
                          <div className="flex items-center gap-2">
                            <a href={authedFileUrl(complianceData.dbsDocumentUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">View DBS Doc</a>
                            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setComplianceData({...complianceData, dbsDocumentUrl: ""})}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                            <FileUp className="h-4 w-4" />
                            Upload PDF/JPG/PNG
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const fd = new FormData();
                              fd.append("file", file);
                              try {
                                const res = await apiClient.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                setComplianceData({...complianceData, dbsDocumentUrl: res.data.url});
                                toast.success("DBS document uploaded");
                              } catch { toast.error("Upload failed"); }
                            }} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="font-semibold text-slate-900">Notes</Label>
                <Textarea
                  value={complianceData.notes}
                  onChange={(e) => setComplianceData({...complianceData, notes: e.target.value})}
                  className="mt-2"
                  rows={3}
                  placeholder="Additional compliance notes..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplianceModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveComplianceData}
              disabled={savingCompliance}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {savingCompliance ? "Saving..." : "Save Compliance Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Performance Export Modal */}
      <Dialog open={showPerformanceExportModal} onOpenChange={setShowPerformanceExportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-emerald-600" />
              Performance Export
            </DialogTitle>
            <DialogDescription>
              Export fundraiser performance data for payroll or reporting.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={performanceExportFilters.startDate}
                  onChange={(e) => setPerformanceExportFilters({...performanceExportFilters, startDate: e.target.value})}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={performanceExportFilters.endDate}
                  onChange={(e) => setPerformanceExportFilters({...performanceExportFilters, endDate: e.target.value})}
                  className="mt-1.5"
                />
              </div>
            </div>
            
            <div>
              <Label>Campaign (Optional)</Label>
              <Select
                value={performanceExportFilters.campaignId || "all"}
                onValueChange={(v) => setPerformanceExportFilters({...performanceExportFilters, campaignId: v === "all" ? "" : v})}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="All Campaigns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Fundraisers (Optional - select multiple)</Label>
              <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {fundraisers.length === 0 ? (
                  <div className="text-sm text-slate-500 p-2">No fundraisers available</div>
                ) : fundraisers.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={performanceExportFilters.fundraiserIds.includes(f.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPerformanceExportFilters({
                            ...performanceExportFilters,
                            fundraiserIds: [...performanceExportFilters.fundraiserIds, f.id]
                          });
                        } else {
                          setPerformanceExportFilters({
                            ...performanceExportFilters,
                            fundraiserIds: performanceExportFilters.fundraiserIds.filter(id => id !== f.id)
                          });
                        }
                      }}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <span className="text-sm">{f.name}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
              <strong>Output fields:</strong> Fundraiser name, Lottery (single/double), Regular giving count, One-off total, Total conversions
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPerformanceExportModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePerformanceExport}
              disabled={exportingPerformance}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {exportingPerformance ? "Exporting..." : "Export CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fundraiser Performance Command Centre */}
      <FundraiserPerformance
        open={showFundraiserPerf}
        onOpenChange={(v) => { setShowFundraiserPerf(v); if (!v) setPerfFundraiserId(null); }}
        fundraiserId={perfFundraiserId}
        onAction={(action, fid) => {
          if (action === "reset-password") {
            const u = teamMembers.find(m => m.id === fid);
            if (u) { setSetPasswordTarget({ id: u.id, name: u.name, email: u.email }); setNewPasswordForm({ password: "", confirm: "" }); setShowSetPasswordModal(true); }
          } else if (action === "open-planner") {
            setShowFundraiserPerf(false); setActiveTab("schedule");
          } else if (action === "view-signups") {
            setShowFundraiserPerf(false); setActiveTab("signups");
          } else if (action === "view-shifts") {
            setShowFundraiserPerf(false); setActiveTab("schedule");
          } else if (action === "edit-role" || action === "assign-campaign") {
            setShowFundraiserPerf(false); setActiveTab("team");
          }
        }}
      />

      {/* Campaign Intelligence Centre */}
      <CampaignIntelligence
        open={showCampaignIntel}
        onOpenChange={(v) => { setShowCampaignIntel(v); if (!v) setIntelCampaignId(null); }}
        campaignId={intelCampaignId}
        onAction={(action) => {
          if (action === "open-planner") {
            setShowCampaignIntel(false); setActiveTab("schedule"); setPlannerView("campaign");
          } else if (action === "duplicate-week") {
            setShowCampaignIntel(false); setActiveTab("schedule"); setShowDuplicateWeek(true);
          } else if (action === "assign-staff" || action === "edit-campaign") {
            setShowCampaignIntel(false); setActiveTab("settings");
          } else if (action === "add-locations") {
            setShowCampaignIntel(false); setActiveTab("settings");
          }
        }}
      />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// OCI V2.5 — Navigation Rationalisation
// 7 top-level groups; legacy tab keys map underneath.
// ─────────────────────────────────────────────────────────────────
const V25_GROUPS = [
  { key: "overview", label: "Overview", defaultTab: "overview", tabs: [
      { key: "overview", label: "Overview" },
      { key: "insights", label: "Analytics" },
  ]},
  { key: "campaigns", label: "Campaigns", defaultTab: "settings", tabs: [
      { key: "settings",          label: "Campaigns & Locations" },
      { key: "schedule",          label: "Shift Planner" },
      { key: "activity-locations", label: "Heat Map" },
  ]},
  { key: "workforce", label: "Workforce", defaultTab: "workforce", tabs: [
      { key: "workforce",  label: "Operations Centre" },
      { key: "team",       label: "Team list" },
      { key: "scheduling", label: "Assignments" },
  ]},
  { key: "donors", label: "Donors", defaultTab: "donor-hub", tabs: [
      { key: "donor-hub",       label: "Donor Hub" },
      { key: "signups",         label: "Sign-Ups" },
      { key: "delivery-centre", label: "Delivery Centre" },
      { key: "delivery",        label: "Signup Delivery" },
      { key: "flagged",         label: "Flagged" },
      { key: "cancellations",   label: "Cancellations" },
      { key: "retention",       label: "Retention" },
  ]},
  { key: "gift-aid", label: "Gift Aid", defaultTab: "gift-aid", tabs: [
      { key: "gift-aid", label: "Gift Aid" },
  ]},
  { key: "trustees", label: "Trustees", defaultTab: "trustee", tabs: [
      { key: "trustee", label: "Trustee Pack" },
  ]},
  { key: "settings", label: "Settings", defaultTab: "users", tabs: [
      { key: "users",      label: "Accounts" },
      { key: "guidance",   label: "Messages" },
      { key: "payroll",    label: "Payroll" },
      { key: "billing",    label: "Billing" },
      { key: "charity-billing", label: "Charity Billing" },
      { key: "earnings",   label: "Earnings" },
      { key: "landing",    label: "Public Page" },
      { key: "api-access", label: "API" },
      { key: "systemlog",  label: "System Log" },
      { key: "activity",   label: "Audit Trail" },
  ]},
];

function _groupForTab(tabKey) {
  for (const g of V25_GROUPS) {
    if (g.tabs.some((t) => t.key === tabKey)) return g.key;
  }
  return "overview";
}

function V25TopNav({ activeTab, setActiveTab, user }) {
  const activeGroup = _groupForTab(activeTab);
  const isManager = user?.role === "manager";
  const isElevatedManager = user?.managerLevel === "elevated";

  // Filter sub-tabs based on role restrictions (mirrors legacy conditional rendering)
  const visibleSubTabs = (groupKey) => {
    const g = V25_GROUPS.find((x) => x.key === groupKey);
    if (!g) return [];
    return g.tabs.filter((t) => {
      if (isManager) {
        if (["users", "billing", "charity-billing", "earnings",
              "landing", "api-access"].includes(t.key)) return false;
        if (["guidance", "systemlog", "activity"].includes(t.key) && !isElevatedManager) return false;
      }
      return true;
    });
  };

  const handleGroupClick = (groupKey) => {
    const g = V25_GROUPS.find((x) => x.key === groupKey);
    if (!g) return;
    const visible = visibleSubTabs(groupKey);
    if (!visible.length) return; // no authorised tabs in this group — do not navigate
    const fallback = visible[0].key;
    // If current tab already belongs to this group, keep it; otherwise switch to fallback
    if (_groupForTab(activeTab) !== groupKey) setActiveTab(fallback);
  };

  const subTabs = visibleSubTabs(activeGroup);

  return (
    <div className="space-y-2" data-testid="v25-top-nav">
      {/* Row 1: 7 primary groups */}
      <div className="flex flex-wrap gap-1 p-1 rounded-lg bg-slate-100 border border-slate-200"
           data-testid="v25-primary-bar">
        {V25_GROUPS.map((g) => {
          const active = g.key === activeGroup;
          return (
            <button key={g.key} type="button"
              onClick={() => handleGroupClick(g.key)}
              data-testid={`v25-group-${g.key}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                active ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                       : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
              }`}>
              {g.label}
            </button>
          );
        })}
      </div>
      {/* Row 2: sub-pills for active group (shown whenever the group has multiple
          tabs overall, so a role seeing a single authorised tab still gets an
          explicit affordance) */}
      {subTabs.length >= 1 && (V25_GROUPS.find((x) => x.key === activeGroup)?.tabs.length || 0) > 1 && (
        <div className="flex flex-wrap gap-1 px-1" data-testid="v25-sub-bar">
          {subTabs.map((t) => (
            <button key={t.key} type="button"
              onClick={() => setActiveTab(t.key)}
              data-testid={`v25-sub-${t.key}`}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                activeTab === t.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
